/**
 * Live API-backed repository for the admin panel.
 *
 * API NORMALIZATION:
 * This layer absorbs backend response variation, shape drift, and ID normalization so
 * feature components can work with stable frontend models and form-friendly string IDs.
 */
import { Injectable, inject } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { firstValueFrom } from "rxjs";

import {
  AgentApprovalRequestRecord,
  AgencyRecord,
  AgencyUpsert,
  AgentRecord,
  ApiRepository,
  DashboardActiveSummary,
  DashboardCentralInventoryItem,
  DashboardPdfExport,
  DashboardSummary,
  InventoryCatalogExcelExport,
  UploadedFileResult,
  SupervisorDashboardSummary,
  AssignedAgencySummary,
  ApiUserSummary,
  AuthCredentials,
  AuthUser,
  CreateUserPayload,
  PagedResult,
  PosListQuery,
  UpdateUserProfilePayload,
  PosRecord,
  PosStockDetails,
  PosStockEntry,
  PosStockTransactionPayload,
  PosUpsertPayload,
  ProspectListQuery,
  ProspectApprovalPayload,
  ProspectRecord,
  ProspectStockTransactionPayload,
  ProspectUpsertPayload,
  SupervisorSignupRequestPayload,
  SupervisorApprovalRequestRecord,
  SupervisorRecord,
  SupervisorUpsertPayload,
  TaskActivity,
  TaskRecord,
  AgentTargetAnalyticsResult,
  AgentTargetRecord,
  UpdatePreferencesPayload,
  UpdateProfilePayload,
  UpdateUserStatusPayload,
  UsersListParams,
  UsersListResult,
  AgentListQuery,
  AgentInventoryTransactionPayload,
  AgencyInventoryTransactionPayload,
  CashBalance,
  CashBalanceListQuery,
  CashMovement,
  CashMovementListQuery,
  CashMovementPayload,
  CashOwnerRef,
  CashOwnerType,
  AgencyDirectSalesLogQuery,
  UpdateAgencyStockVariantPayload,
  UpdatePosStockVariantPayload,
  UpdateProspectStockVariantPayload,
  UpdateAgentStockVariantPayload,
  AgencyDirectSalesLogRecord,
  AgencyInventoryStockCategoryGroup,
  AgencyProfileConfiguration,
  AgencyListQuery,
  TaskActivityListQuery,
  TaskListQuery,
  CreateTaskPayload,
  UpdateTaskPayload,
  AgentInventoryStockCategoryGroup,
  AgentInventoryTransactionRecord,
  AgencyInventoryTransactionRecord,
  CentralBankingNotePayload,
  CentralBankingNoteRow,
  CentralTransferListRow,
  CentralStockListRow,
  CentralStockOverviewData,
  CreateStockThresholdPayload,
  InventoryCategory,
  InventoryProduct,
  InventoryVariant,
  CreateCategoryPayload,
  CentralAddStockPayload,
  AgencyReferenceOption,
  CreateProductPayload,
  CreateVariantPayload,
  MaintenanceTypeRecord,
  BtlCampaignApiRecord,
  BtlCampaignListItem,
  BtlCampaignListQuery,
  BtlCampaignUpsertPayload,
  BtlDashboardSummary,
  BtlUserApiRecord,
  BtlUserUpdatePayload,
  LogisticsDashboardSummary,
  LogisticsTaskListItem,
  LogisticsTaskDetailApiRecord,
  LogisticsTaskListQuery,
  LogisticsUserApiRecord,
  CentralReference,
  LogisticsTaskUpsertPayload,
  VehicleApiRecord,
  StockThresholdQuery,
  StockThresholdRow,
  CreateVehiclePayload,
  UpdateVehiclePayload,
  CreateVehicleDocumentStandalonePayload,
  UpdateVehicleDocumentPayload,
  VehicleDocumentApiRecord,
  VehicleDocumentListQuery,
  FuelRequestApiRecord,
  CreateFuelRequestPayload,
  FuelRequestListQuery,
  UpdateFuelRequestPayload,
  CreateMaintenanceRequestPayload,
  UpdateMaintenanceRequestPayload,
  MaintenanceRequestApiRecord,
  MaintenanceRequestListQuery,
  fuelActionPayload,
  maintenanceActionPayload,
  LogisticsTaskReviewPayload,
  LogisticsTaskReviewResult,
  AgentTaskReviewPayload,
  AgentTaskReviewResult,
  DashboardAgentPerformance,
  DashboardAgentStockItem,
  DashboardAgentTargetProgressRow,
  DashboardRecentAgentTask,
  AgentTargetPayload,
  SupervisorDashboardSummaryQuery,
  TargetListQuery,
  TargetPerformanceAnalyticsDocumentExport,
  TargetPerformanceAnalyticsDocumentQuery,
  TargetPerformanceAnalyticsQuery
} from './api-repository';
import { TokenService } from '../token.service';
import { environment } from '../../../../environments/environment';
import { cleanPayload } from '../../../shared/utils';
import type { AgencyAssignedPosSummary, AgencyAssignedUserSummary } from 'src/app/features/agencies/agencies.types';
import type { AgentTargetAnalyticsMetrics, AgentTargetAnalyticsRow, AgentTargetAnalyticsSection } from '../../../features/agents/agents.types';
import { AgencyStockStatus } from 'src/app/features/agencies/agencies.types';

interface ApiUserShape {
  id?: string;
  email?: string;
  fullName?: string;
  firstName?: string;
  lastName?: string;
  profilePhoto?: string | null;
  avatar?: string | null;
  preferredLanguage?: string;
  preferredTheme?: string;
  accountStatus?: string;
  assignedAgencyId?: string | null;
  assignedAgencyIds?: string[] | null;
  assignedSupervisorId?: string | null;
  role?:
    | {
        code?: string;
        name?: string;
        userType?: string;
        permissions?: string[];
      }
    | string;
  contact?: {
    phone?: {
      dialCode?: number;
      iso2?: string;
      country?: string;
      number?: string;
    };
  };
  address?: {
    formattedAddress?: string;
    streetAddress?: string;
    locality?: string;
    subLocality?: string;
    region?: string;
    country?: string;
    postalCode?: string;
    area?: string;
  };
}

type LoginApiUser = ApiUserShape;

interface LoginApiData {
  accessToken: string;
  refreshToken: string;
  sessionId: string;
  expiresIn: string;
  user?: LoginApiUser;
}

interface LoginApiResponse {
  success?: boolean;
  data?: LoginApiData;
  // flat shape (no wrapper)
  accessToken?: string;
  refreshToken?: string;
  user?: LoginApiUser;
}

interface ApiCollectionResponse<T> {
  success?: boolean;
  data?:
    | {
        items?: T[];
        page?: number;
        limit?: number;
        total?: number;
      }
    | T[];
  items?: T[];
}

interface ApiEntityResponse<T> {
  success?: boolean;
  data?: T;
}

/**
 * SERVICE: Http Repository Ts
 * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
 * Output: Returns the documented value or updates component/service state without changing the public contract.
 * Processing: Documents the Angular declaration that connects this class to templates, dependency injection, or template transforms.
 */
@Injectable({ providedIn: "root" })
export class HttpRepository extends ApiRepository {
  private readonly http = inject(HttpClient);
  private readonly tokenService = inject(TokenService);

  /**
   * Normalizes maybe-array backend values into safe record arrays before field extraction.
   */
  private toArray(value: unknown): Record<string, unknown>[] {
    return Array.isArray(value)
      ? value.filter(
          (item): item is Record<string, unknown> =>
            item !== null && typeof item === "object",
        )
      : [];
  }

  /**
   * Reads a nested entity defensively when APIs may return either an object or partial shape.
   */
  private normalizeNestedEntity(value: unknown): { id: string; name: string } {
    const row =
      value && typeof value === "object"
        ? (value as Record<string, unknown>)
        : {};
    return {
      id: String(row["id"] ?? row["_id"] ?? ""),
      name: String(row["name"] ?? ""),
    };
  }

  private readAssignedAgencySummary(
    value: unknown,
    fallbackId?: unknown,
    fallbackName?: unknown,
  ): AssignedAgencySummary | null {
    const fallbackAgencyId = this.toNullableString(fallbackId);
    const fallbackAgencyName = this.toNullableString(fallbackName);

    if (typeof value === "string") {
      const normalized = value.trim();

      if (!normalized && !fallbackAgencyId && !fallbackAgencyName) {
        return null;
      }

      return {
        id: fallbackAgencyId ?? normalized,
        name: fallbackAgencyName ?? (fallbackAgencyId ? normalized : ""),
      };
    }

    const row =
      value && typeof value === "object"
        ? (value as Record<string, unknown>)
        : {};
    const id =
      this.toNullableString(row["id"]) ??
      this.toNullableString(row["_id"]) ??
      this.toNullableString(row["agencyId"]) ??
      this.toNullableString(row["assignedAgencyId"]) ??
      fallbackAgencyId ??
      "";
    const name =
      this.toNullableString(row["name"]) ??
      this.toNullableString(row["agencyName"]) ??
      this.toNullableString(row["assignedAgencyName"]) ??
      fallbackAgencyName ??
      "";

    if (!id && !name) {
      return null;
    }

    return {
      id,
      name,
      city: this.toNullableString(row["city"]) ?? undefined,
      area: this.toNullableString(row["area"]) ?? undefined,
    };
  }

  private readAssignedAgencyFromUser(
    value: unknown,
    profileKey?: "agent" | "btl" | "logistics" | "supervisor",
  ): AssignedAgencySummary | null {
    const source =
      value && typeof value === "object"
        ? (value as Record<string, unknown>)
        : null;

    if (!source) {
      return null;
    }

    const assignedAgencyIds = Array.isArray(source["assignedAgencyIds"])
      ? source["assignedAgencyIds"]
      : [];
    const profile = source["profile"] as Record<string, unknown> | undefined;
    const nestedProfile = profileKey
      ? (profile?.[profileKey] as Record<string, unknown> | undefined)
      : undefined;
    const directProfile = profileKey
      ? (source[`${profileKey}Profile`] as Record<string, unknown> | undefined)
      : undefined;
    const fallbackId =
      source["assignedAgencyId"] ??
      assignedAgencyIds.find(
        (id): id is string => typeof id === "string" && id.trim().length > 0,
      ) ??
      source["agencyId"];
    const fallbackName =
      source["assignedAgencyName"] ??
      source["agencyName"] ??
      (typeof source["agency"] === "string" ? source["agency"] : undefined);
    const candidates = [
      source["assignedAgency"],
      directProfile?.["assignedAgency"],
      nestedProfile?.["assignedAgency"],
      Array.isArray(source["assignedAgencies"])
        ? source["assignedAgencies"][0]
        : undefined,
      typeof source["agency"] === "object" ? source["agency"] : undefined,
    ];

    let firstUsable: AssignedAgencySummary | null = null;

    for (const candidate of candidates) {
      const summary = this.readAssignedAgencySummary(
        candidate,
        fallbackId,
        fallbackName,
      );

      if (!summary) {
        continue;
      }

      if (summary.name) {
        return summary;
      }

      firstUsable ??= summary;
    }

    return (
      firstUsable ??
      this.readAssignedAgencySummary(undefined, fallbackId, fallbackName)
    );
  }

  /**
   * Handles the login response contract and persists auth tokens before mapping the user model.
   * API NORMALIZATION: the backend may return either a wrapped `data` payload or a flat payload.
   */
  override async login(payload: AuthCredentials): Promise<AuthUser | null> {
    const response = await firstValueFrom(
      this.http.post<LoginApiResponse>(
        `${environment.apiBaseUrl}/auth/login`,
        cleanPayload({ ...payload, platform: "web" }),
      ),
    );

    // Handle both { data: { accessToken } } and flat { accessToken } shapes
    const tokenData: LoginApiData | undefined =
      response?.data ??
      (response?.accessToken ? (response as LoginApiData) : undefined);

    if (!tokenData?.accessToken) {
      return null;
    }

    this.tokenService.setTokens(
      tokenData.accessToken,
      tokenData.refreshToken ?? "",
      tokenData.sessionId ?? "",
      tokenData.expiresIn ?? "",
    );

    return this.mapApiUser(tokenData.user, payload.email);
  }

  override logout(sessionId: string): Promise<void> {
    return firstValueFrom(
      this.http.post<void>(
        `${environment.apiBaseUrl}/auth/logout`,
        cleanPayload({ sessionId }),
      ),
    );
  }

  override changePassword(
    currentPassword: string,
    newPassword: string,
  ): Promise<void> {
    return firstValueFrom(
      this.http.post<void>(
        `${environment.apiBaseUrl}/auth/change-password`,
        cleanPayload({ currentPassword, newPassword }),
      ),
    );
  }

  override async getProfile(): Promise<AuthUser> {
    const response = await firstValueFrom(
      this.http.get<{ success?: boolean; data?: ApiUserShape }>(
        `${environment.apiBaseUrl}/auth/me`,
      ),
    );
    return this.mapApiUser(response?.data);
  }

  override async updateProfile(
    payload: UpdateProfilePayload,
  ): Promise<AuthUser> {
    const response = await firstValueFrom(
      this.http.patch<{ success?: boolean; data?: ApiUserShape }>(
        `${environment.apiBaseUrl}/auth/me/profile`,
        payload,
      ),
    );
    return this.mapApiUser(response?.data);
  }

  override updatePreferences(payload: UpdatePreferencesPayload): Promise<void> {
    return firstValueFrom(
      this.http.patch<void>(`${environment.apiBaseUrl}/auth/me/preferences`, {
        preferredLanguage: payload.preferredLanguage?.toUpperCase(),
        preferredTheme: payload.preferredTheme,
      }),
    );
  }

  override requestSignup(
    payload: SupervisorSignupRequestPayload,
  ): Promise<void> {
    return firstValueFrom(
      this.http.post<void>(
        `${environment.apiBaseUrl}/auth/supervisors/signup`,
        cleanPayload(payload as unknown as Record<string, unknown>),
      ),
    );
  }

  override requestPasswordOtp(email: string): Promise<boolean> {
    return firstValueFrom(
      this.http.post<boolean>(
        `${environment.apiBaseUrl}/auth/forgot-password/request-otp`,
        cleanPayload({ email }),
      ),
    );
  }

  override verifyPasswordOtp(email: string, otp: string): Promise<boolean> {
    return firstValueFrom(
      this.http.post<boolean>(
        `${environment.apiBaseUrl}/auth/forgot-password/verify-otp`,
        cleanPayload({ email, otp }),
      ),
    );
  }

  override resetPassword(
    email: string,
    nextPassword: string,
  ): Promise<boolean> {
    return firstValueFrom(
      this.http.post<boolean>(
        `${environment.apiBaseUrl}/auth/forgot-password/reset`,
        cleanPayload({ email, password: nextPassword }),
      ),
    );
  }

  // override getAgencies(): Promise<AgencyRecord[]> {
  //   return firstValueFrom(this.http.get<AgencyRecord[]>(`${environment.apiBaseUrl}/aggregation/agencies`));
  // }
  override async getAgencies(
    query: AgencyListQuery = {},
  ): Promise<PagedResult<AgencyRecord>> {
    const params: Record<string, string> = {};
    if (query.page !== undefined) params["page"] = String(query.page);
    if (query.limit !== undefined) params["limit"] = String(query.limit);
    if (query.search) params["search"] = query.search;
    if (query.profile) params["profile"] = query.profile;
    if (query.status) params["status"] = query.status;
    if (query.city) params["city"] = query.city;

    const response = await firstValueFrom(
      this.http.get<ApiCollectionResponse<Record<string, unknown>>>(
        `${environment.apiBaseUrl}/aggregation/agencies`,
        {
          params,
        },
      ),
    );

    const rawItems = this.extractCollectionItems(response);
    const data =
      response.data && !Array.isArray(response.data) ? response.data : null;
    const items: AgencyRecord[] = rawItems.map((u) =>
      this.mapAgencyRecord(u as Record<string, unknown>),
    );

    return this.capPagedResult(
      {
        items,
        page: Number(data?.page ?? query.page ?? 1),
        limit: Number(data?.limit ?? query.limit ?? items.length),
        total: Number(data?.total ?? items.length),
      },
      query.page,
      query.limit,
    );
  }

  override async getAgencyProfiles(
    query: { search?: string; page?: number; limit?: number } = {},
  ): Promise<PagedResult<AgencyProfileConfiguration>> {
    const params = Object.fromEntries(
      Object.entries(query).filter(
        ([, value]) => value !== undefined && value !== null && value !== "",
      ),
    ) as Record<string, string | number>;
    const response = await firstValueFrom(
      this.http.get<{
        success: boolean;
        data: {
          items: Record<string, unknown>[];
          page: number;
          limit: number;
          total: number;
        };
      }>(`${environment.apiBaseUrl}/aggregation/agencies/profiles`, {
        params,
      }),
    );

    return this.capPagedResult(
      {
        items: (response.data?.items ?? []).map((item) =>
          this.normalizeAgencyProfileConfiguration(item),
        ),
        page: response.data.page,
        limit: response.data.limit,
        total: response.data.total,
      },
      query.page,
      query.limit,
    );
  }

  override async getAgencyProfileById(
    id: string,
  ): Promise<AgencyProfileConfiguration | null> {
    const response = await firstValueFrom(
      this.http.get<{ success?: boolean; data?: Record<string, unknown> }>(
        `${environment.apiBaseUrl}/aggregation/agencies/profiles/${id}`,
      ),
    ).catch(() => null);

    return response?.data
      ? this.normalizeAgencyProfileConfiguration(response.data)
      : null;
  }

  override async createAgencyProfile(payload: {
    name: string;
  }): Promise<AgencyProfileConfiguration> {
    const response = await firstValueFrom(
      this.http.post<{ success?: boolean; data?: Record<string, unknown> }>(
        `${environment.apiBaseUrl}/aggregation/agencies/profiles`,
        cleanPayload({ name: payload.name }),
      ),
    );

    return this.normalizeAgencyProfileConfiguration(response.data ?? {});
  }

  override async updateAgencyProfile(
    id: string,
    payload: { name: string },
  ): Promise<AgencyProfileConfiguration> {
    const response = await firstValueFrom(
      this.http.put<{ success?: boolean; data?: Record<string, unknown> }>(
        `${environment.apiBaseUrl}/aggregation/agencies/profiles/${id}`,
        cleanPayload({ name: payload.name }),
      ),
    );

    return this.normalizeAgencyProfileConfiguration(response.data ?? {});
  }

  override deleteAgencyProfile(id: string): Promise<void> {
    return firstValueFrom(
      this.http.delete<{ success?: boolean; data?: unknown }>(
        `${environment.apiBaseUrl}/aggregation/agencies/profiles/${id}`,
      ),
    ).then(() => undefined);
  }

  override async getMyAgency(): Promise<AgencyRecord | null> {
    const response = await firstValueFrom(
      this.http.get<{ success?: boolean; data?: Record<string, unknown> }>(
        `${environment.apiBaseUrl}/aggregation/agencies/me`,
      ),
    );

    return this.normalizeAgencyRecord(response?.data);
  }

  override async getAgencyById(id: string): Promise<AgencyRecord | null> {
    const response = await firstValueFrom(
      this.http.get<{ success?: boolean; data?: Record<string, unknown> }>(
        `${environment.apiBaseUrl}/aggregation/agencies/${id}`,
      ),
    );

    return this.normalizeAgencyRecord(response?.data);
  }

  override async createAgency(payload: AgencyUpsert): Promise<AgencyRecord> {
    const body = this.buildAgencyBody(payload);
    const res = await firstValueFrom(
      this.http.post<{ success?: boolean; data?: Record<string, unknown> }>(
        `${environment.apiBaseUrl}/aggregation/agencies`,
        body,
      ),
    );
    const u: Record<string, unknown> =
      (res.data as Record<string, unknown>) ??
      (res as unknown as Record<string, unknown>);
    return this.mapAgencyRecord(u);
  }

  override async updateAgency(
    id: string,
    payload: AgencyUpsert,
  ): Promise<AgencyRecord | null> {
    const body = this.buildAgencyBody(payload);
    const res = await firstValueFrom(
      this.http.put<{ success?: boolean; data?: Record<string, unknown> }>(
        `${environment.apiBaseUrl}/aggregation/agencies/${id}`,
        body,
      ),
    ).catch(() => null);
    if (!res) return null;
    const u: Record<string, unknown> =
      (res.data as Record<string, unknown>) ??
      (res as unknown as Record<string, unknown>);
    return this.mapAgencyRecord(u);
  }

  private buildAgencyBody(
    payload: AgencyUpsert,
    id?: string,
  ): Record<string, unknown> {
    // BUSINESS LOGIC: agency save payloads keep geo coordinates, assignment arrays, and
    // secondary-assignment groups aligned with the backend contract in one shared builder.
    return cleanPayload({
      ...(id ? { id } : {}),
      name: payload.name,
      code: payload.code,
      profile: payload.profile,
      agencyChief: payload.agencyChief,
      officeNumber: payload.officeNumber,
      posNumber: payload.posNumber,
      status: "active",
      contact: {
        email: payload.contact.email,
        phone: {
          dialCode: payload.contact.phone.dialCode,
          iso2: payload.contact.phone.iso2,
          country: payload.contact.phone.country,
          number: payload.contact.phone.number,
        },
      },
      address: {
        formattedAddress: payload.address.formattedAddress,
        streetAddress: payload.address.streetAddress,
        locality: payload.address.locality,
        subLocality: payload.address.subLocality,
        region: payload.address.region,
        country: payload.address.country,
        postalCode: payload.address.postalCode,
        area: payload.address.area,
        geo: {
          type: "Point",
          coordinates: payload.address.geo,
        },
      },
      assignedSupervisors: payload.assignedSupervisors ?? [],
      assignedSecondarySupervisors: payload.assignedSecondarySupervisors ?? [],
      assignedAgents: payload.assignedAgents ?? [],
      assignedSecondaryAgents: payload.assignedSecondaryAgents ?? [],
      assignedBtlUsers: payload.assignedBtlUsers ?? [],
      assignedSecondaryBtlUsers: payload.assignedSecondaryBtlUsers ?? [],
      assignedLogisticsUsers: payload.assignedLogisticsUsers ?? [],
      assignedSecondaryLogisticsUsers:
        payload.assignedSecondaryLogisticsUsers ?? [],
      assignedPos: payload.assignedPos ?? [],
    });
  }

  /**
   * BUSINESS LOGIC: resolveAgencyAddress
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private resolveAgencyAddress(record: Record<string, unknown>): Record<string, unknown> {
    const addressDetails = this.asRecord(record["addressDetails"]);

    if (Object.keys(addressDetails).length > 0) {
      return addressDetails;
    }

    return this.asRecord(record["address"]);
  }

  /**
   * BUSINESS LOGIC: asRecord
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private asRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === "object" && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : {};
  }

  /**
   * BUSINESS LOGIC: readGeoCoordinates
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private readGeoCoordinates(value: unknown): [number, number] {
    if (Array.isArray(value)) {
      return [Number(value[0]) || 0, Number(value[1]) || 0];
    }

    const record = this.asRecord(value);
    const coordinates = Array.isArray(record["coordinates"])
      ? record["coordinates"]
      : [];

    return [Number(coordinates[0]) || 0, Number(coordinates[1]) || 0];
  }

  /**
   * BUSINESS LOGIC: mapAgencyRecord
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private mapAgencyRecord(u: Record<string, unknown>): AgencyRecord {
    const address = this.resolveAgencyAddress(u);
    const contactDetails =
      (u["contactDetails"] as Record<string, unknown>) ?? {};
    const contact =
      Object.keys(contactDetails).length > 0
        ? contactDetails
        : ((u["contact"] as Record<string, unknown>) ?? {});
    const phone = (contact["phone"] as Record<string, unknown>) ?? {};
    const geoCoords = this.readGeoCoordinates(address["geo"]);

    const assignedPos = this.readEntityIds(
      u["assignedPos"],
      u["assignedPosDetails"],
    );
    const assignedPosDetails = this.readAgencyAssignedPosSummaries(
      u["assignedPosDetails"],
      u["assignedPos"],
    );

    return {
      id: (u["_id"] as string) ?? (u["id"] as string) ?? "",
      name: (u["name"] as string) ?? "",
      code: (u["code"] as string) ?? "",
      city: (u["city"] as string) ?? (address["locality"] as string) ?? "",
      area: (u["area"] as string) ?? (address["area"] as string) ?? "",
      address:
        (typeof u["address"] === "string" ? u["address"] : null) ??
        (address["formattedAddress"] as string) ??
        (address["streetAddress"] as string) ??
        "",
      contact:
        (typeof u["contact"] === "string" ? u["contact"] : null) ??
        (phone["number"] as string) ??
        "",
      status:
        ((u["accountStatus"] as string) ?? (u["status"] as string)) ===
        "inactive"
          ? "inactive"
          : "active",
      profile: (u["profile"] as string) ?? undefined,
      profileName: (u["profileName"] as string) ?? "",
      stockStatus: (u["stockStatus"] as AgencyStockStatus) ?? "ok",
      supervisors: Number(u["supervisors"] ?? 0),
      agents: Number(u["agents"] ?? 0),
      posCount: this.readCount(
        u["posCount"],
        assignedPosDetails.length || undefined,
        assignedPos.length || undefined,
      ),
      agencyChief: (u["agencyChief"] as string) ?? "",
      officeNumber: (u["officeNumber"] as string) ?? "",
      posNumber: (u["posNumber"] as string) ?? "",
      contactDetails: {
        email: (contact["email"] as string) ?? "",
        phone: {
          dialCode: Number(phone["dialCode"] ?? 0),
          iso2: (phone["iso2"] as string) ?? "",
          country: (phone["country"] as string) ?? "",
          number: (phone["number"] as string) ?? "",
        },
      },
      addressDetails: {
        formattedAddress: (address["formattedAddress"] as string) ?? "",
        streetAddress: (address["streetAddress"] as string) ?? "",
        locality:
          (address["locality"] as string) ?? (u["city"] as string) ?? "",
        subLocality: (address["subLocality"] as string) ?? "",
        region: (address["region"] as string) ?? "",
        country: (address["country"] as string) ?? "",
        postalCode: (address["postalCode"] as string) ?? "",
        area: (address["area"] as string) ?? (u["area"] as string) ?? "",
        geo: geoCoords,
      },
      assignedSupervisors: this.readEntityIds(
        u["assignedSupervisors"],
        u["assignedSupervisorDetails"],
      ),
      assignedSupervisorDetails: this.readAgencyAssignedUserSummaries(
        u["assignedSupervisorDetails"],
        u["assignedSupervisors"],
      ),
      assignedSecondarySupervisors: this.readEntityIds(
        u["assignedSecondarySupervisors"],
        u["assignedSecondarySupervisorDetails"],
      ),
      assignedSecondarySupervisorDetails: this.readAgencyAssignedUserSummaries(
        u["assignedSecondarySupervisorDetails"],
        u["assignedSecondarySupervisors"],
      ),
      assignedAgents: this.readEntityIds(
        u["assignedAgents"],
        u["assignedAgentDetails"],
      ),
      assignedAgentDetails: this.readAgencyAssignedUserSummaries(
        u["assignedAgentDetails"],
        u["assignedAgents"],
      ),
      assignedSecondaryAgents: this.readEntityIds(
        u["assignedSecondaryAgents"],
        u["assignedSecondaryAgentDetails"],
      ),
      assignedSecondaryAgentDetails: this.readAgencyAssignedUserSummaries(
        u["assignedSecondaryAgentDetails"],
        u["assignedSecondaryAgents"],
      ),
      assignedBtlUsers: this.readEntityIds(
        u["assignedBtlUsers"],
        u["assignedBtlUserDetails"],
      ),
      assignedBtlUserDetails: this.readAgencyAssignedUserSummaries(
        u["assignedBtlUserDetails"],
        u["assignedBtlUsers"],
      ),
      assignedSecondaryBtlUsers: this.readEntityIds(
        u["assignedSecondaryBtlUsers"],
        u["assignedSecondaryBtlUserDetails"],
      ),
      assignedSecondaryBtlUserDetails: this.readAgencyAssignedUserSummaries(
        u["assignedSecondaryBtlUserDetails"],
        u["assignedSecondaryBtlUsers"],
      ),
      assignedLogisticsUsers: this.readEntityIds(
        u["assignedLogisticsUsers"],
        u["assignedLogisticsUserDetails"],
      ),
      assignedLogisticsUserDetails: this.readAgencyAssignedUserSummaries(
        u["assignedLogisticsUserDetails"],
        u["assignedLogisticsUsers"],
      ),
      assignedSecondaryLogisticsUsers: this.readEntityIds(
        u["assignedSecondaryLogisticsUsers"],
        u["assignedSecondaryLogisticsUserDetails"],
      ),
      assignedSecondaryLogisticsUserDetails:
        this.readAgencyAssignedUserSummaries(
          u["assignedSecondaryLogisticsUserDetails"],
          u["assignedSecondaryLogisticsUsers"],
        ),
      assignedPos,
      assignedPosDetails,
    };
  }

  override async getAgents(
    query: AgentListQuery = {},
  ): Promise<PagedResult<AgentRecord>> {
    const params: Record<string, string> = {};

    if (query.page !== undefined) params["page"] = String(query.page);
    if (query.limit !== undefined) params["limit"] = String(query.limit);
    if (query.search) params["search"] = query.search;
    if (query.accountStatus) params["accountStatus"] = query.accountStatus;
    if (query.approvalStatus) params["approvalStatus"] = query.approvalStatus;
    if (query.agentType) params["agentType"] = query.agentType;
    if (query.agency) params["agency"] = query.agency;
    if (query.assignedAgencyId) params["assignedAgencyId"] = query.assignedAgencyId;

    const response = await firstValueFrom(
      this.http.get<{
        success: boolean;
        data: {
          items: Record<string, unknown>[];
          page: number;
          limit: number;
          total: number;
        };
      }>(`${environment.apiBaseUrl}/auth/agents`, {
        params,
      }),
    );
    const items = (response.data?.items ?? []).map((u) => {
      const address = (u["address"] as Record<string, unknown>) ?? {};
      const contact = (u["contact"] as Record<string, unknown>) ?? {};
      const phone = (contact["phone"] as Record<string, unknown>) ?? {};
      const agentProfile =
        (u["agentProfile"] as Record<string, unknown>) ??
        ((u["profile"] as Record<string, unknown>)?.["agent"] as Record<
          string,
          unknown
        >) ??
        {};
      const guarantor1 =
        (agentProfile["guarantorContact1"] as Record<string, unknown>) ?? {};
      const guarantor2 =
        (agentProfile["guarantorContact2"] as Record<string, unknown>) ?? {};
      const agencies = this.normalizeAgentAgencies(u);
      const primaryAgency = agencies[0];
      const assignedPos = this.readAgentAssignedPos(
        u["assignedPos"],
        primaryAgency,
      );
      const geoRaw = address["geo"] as
        | { coordinates?: number[] }
        | number[]
        | undefined;
      const geoCoords = Array.isArray(geoRaw)
        ? geoRaw
        : (geoRaw?.coordinates ?? [0, 0]);
      const phoneNumber =
        this.toNullableString(phone["number"]) ??
        this.toNullableString(contact["phoneNumber"]) ??
        this.toNullableString(u["phoneNumber"]) ??
        this.toNullableString(u["phone"]) ??
        "";
      const dialCodeValue = this.toNullableString(phone["dialCode"]) ?? "";

      const resolvedId = this.resolveMongoId(u);
      return {
        ...u,
        id: resolvedId,
        authUserId: resolvedId,
        name:
          (u["fullName"] as string) ||
          (u["name"] as string) ||
          `${u["firstName"] ?? ""} ${u["lastName"] ?? ""}`.trim(),
        firstName: (u["firstName"] as string) ?? "",
        lastName: (u["lastName"] as string) ?? "",
        email: (u["email"] as string) ?? "",
        contact: {
          email: (contact["email"] as string) ?? (u["email"] as string) ?? "",
          phone: {
            dialCode: Number(phone["dialCode"] ?? 0),
            iso2: (phone["iso2"] as string) ?? "",
            country: (phone["country"] as string) ?? "",
            number: phoneNumber,
          },
        },
        phoneDisplay:
          this.toNullableString(u["contactPhoneDisplay"]) ??
          this.toNullableString(u["phoneDisplay"]) ??
          this.toNullableString(contact["phoneDisplay"]) ??
          (phoneNumber.startsWith("+")
            ? phoneNumber
            : [dialCodeValue ? `+${dialCodeValue}` : "", phoneNumber]
                .filter(Boolean)
                .join(" ")
                .trim()),
        address: {
          formattedAddress: (address["formattedAddress"] as string) ?? "",
          streetAddress: (address["streetAddress"] as string) ?? "",
          locality: (address["locality"] as string) ?? "",
          subLocality: (address["subLocality"] as string) ?? "",
          region: (address["region"] as string) ?? "",
          country: (address["country"] as string) ?? "",
          postalCode: (address["postalCode"] as string) ?? "",
          area: (address["area"] as string) ?? "",
          geo: [Number(geoCoords[0]) || 0, Number(geoCoords[1]) || 0] as [
            number,
            number,
          ],
        },
        agencyId: primaryAgency?.id ?? (u["assignedAgencyId"] as string) ?? "",
        agencyName: primaryAgency?.name || (u["agencyName"] as string) || "",
        city: (address["locality"] as string) ?? "",
        area: (address["area"] as string) ?? "",
        assignedAgencies: agencies,
        assignedPos,
        posAssigned: this.readCount(
          u["assignedPosCount"],
          u["posAssigned"],
          assignedPos.length || undefined,
        ),
        agentType: ((agentProfile["agentType"] as string) ??
          "Employee") as AgentRecord["agentType"],
        agentProfile: {
          agentType: ((agentProfile["agentType"] as string) ??
            "Employee") as AgentRecord["agentType"],
          dealerId: String(agentProfile["dealerId"] ?? ""),
          joinedAt: agentProfile["joinedAt"]
            ? String(agentProfile["joinedAt"])
            : "",
          guarantorContact1: {
            name: (guarantor1["name"] as string) ?? "",
            phoneNumber: (guarantor1["phoneNumber"] as string) ?? "",
          },
          guarantorContact2: {
            name: (guarantor2["name"] as string) ?? "",
            phoneNumber: (guarantor2["phoneNumber"] as string) ?? "",
          },
        },
        status:
          (u["accountStatus"] as string) === "active" ? "active" : "inactive",
        approvalStatus: (u["approvalStatus"] as string) ?? "",
        dealerId: String(agentProfile["dealerId"] ?? ""),
        joinedAt: agentProfile["joinedAt"]
          ? String(agentProfile["joinedAt"])
          : "",
        taskSummary: u["taskSummary"] as
          | {
              total: number;
              open: number;
              inProgress: number;
              completed: number;
            }
          | undefined,
      } as unknown as AgentRecord;
    });
    return this.capPagedResult(
      {
        items,
        page: response.data?.page ?? query.page ?? 1,
        limit: response.data?.limit ?? query.limit ?? items.length,
        total: response.data?.total ?? items.length,
      },
      query.page,
      query.limit,
    );
  }

  override async getAgentById(id: string): Promise<AgentRecord | null> {
    const res = await firstValueFrom(
      this.http.get<{ success: boolean; data: Record<string, unknown> }>(
        `${environment.apiBaseUrl}/auth/agents/${id}`,
      ),
    ).catch(() => null);

    if (!res) return null;

    const data =
      (res.data as Record<string, unknown>) ??
      (res as unknown as Record<string, unknown>);
    const u: Record<string, unknown> =
      (data["user"] as Record<string, unknown>) ?? data;
    const address = (u["address"] as Record<string, unknown>) ?? {};
    const contact = (u["contact"] as Record<string, unknown>) ?? {};
    const phone = (contact["phone"] as Record<string, unknown>) ?? {};
    const agentProfile =
      (u["agentProfile"] as Record<string, unknown>) ??
      ((u["profile"] as Record<string, unknown>)?.["agent"] as Record<
        string,
        unknown
      >) ??
      {};
    const guarantor1 =
      (agentProfile["guarantorContact1"] as Record<string, unknown>) ?? {};
    const guarantor2 =
      (agentProfile["guarantorContact2"] as Record<string, unknown>) ?? {};
    const agencies = this.normalizeAgentAgencies(u);
    const primaryAgency = agencies[0];
    const rawPos = Array.isArray(u["assignedPos"])
      ? u["assignedPos"]
      : Array.isArray(agentProfile["assignedPos"])
        ? agentProfile["assignedPos"]
        : [];
    const assignedPos = this.readAgentAssignedPos(rawPos, primaryAgency);
    const geoRaw = address["geo"] as
      | { coordinates?: number[] }
      | number[]
      | undefined;
    const geoCoords = Array.isArray(geoRaw)
      ? geoRaw
      : (geoRaw?.coordinates ?? [0, 0]);
    const phoneNumber =
      this.toNullableString(phone["number"]) ??
      this.toNullableString(contact["phoneNumber"]) ??
      this.toNullableString(u["phoneNumber"]) ??
      this.toNullableString(u["phone"]) ??
      "";
    const dialCodeValue = this.toNullableString(phone["dialCode"]) ?? "";

    return {
      id: (u["_id"] as string) ?? (u["id"] as string) ?? "",
      authUserId: (u["_id"] as string) ?? (u["id"] as string) ?? "",
      name:
        (u["fullName"] as string) ||
        `${u["firstName"] ?? ""} ${u["lastName"] ?? ""}`.trim(),
      firstName: (u["firstName"] as string) ?? "",
      lastName: (u["lastName"] as string) ?? "",
      email: (u["email"] as string) ?? "",
      contact: {
        email: (contact["email"] as string) ?? (u["email"] as string) ?? "",
        phone: {
          dialCode: Number(phone["dialCode"] ?? 0),
          iso2: (phone["iso2"] as string) ?? "",
          country: (phone["country"] as string) ?? "",
          number: phoneNumber,
        },
      },
      phoneDisplay:
        this.toNullableString(u["contactPhoneDisplay"]) ??
        this.toNullableString(u["phoneDisplay"]) ??
        this.toNullableString(contact["phoneDisplay"]) ??
        (phoneNumber.startsWith("+")
          ? phoneNumber
          : [dialCodeValue ? `+${dialCodeValue}` : "", phoneNumber]
              .filter(Boolean)
              .join(" ")
              .trim()),
      address: {
        formattedAddress: (address["formattedAddress"] as string) ?? "",
        streetAddress: (address["streetAddress"] as string) ?? "",
        locality: (address["locality"] as string) ?? "",
        subLocality: (address["subLocality"] as string) ?? "",
        region: (address["region"] as string) ?? "",
        country: (address["country"] as string) ?? "",
        postalCode: (address["postalCode"] as string) ?? "",
        area: (address["area"] as string) ?? "",
        geo: [Number(geoCoords[0]) || 0, Number(geoCoords[1]) || 0] as [
          number,
          number,
        ],
      },
      city: (address["locality"] as string) ?? "",
      area: (address["area"] as string) ?? "",
      assignedAgencies: agencies,
      assignedPos,
      agencyId: primaryAgency?.id ?? "",
      agencyName: primaryAgency?.name ?? "",
      posAssigned: this.readCount(
        u["assignedPosCount"],
        u["posAssigned"],
        assignedPos.length || undefined,
      ),
      agentType: ((agentProfile["agentType"] as string) ??
        "Employee") as AgentRecord["agentType"],
      agentProfile: {
        agentType: ((agentProfile["agentType"] as string) ??
          "Employee") as AgentRecord["agentType"],
        dealerId: String(agentProfile["dealerId"] ?? ""),
        joinedAt: agentProfile["joinedAt"]
          ? String(agentProfile["joinedAt"])
          : "",
        guarantorContact1: {
          name: (guarantor1["name"] as string) ?? "",
          phoneNumber: (guarantor1["phoneNumber"] as string) ?? "",
        },
        guarantorContact2: {
          name: (guarantor2["name"] as string) ?? "",
          phoneNumber: (guarantor2["phoneNumber"] as string) ?? "",
        },
      },
      status:
        (u["accountStatus"] as string) === "active" ? "active" : "inactive",
      approvalStatus: (u["approvalStatus"] as string) ?? "",
      dealerId: String(agentProfile["dealerId"] ?? ""),
      joinedAt: agentProfile["joinedAt"]
        ? String(agentProfile["joinedAt"])
        : "",
      taskSummary: u["taskSummary"] as AgentRecord["taskSummary"],
    };
  }

  override async getPendingAgentRequests(): Promise<
    AgentApprovalRequestRecord[]
  > {
    const result = await this.getAgents({
      page: 1,
      limit: 100,
      accountStatus: "pending_approval",
      approvalStatus: "pending",
    });

    return result.items.map((agent) => ({
      id: agent.id,
      authUserId: agent.authUserId,
      name: agent.name,
      email: agent.email,
      contact: agent.contact,
      address: agent.address,
      requestedAgency: agent.assignedAgencies[0] ?? { id: "", name: "" },
      requestedPos: agent.assignedPos,
      requestDate: agent.joinedAt || "",
      approvalStatus: "pending",
      agentType: agent.agentType,
      agentProfile: agent.agentProfile,
      dealerId: agent.dealerId,
    }));
  }

  override async approveAgentRequest(id: string): Promise<AgentRecord | null> {
    await firstValueFrom(
      this.http.patch<unknown>(
        `${environment.apiBaseUrl}/auth/agents/${id}`,
        cleanPayload({ accountStatus: "active" }),
      ),
    );

    return this.getAgentById(id);
  }

  override getPos(query: PosListQuery = {}): Promise<PagedResult<PosRecord>> {
    return firstValueFrom(
      this.http.get<ApiCollectionResponse<PosRecord> | PosRecord[]>(
        `${environment.apiBaseUrl}/aggregation/pos${this.toQueryString(query)}`,
      ),
    ).then((response) =>
      this.capPagedResult(
        this.extractPagedCollection(response, (item) =>
          this.normalizePosRecord(item),
        ),
        query.page,
        query.limit,
      ),
    );
  }

  override getPosById(id: string): Promise<PosRecord | null> {
    return firstValueFrom(
      this.http.get<ApiEntityResponse<PosRecord> | PosRecord>(
        `${environment.apiBaseUrl}/aggregation/pos/${id}`,
      ),
    ).then((response) => {
      const item = this.extractEntity(response);
      return item ? this.normalizePosRecord(item) : null;
    });
  }

  override createPos(payload: PosUpsertPayload): Promise<PosRecord> {
    return firstValueFrom(
      this.http.post<ApiEntityResponse<PosRecord> | PosRecord>(
        `${environment.apiBaseUrl}/aggregation/pos`,
        cleanPayload(this.toPosApiPayload(payload)),
      ),
    ).then((response) => this.normalizePosRecord(this.requireEntity(response)));
  }

  override updatePos(
    id: string,
    payload: PosUpsertPayload,
  ): Promise<PosRecord | null> {
    return firstValueFrom(
      this.http.put<ApiEntityResponse<PosRecord> | PosRecord>(
        `${environment.apiBaseUrl}/aggregation/pos/${id}`,
        cleanPayload(this.toPosApiPayload(payload)),
      ),
    ).then((response) => {
      const item = this.extractEntity(response);
      return item ? this.normalizePosRecord(item) : null;
    });
  }

  override deletePos(id: string): Promise<boolean> {
    return firstValueFrom(
      this.http.delete<{ success?: boolean }>(
        `${environment.apiBaseUrl}/aggregation/pos/${id}`,
      ),
    ).then((res) => res?.success ?? true);
  }

  override addPosStockTransactions(
    id: string,
    payload: PosStockTransactionPayload,
  ): Promise<boolean> {
    return firstValueFrom(
      this.http.post<{ success?: boolean }>(
        `${environment.apiBaseUrl}/aggregation/pos/${id}/stock-transactions`,
        payload,
      ),
    ).then((res) => res?.success ?? true);
  }

  override async updatePosStockVariant(
    id: string,
    variantId: string,
    payload: UpdatePosStockVariantPayload,
  ): Promise<boolean> {
    await firstValueFrom(
      this.http.put(
        `${environment.apiBaseUrl}/aggregation/pos/${id}/stock/variants/${variantId}`,
        cleanPayload(payload as unknown as Record<string, unknown>),
      ),
    );

    return true;
  }

  override async deletePosStockCategory(
    id: string,
    categoryId: string,
  ): Promise<boolean> {
    await firstValueFrom(
      this.http.delete(
        `${environment.apiBaseUrl}/aggregation/pos/${id}/stock/categories/${categoryId}`,
      ),
    );

    return true;
  }

  override getPosStockDetails(id: string): Promise<PosStockDetails> {
    return firstValueFrom(
      this.http.get<
        ApiEntityResponse<Record<string, unknown>> | Record<string, unknown>
      >(`${environment.apiBaseUrl}/aggregation/pos/${id}/stock-details`),
    ).then((response) =>
      this.normalizeStockDetailsEntity(this.extractEntity(response)),
    );
  }

  override getPosStockTransactions(id: string): Promise<PosStockEntry[]> {
    return firstValueFrom(
      this.http.get<
        ApiEntityResponse<Record<string, unknown>> | Record<string, unknown>
      >(`${environment.apiBaseUrl}/aggregation/pos/${id}/stock-transactions`),
    ).then((response) =>
      this.normalizeStockTransactionsEntity(this.extractEntity(response)),
    );
  }

  override deleteProspect(id: string): Promise<boolean> {
    return firstValueFrom(
      this.http.delete<{ success?: boolean }>(
        `${environment.apiBaseUrl}/aggregation/pos/prospects/${id}`,
      ),
    ).then((res) => res?.success ?? true);
  }

  override addProspectStockTransactions(
    id: string,
    payload: ProspectStockTransactionPayload,
  ): Promise<boolean> {
    return firstValueFrom(
      this.http.post<{ success?: boolean }>(
        `${environment.apiBaseUrl}/aggregation/pos/prospects/${id}/stock-transactions`,
        payload,
      ),
    ).then((res) => res?.success ?? true);
  }

  override async updateProspectStockVariant(
    id: string,
    variantId: string,
    payload: UpdateProspectStockVariantPayload,
  ): Promise<boolean> {
    await firstValueFrom(
      this.http.put(
        `${environment.apiBaseUrl}/aggregation/pos/prospects/${id}/stock/variants/${variantId}`,
        cleanPayload(payload as unknown as Record<string, unknown>),
      ),
    );

    return true;
  }

  override async deleteProspectStockCategory(
    id: string,
    categoryId: string,
  ): Promise<boolean> {
    await firstValueFrom(
      this.http.delete(
        `${environment.apiBaseUrl}/aggregation/pos/prospects/${id}/stock/categories/${categoryId}`,
      ),
    );

    return true;
  }

  override getProspects(
    query: ProspectListQuery = {},
  ): Promise<PagedResult<ProspectRecord>> {
    return firstValueFrom(
      this.http.get<ApiCollectionResponse<ProspectRecord> | ProspectRecord[]>(
        `${environment.apiBaseUrl}/aggregation/pos/prospects${this.toQueryString(query)}`,
      ),
    ).then((response) =>
      this.capPagedResult(
        this.extractPagedCollection(response, (item) =>
          this.normalizeProspectRecord(item),
        ),
        query.page,
        query.limit,
      ),
    );
  }

  override getProspectById(id: string): Promise<ProspectRecord | null> {
    return firstValueFrom(
      this.http.get<ApiEntityResponse<ProspectRecord> | ProspectRecord>(
        `${environment.apiBaseUrl}/aggregation/pos/prospects/${id}`,
      ),
    ).then((response) => {
      const item = this.extractEntity(response);
      return item ? this.normalizeProspectRecord(item) : null;
    });
  }

  override createProspect(
    payload: ProspectUpsertPayload,
  ): Promise<ProspectRecord> {
    return firstValueFrom(
      this.http.post<ApiEntityResponse<ProspectRecord> | ProspectRecord>(
        `${environment.apiBaseUrl}/aggregation/pos/prospects`,
        this.toCleanProspectApiPayload(payload),
      ),
    ).then((response) =>
      this.normalizeProspectRecord(this.requireEntity(response)),
    );
  }

  override updateProspect(
    id: string,
    payload: ProspectUpsertPayload,
  ): Promise<ProspectRecord | null> {
    return firstValueFrom(
      this.http.put<ApiEntityResponse<ProspectRecord> | ProspectRecord>(
        `${environment.apiBaseUrl}/aggregation/pos/prospects/${id}`,
        this.toCleanProspectApiPayload(payload),
      ),
    ).then((response) => {
      const item = this.extractEntity(response);
      return item ? this.normalizeProspectRecord(item) : null;
    });
  }

  override approveProspect(
    id: string,
    payload: ProspectApprovalPayload,
  ): Promise<ProspectRecord | null> {
    return firstValueFrom(
      this.http.post<ApiEntityResponse<ProspectRecord> | ProspectRecord | { success?: boolean }>(
        `${environment.apiBaseUrl}/aggregation/pos/prospects/${id}/approve`,
        { approvalNote: payload.approvalNote },
      ),
    ).then((response) => {
      if (!response || typeof response !== "object") {
        return null;
      }

      if ("data" in response) {
        const item = response.data;
        return item && typeof item === "object"
          ? this.normalizeProspectRecord(item as ProspectRecord)
          : null;
      }

      if ("success" in response && !("id" in response) && !("_id" in response)) {
        return null;
      }

      return this.normalizeProspectRecord(response as ProspectRecord);
    });
  }

  override getProspectStockDetails(id: string): Promise<PosStockDetails> {
    return firstValueFrom(
      this.http.get<
        ApiEntityResponse<Record<string, unknown>> | Record<string, unknown>
      >(
        `${environment.apiBaseUrl}/aggregation/pos/prospects/${id}/stock-details`,
      ),
    ).then((response) =>
      this.normalizeStockDetailsEntity(this.extractEntity(response)),
    );
  }

  override getProspectStockTransactions(id: string): Promise<PosStockEntry[]> {
    return firstValueFrom(
      this.http.get<
        ApiEntityResponse<Record<string, unknown>> | Record<string, unknown>
      >(
        `${environment.apiBaseUrl}/aggregation/pos/prospects/${id}/stock-transactions`,
      ),
    ).then((response) =>
      this.normalizeStockTransactionsEntity(this.extractEntity(response)),
    );
  }
  override async getSupervisors(
    query: {
      page?: number;
      limit?: number;
      search?: string;
      accountStatus?: string;
      approvalStatus?: string;
      agency?: string;
    } = {},
  ): Promise<PagedResult<SupervisorRecord>> {
    const params: Record<string, string> = {
      page: String(query.page ?? 1),
      limit: String(query.limit ?? 20),
    };
    if (query.search) params["search"] = query.search;
    if (query.accountStatus) params["accountStatus"] = query.accountStatus;
    if (query.approvalStatus) params["approvalStatus"] = query.approvalStatus;
    if (query.agency) params["agency"] = query.agency;

    const res = await firstValueFrom(
      this.http.get<{
        success: boolean;
        data: {
          items: Record<string, unknown>[];
          page: number;
          limit: number;
          total: number;
        };
      }>(`${environment.apiBaseUrl}/auth/supervisors`, { params }),
    );

    const items: SupervisorRecord[] = (res.data?.items ?? []).map((u) => {
      const contact = (u["contact"] as Record<string, unknown>) ?? {};
      const phone = (contact["phone"] as Record<string, unknown>) ?? {};
      const address = (u["address"] as Record<string, unknown>) ?? {};
      const geoRaw = address["geo"] as
        | { coordinates?: unknown }
        | number[]
        | undefined;
      const geoCoordinates = Array.isArray(geoRaw)
        ? geoRaw
        : Array.isArray(geoRaw?.coordinates)
          ? geoRaw.coordinates
          : [0, 0];
      const supervisorProfile =
        (u["supervisorProfile"] as Record<string, unknown>) ?? {};
      const rawProfile = (u["profile"] as Record<string, unknown>) ?? {};
      const nestedSupervisorProfile =
        (rawProfile["supervisor"] as Record<string, unknown>) ?? {};
      const guarantor1 =
        (supervisorProfile["guarantorContact1"] as Record<string, unknown>) ??
        {};
      const guarantor2 =
        (supervisorProfile["guarantorContact2"] as Record<string, unknown>) ??
        {};
      const assignedAgencyId = u["assignedAgencyId"] as string | null;
      const assignedAgency =
        (u["assignedAgency"] as Record<string, unknown>) ??
        (supervisorProfile["assignedAgency"] as Record<string, unknown>) ??
        null;
      const dialCode = phone["dialCode"] ? `+${phone["dialCode"]}` : "";
      const phoneNumber = (phone["number"] as string) ?? "";

      return {
        id: (u["id"] as string) ?? (u["_id"] as string) ?? "",
        name:
          (u["fullName"] as string) ||
          `${u["firstName"] ?? ""} ${u["lastName"] ?? ""}`.trim(),
        email: (u["email"] as string) ?? "",
        contact: {
          email: (contact["email"] as string) ?? (u["email"] as string) ?? "",
          phone: {
            dialCode: Number(phone["dialCode"] ?? 0),
            iso2: (phone["iso2"] as string) ?? "",
            country: (phone["country"] as string) ?? "",
            number: phoneNumber,
          },
        },
        phoneDisplay: [dialCode, phoneNumber].filter(Boolean).join(" ").trim(),
        address: {
          formattedAddress: (address["formattedAddress"] as string) ?? "",
          streetAddress: (address["streetAddress"] as string) ?? "",
          locality: (address["locality"] as string) ?? "",
          subLocality: (address["subLocality"] as string) ?? "",
          region: (address["region"] as string) ?? "",
          country: (address["country"] as string) ?? "",
          postalCode: (address["postalCode"] as string) ?? "",
          area: (address["area"] as string) ?? "",
          geo: [
            Number(geoCoordinates[0]) || 0,
            Number(geoCoordinates[1]) || 0,
          ] as [number, number],
        },
        city: (address["locality"] as string) ?? "",
        status:
          (u["accountStatus"] as string) === "active" ? "active" : "inactive",
        createdAt: (u["createdAt"] as string) ?? "",
        assignedAgencies: assignedAgency
          ? [
              {
                id:
                  (assignedAgency["id"] as string) ??
                  (assignedAgency["agencyId"] as string) ??
                  assignedAgencyId ??
                  "",
                name:
                  (assignedAgency["name"] as string) ??
                  (assignedAgency["agencyName"] as string) ??
                  "",
                city: (assignedAgency["city"] as string) ?? "",
                area: (assignedAgency["area"] as string) ?? "",
              },
            ]
          : assignedAgencyId
            ? [{ id: assignedAgencyId, name: "" }]
            : [],
        supervisorProfile: {
          profile:
            (supervisorProfile["profile"] as string) ??
            (nestedSupervisorProfile["profile"] as string) ??
            "",
          code: (supervisorProfile["code"] as string) ?? "",
          guarantorContact1: {
            name: (guarantor1["name"] as string) ?? "",
            phoneNumber: (guarantor1["phoneNumber"] as string) ?? "",
          },
          guarantorContact2: {
            name: (guarantor2["name"] as string) ?? "",
            phoneNumber: (guarantor2["phoneNumber"] as string) ?? "",
          },
        },
      } as SupervisorRecord;
    });

    const page = res.data?.page ?? query.page ?? 1;
    const limit = res.data?.limit ?? query.limit ?? 20;
    const total = res.data?.total ?? items.length;
    const pagedItems =
      items.length > limit
        ? items.slice((page - 1) * limit, page * limit)
        : items;

    return { items: pagedItems, page, limit, total };
  }

  override async getPendingSupervisorRequests(
    query: { page?: number; limit?: number; search?: string } = {},
  ): Promise<PagedResult<SupervisorApprovalRequestRecord>> {
    const params: Record<string, string> = {
      page: String(query.page ?? 1),
      limit: String(query.limit ?? 10),
    };
    if (query.search) params["search"] = query.search;
    const res = await firstValueFrom(
      this.http.get<{
        success: boolean;
        data: {
          items: Record<string, unknown>[];
          page: number;
          limit: number;
          total: number;
        };
      }>(`${environment.apiBaseUrl}/auth/supervisors/pending`, { params }),
    );
    const items = (res.data?.items ?? []).map((u) => {
      const contact = (u["contact"] as Record<string, unknown>) ?? {};
      const phone = (contact["phone"] as Record<string, unknown>) ?? {};
      return {
        id: (u["id"] as string) ?? (u["_id"] as string) ?? "",
        name:
          (u["fullName"] as string) ||
          `${u["firstName"] ?? ""} ${u["lastName"] ?? ""}`.trim(),
        email: (u["email"] as string) ?? "",
        contact: {
          email: (contact["email"] as string) ?? (u["email"] as string) ?? "",
          phone: {
            dialCode: Number(phone["dialCode"] ?? 0),
            iso2: (phone["iso2"] as string) ?? "",
            country: (phone["country"] as string) ?? "",
            number: (phone["number"] as string) ?? "",
          },
        },
        requestedAgency: null,
        requestDate: (u["createdAt"] as string) ?? "",
        approvalStatus: ((u["approvalStatus"] as string) ??
          "pending") as SupervisorApprovalRequestRecord["approvalStatus"],
      };
    });

    return this.capPagedResult(
      {
        items,
        page: res.data?.page ?? query.page ?? 1,
        limit: res.data?.limit ?? query.limit ?? 10,
        total: res.data?.total ?? items.length,
      },
      query.page,
      query.limit,
    );
  }

  override createSupervisor(
    payload: SupervisorUpsertPayload,
  ): Promise<SupervisorRecord> {
    return firstValueFrom(
      this.http.post<SupervisorRecord>(
        `${environment.apiBaseUrl}/auth/supervisors`,
        cleanPayload(payload as unknown as Record<string, unknown>),
      ),
    );
  }

  override updateSupervisor(
    id: string,
    payload: SupervisorUpsertPayload,
  ): Promise<SupervisorRecord | null> {
    return firstValueFrom(
      this.http.patch<SupervisorRecord>(
        `${environment.apiBaseUrl}/auth/supervisors/${id}`,
        cleanPayload(payload as unknown as Record<string, unknown>),
      ),
    );
  }

  override updateBtlUser(
    id: string,
    payload: BtlUserUpdatePayload,
  ): Promise<BtlUserApiRecord | null> {
    return firstValueFrom(
      this.http.patch<BtlUserApiRecord>(
        `${environment.apiBaseUrl}/auth/btl-users/${id}`,
        cleanPayload(payload as unknown as Record<string, unknown>),
      ),
    );
  }

  override approveSupervisorRequest(
    id: string,
    assignedAgencyId: string,
  ): Promise<SupervisorRecord | null> {
    return firstValueFrom(
      this.http.post<SupervisorRecord>(
        `${environment.apiBaseUrl}/auth/supervisors/${id}/approve`,
        { assignedAgencyId },
      ),
    );
  }

  override rejectSupervisorRequest(id: string): Promise<void> {
    return firstValueFrom(
      this.http.post<void>(
        `${environment.apiBaseUrl}/auth/supervisors/${id}/reject`,
        {},
      ),
    );
  }

  override deleteSupervisor(ids: string[]): Promise<boolean> {
    return firstValueFrom(
      this.http.delete<boolean>(`${environment.apiBaseUrl}/auth/supervisors`, {
        body: { ids },
      }),
    );
  }

  override deleteBtlUser(ids: string[]): Promise<boolean> {
    return firstValueFrom(
      this.http.delete<boolean>(`${environment.apiBaseUrl}/auth/btl-users`, {
        body: { ids },
      }),
    );
  }
  override async createVehicle(
    payload: CreateVehiclePayload,
  ): Promise<VehicleApiRecord> {
    const res = await firstValueFrom(
      this.http.post<{ success: boolean; data: Record<string, unknown> }>(
        `${environment.apiBaseUrl}/aggregation/logistics/vehicles`,
        payload,
      ),
    );
    const v = res.data ?? (res as unknown as Record<string, unknown>);

    return {
      id: (v["id"] as string) ?? (v["_id"] as string) ?? "",
      registrationNumber: (v["registrationNumber"] as string) ?? "",
      make: (v["make"] as string) ?? "",
      modelName: (v["modelName"] as string) ?? "",
      vehicleName: (v["vehicleName"] as string) ?? "",
      year: Number(v["year"] ?? 0),
      type: (v["type"] as string) ?? "",
      assignedToAuthUserId: this.readEntityId(
        v["assignedToAuthUserId"] ?? v["assignedUser"],
      ),
      currentAgencyId: this.readEntityId(
        v["currentAgencyId"] ?? v["currentAgency"],
      ),
      status: (v["status"] as string) ?? "",
      fuelType: (v["fuelType"] as string) ?? "",
      isActive: Boolean(v["isActive"]),
      documents: Array.isArray(v["documents"])
        ? (v["documents"] as Array<Record<string, unknown>>).map((doc) => ({
            imageUris: Array.isArray(doc["imageUris"])
              ? (doc["imageUris"] as string[])
              : [],
            expiryDate: (doc["expiryDate"] as string) ?? "",
            category: (doc["category"] as string) ?? "",
            categoryName: (doc["categoryName"] as string) ?? "",
          }))
        : [],
      createdAt: (v["createdAt"] as string) ?? "",
      updatedAt: (v["updatedAt"] as string) ?? "",
    };
  }

  override async createVehicleDocument(
    payload: CreateVehicleDocumentStandalonePayload,
  ): Promise<VehicleDocumentApiRecord | null> {
    try {
      const res = await firstValueFrom(
        this.http.post<{ success: boolean; data: Record<string, unknown> }>(
          `${environment.apiBaseUrl}/aggregation/logistics/vehicle-documents`,
          payload,
        ),
      );
      const d = res.data ?? (res as unknown as Record<string, unknown>);
      return {
        id: (d["id"] as string) ?? (d["_id"] as string) ?? undefined,
        vehicleId: (d["vehicleId"] as string) ?? payload.vehicleId,
        imageUris: Array.isArray(d["imageUris"])
          ? (d["imageUris"] as string[])
          : payload.imageUris,
        expiryDate: (d["expiryDate"] as string) ?? payload.expiryDate,
        category: (d["category"] as string) ?? payload.category,
        categoryName: (d["categoryName"] as string) ?? payload.categoryName,
      };
    } catch {
      return null;
    }
  }

  override async updateVehicleDocument(
    documentId: string,
    payload: UpdateVehicleDocumentPayload,
  ): Promise<VehicleDocumentApiRecord | null> {
    try {
      const res = await firstValueFrom(
        this.http.put<{ success: boolean; data: Record<string, unknown> }>(
          `${environment.apiBaseUrl}/aggregation/logistics/vehicle-documents/${documentId}`,
          payload,
        ),
      );
      const d = res.data ?? (res as unknown as Record<string, unknown>);
      return {
        id: (d["id"] as string) ?? (d["_id"] as string) ?? documentId,
        vehicleId: (d["vehicleId"] as string) ?? payload.vehicleId,
        imageUris: Array.isArray(d["imageUris"])
          ? (d["imageUris"] as string[])
          : payload.imageUris,
        expiryDate: (d["expiryDate"] as string) ?? payload.expiryDate,
        category: (d["category"] as string) ?? payload.category,
        categoryName: (d["categoryName"] as string) ?? payload.categoryName,
        createdAt: (d["createdAt"] as string) ?? undefined,
        updatedAt: (d["updatedAt"] as string) ?? undefined,
      };
    } catch {
      return null;
    }
  }

  override async getVehicleDocuments(
    query: VehicleDocumentListQuery = {},
  ): Promise<PagedResult<VehicleDocumentApiRecord>> {
    const params: Record<string, string> = {
      page: String(query.page ?? 1),
      limit: String(query.limit ?? 20),
    };
    if (query.vehicleId) params["vehicleId"] = query.vehicleId;
    if (query.category) params["category"] = query.category;
    if (query.expiryFrom) params["expiryFrom"] = query.expiryFrom;
    if (query.expiryTo) params["expiryTo"] = query.expiryTo;

    try {
      const res = await firstValueFrom(
        this.http.get<{
          success: boolean;
          data: {
            items: Record<string, unknown>[];
            page: number;
            limit: number;
            total: number;
          };
        }>(
          `${environment.apiBaseUrl}/aggregation/logistics/vehicle-documents`,
          { params },
        ),
      );
      const items: VehicleDocumentApiRecord[] = (res.data?.items ?? []).map(
        (d) => ({
          id: (d["id"] as string) ?? (d["_id"] as string) ?? "",
          vehicleId: (d["vehicleId"] as string) ?? "",
          imageUris: Array.isArray(d["imageUris"])
            ? (d["imageUris"] as string[])
            : [],
          expiryDate: (d["expiryDate"] as string) ?? "",
          category: (d["category"] as string) ?? "",
          categoryName: (d["categoryName"] as string) ?? undefined,
          createdAt: (d["createdAt"] as string) ?? undefined,
          updatedAt: (d["updatedAt"] as string) ?? undefined,
        }),
      );
      return this.capPagedResult(
        {
          items,
          page: res.data.page,
          limit: res.data.limit,
          total: res.data.total,
        },
        query.page,
        query.limit,
      );
    } catch {
      return { items: [], page: 1, limit: 20, total: 0 };
    }
  }

  override async getVehicleDocumentsByVehicleId(
    vehicleId: string,
    query: { page?: number; limit?: number } = {},
  ): Promise<PagedResult<VehicleDocumentApiRecord>> {
    const params: Record<string, string> = {
      page: String(query.page ?? 1),
      limit: String(query.limit ?? 20),
    };
    try {
      const res = await firstValueFrom(
        this.http.get<{
          success: boolean;
          data: {
            items: Record<string, unknown>[];
            page: number;
            limit: number;
            total: number;
          };
        }>(
          `${environment.apiBaseUrl}/aggregation/logistics/vehicles/${vehicleId}/documents`,
          { params },
        ),
      );
      const items: VehicleDocumentApiRecord[] = (res.data?.items ?? []).map(
        (d) => ({
          id: (d["id"] as string) ?? (d["_id"] as string) ?? "",
          vehicleId: (d["vehicleId"] as string) ?? vehicleId,
          imageUris: Array.isArray(d["imageUris"])
            ? (d["imageUris"] as string[])
            : [],
          expiryDate: (d["expiryDate"] as string) ?? "",
          category: (d["category"] as string) ?? "",
          categoryName: (d["categoryName"] as string) ?? undefined,
          createdAt: (d["createdAt"] as string) ?? undefined,
          updatedAt: (d["updatedAt"] as string) ?? undefined,
        }),
      );
      return this.capPagedResult(
        {
          items,
          page: res.data.page,
          limit: res.data.limit,
          total: res.data.total,
        },
        query.page,
        query.limit,
      );
    } catch {
      return { items: [], page: 1, limit: 20, total: 0 };
    }
  }

  override async deleteVehicleDocument(documentId: string): Promise<boolean> {
    try {
      await firstValueFrom(
        this.http.delete(
          `${environment.apiBaseUrl}/aggregation/logistics/vehicle-documents/${documentId}`,
        ),
      );
      return true;
    } catch {
      return false;
    }
  }

  override async updateVehicle(
    id: string,
    payload: UpdateVehiclePayload,
  ): Promise<VehicleApiRecord | null> {
    try {
      const res = await firstValueFrom(
        this.http.put<{ success: boolean; data: Record<string, unknown> }>(
          `${environment.apiBaseUrl}/aggregation/logistics/vehicles/${id}`,
          payload,
        ),
      );
      const v = res.data;
      if (!v) return null;
      return {
        id: (v["id"] as string) ?? (v["_id"] as string) ?? id,
        registrationNumber: (v["registrationNumber"] as string) ?? "",
        make: (v["make"] as string) ?? "",
        modelName: (v["modelName"] as string) ?? "",
        vehicleName: (v["vehicleName"] as string) ?? "",
        year: Number(v["year"] ?? 0),
        type: (v["type"] as string) ?? "",
        assignedToAuthUserId: this.readEntityId(
          v["assignedToAuthUserId"] ?? v["assignedUser"],
        ),
        currentAgencyId: this.readEntityId(
          v["currentAgencyId"] ?? v["currentAgency"],
        ),
        status: (v["status"] as string) ?? "",
        fuelType: (v["fuelType"] as string) ?? "",
        isActive: Boolean(v["isActive"]),
        documents: Array.isArray(v["documents"])
          ? (v["documents"] as Array<Record<string, unknown>>).map((doc) => ({
              imageUris: Array.isArray(doc["imageUris"])
                ? (doc["imageUris"] as string[])
                : [],
              expiryDate: (doc["expiryDate"] as string) ?? "",
              category: (doc["category"] as string) ?? "",
              categoryName: (doc["categoryName"] as string) ?? "",
            }))
          : [],
        createdAt: (v["createdAt"] as string) ?? "",
        updatedAt: (v["updatedAt"] as string) ?? "",
      };
    } catch {
      return null;
    }
  }

  override deleteFuel(ids: string[]): Promise<boolean> {
    return firstValueFrom(
      this.http.delete<boolean>(
        `${environment.apiBaseUrl}/aggregation/logistics/fuel-requests/${ids}`,
      ),
    );
  }

  override deleteVehicle(ids: string[]): Promise<boolean> {
    return firstValueFrom(
      this.http.delete<boolean>(
        `${environment.apiBaseUrl}/aggregation/logistics/vehicles/${ids}`,
      ),
    );
  }
  override deleteLogisticUser(ids: string[]): Promise<boolean> {
    return firstValueFrom(
      this.http.delete<boolean>(
        `${environment.apiBaseUrl}/auth/logistics-users`,
        { body: { ids } },
      ),
    );
  }

  override deleteAdmin(ids: string[]): Promise<boolean> {
    return firstValueFrom(
      this.http.delete<boolean>(`${environment.apiBaseUrl}/auth/admins`, {
        body: { ids },
      }),
    );
  }

  override getTasks(
    query: TaskListQuery = {},
  ): Promise<PagedResult<TaskRecord>> {
    return firstValueFrom(
      this.http.get<ApiCollectionResponse<TaskRecord> | TaskRecord[]>(
        `${environment.apiBaseUrl}/aggregation/tasks${this.toQueryString(query)}`,
      ),
    ).then((response) =>
      this.capPagedResult(
        this.extractPagedCollection(response, (item) => item),
        query.page,
        query.limit,
      ),
    );
  }

  override async getTaskById(id: string): Promise<TaskRecord | null> {
    const res = await firstValueFrom(
      this.http.get<ApiEntityResponse<TaskRecord> | TaskRecord>(
        `${environment.apiBaseUrl}/aggregation/tasks/${id}`,
      ),
    );

    return this.extractEntity(res as ApiEntityResponse<TaskRecord> | TaskRecord);
  }

  override getTaskActivities(
    query: TaskActivityListQuery = {},
  ): Promise<PagedResult<TaskActivity>> {
    return firstValueFrom(
      this.http.get<ApiCollectionResponse<TaskActivity> | TaskActivity[]>(
        `${environment.apiBaseUrl}/aggregation/tasks/activities${this.toQueryString(query)}`,
      ),
    ).then((response) =>
      this.capPagedResult(
        this.extractPagedCollection(response, (item) =>
          this.normalizeTaskActivity(item),
        ),
        query.page,
        query.limit,
      ),
    );
  }

  override async createTask(payload: CreateTaskPayload): Promise<TaskRecord> {
    const res = await firstValueFrom(
      this.http.post<{ success?: boolean; data?: TaskRecord } | TaskRecord>(
        `${environment.apiBaseUrl}/aggregation/tasks`,
        {
          ...cleanPayload(payload as unknown as Record<string, unknown>),
          instructions: payload.instructions ?? "",
        },
      ),
    );
    const entity = this.extractEntity(
      res as ApiEntityResponse<TaskRecord> | TaskRecord,
    );
    if (!entity) throw new Error("Create task response missing");
    return entity;
  }

  override async updateTask(
    id: string,
    payload: UpdateTaskPayload,
  ): Promise<TaskRecord> {
    const body = {
      ...cleanPayload(payload as unknown as Record<string, unknown>),
      instructions: payload.instructions ?? "",
    };
    const res = await firstValueFrom(
      this.http.put<{ success?: boolean; data?: TaskRecord } | TaskRecord>(
        `${environment.apiBaseUrl}/aggregation/tasks/${id}`,
        body,
      ),
    );
    const entity = this.extractEntity(
      res as ApiEntityResponse<TaskRecord> | TaskRecord,
    );
    if (!entity) throw new Error("Update task response missing");
    return entity;
  }

  override deleteTask(id: string): Promise<void> {
    return firstValueFrom(
      this.http.delete<void>(
        `${environment.apiBaseUrl}/aggregation/tasks/${id}`,
      ),
    );
  }

  override async reviewAgentTask(
    taskId: string,
    payload: AgentTaskReviewPayload,
  ): Promise<AgentTaskReviewResult | null> {
    try {
      const res = await firstValueFrom(
        this.http.post<{ success: boolean; data: Record<string, unknown> }>(
          `${environment.apiBaseUrl}/aggregation/tasks/${taskId}/review`,
          payload,
        ),
      );
      const d = res?.data;
      if (!d) return null;
      const approval = d["approval"] as Record<string, unknown> | undefined;
      return {
        id: (d["id"] as string) ?? (d["_id"] as string) ?? taskId,
        status: (d["status"] as string) ?? undefined,
        approval: approval
          ? { status: (approval["status"] as string) ?? undefined }
          : undefined,
      };
    } catch {
      return null;
    }
  }

  override getTargets(query: TargetListQuery = {}): Promise<PagedResult<AgentTargetRecord>> {
    return firstValueFrom(
      this.http.get<ApiCollectionResponse<Record<string, unknown>> | Record<string, unknown>[]>(
        `${environment.apiBaseUrl}/aggregation/targets${this.toQueryString(query)}`
      )
    ).then((response) => {
      const raw = this.extractPagedCollection(response, (item) => item);
      return this.capPagedResult({
        ...raw,
        items: raw.items.map((item) => this.normalizeAgentTargetRecord(item))
      }, query.page, query.limit);
    });
  }

  override getTargetPerformanceAnalytics(query: TargetPerformanceAnalyticsQuery): Promise<AgentTargetAnalyticsResult> {
    return firstValueFrom(
      this.http.get<{ success?: boolean; data?: Record<string, unknown> } | Record<string, unknown>>(
        `${environment.apiBaseUrl}/aggregation/targets/performance-analytics${this.toQueryString(query)}`
      )
    ).then((response) => this.normalizeTargetPerformanceAnalytics(
      this.extractEntity(response as ApiEntityResponse<Record<string, unknown>> | Record<string, unknown>) ?? {},
      query
    ));
  }

  override async exportTargetPerformanceAnalyticsDocument(query: TargetPerformanceAnalyticsDocumentQuery): Promise<TargetPerformanceAnalyticsDocumentExport> {
    const response = await firstValueFrom(
      this.http.get<{ success?: boolean; data?: Partial<TargetPerformanceAnalyticsDocumentExport> }>(
        `${environment.apiBaseUrl}/aggregation/targets/performance-analytics/export-document${this.toQueryString(query)}`,
        { headers: { Accept: 'application/json' } }
      )
    );

    const isPdf = query.documentType === 'pdf';

    return {
      fileName: response.data?.fileName?.trim() || `target-performance-analytics-${query.date}.${isPdf ? 'pdf' : 'xlsx'}`,
      mimeType: response.data?.mimeType?.trim() || (isPdf ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'),
      base64: response.data?.base64?.trim() || ''
    };
  }

  override async createTarget(payload: AgentTargetPayload): Promise<AgentTargetRecord> {
    const res = await firstValueFrom(
      this.http.post<ApiEntityResponse<Record<string, unknown>> | Record<string, unknown>>(
        `${environment.apiBaseUrl}/aggregation/targets`,
        this.toTargetPayload(payload)
      )
    );
    return this.normalizeAgentTargetRecord(this.requireEntity(res));
  }

  override async updateTarget(id: string, payload: AgentTargetPayload): Promise<AgentTargetRecord> {
    const res = await firstValueFrom(
      this.http.put<ApiEntityResponse<Record<string, unknown>> | Record<string, unknown>>(
        `${environment.apiBaseUrl}/aggregation/targets/${id}`,
        this.toTargetPayload(payload)
      )
    );
    return this.normalizeAgentTargetRecord(this.requireEntity(res));
  }

  override deleteTarget(id: string): Promise<void> {
    return firstValueFrom(
      this.http.delete<void>(`${environment.apiBaseUrl}/aggregation/targets/${id}`)
    );
  }

  /**
   * API NORMALIZATION:
   * Task activity responses can mix raw ids, embedded entity objects, and sparse resource rows.
   * This mapper keeps the task drawer contract stable before the data reaches the feature layer.
   */
  private normalizeTaskActivity(activity: TaskActivity): TaskActivity {
    const row = activity as unknown as Record<string, unknown>;
    const createdBy = row["createdBy"] as Record<string, unknown> | undefined;
    const pos = row["pos"] as Record<string, unknown> | undefined;

    return {
      id: (row["id"] as string) ?? (row["_id"] as string) ?? "",
      activityType: (row["activityType"] as string) ?? "",
      note: (row["note"] as string) ?? "",
      posId: this.readEntityId(row["posId"] ?? row["pos"]) ?? undefined,
      posName: (row["posName"] as string) ?? (pos?.["name"] as string) ?? "",
      checkIn: this.normalizeTaskActivityCheckpoint(row["checkIn"]),
      checkOut: this.normalizeTaskActivityCheckpoint(row["checkOut"]),
      distributedResources: this.normalizeTaskActivityResources(
        row["distributedResources"],
      ),
      collectedResources: this.normalizeTaskActivityResources(
        row["collectedResources"],
      ),
      returnedResources: this.normalizeTaskActivityResources(
        row["returnedResources"],
      ),
      evidence: Array.isArray(row["evidence"])
        ? (row["evidence"] as unknown[])
        : [],
      createdAt: (row["createdAt"] as string) ?? "",
      createdByAuthUserId:
        this.readEntityId(row["createdByAuthUserId"] ?? row["createdBy"]) ??
        undefined,
      createdByName:
        (row["createdByName"] as string) ??
        (createdBy?.["name"] as string) ??
        "",
      taskId: this.readEntityId(row["taskId"] ?? row["task"]) ?? undefined,
      taskTitle: (row["taskTitle"] as string) ?? "",
      taskDescription: (row["taskDescription"] as string) ?? "",
      taskDueDate: (row["taskDueDate"] as string) ?? "",
      taskStatus: (row["taskStatus"] as string) ?? "",
      taskApprovalStatus: (row["taskApprovalStatus"] as string) ?? "",
      taskPriority: (row["taskPriority"] as string) ?? "",
      assigneeAuthUserId:
        this.readEntityId(row["assigneeAuthUserId"] ?? row["assignee"]) ??
        undefined,
      assigneeUserType: (row["assigneeUserType"] as string) ?? "",
      assignedAgencyId:
        this.readEntityId(row["assignedAgencyId"] ?? row["assignedAgency"]) ??
        undefined,
      assignedAgencyName: (row["assignedAgencyName"] as string) ?? "",
    };
  }

  private normalizeTaskActivityCheckpoint(
    value: unknown,
  ): TaskActivity["checkIn"] {
    if (!value || typeof value !== "object") {
      return undefined;
    }

    const row = value as Record<string, unknown>;
    return {
      point: row["point"],
      capturedAt: (row["capturedAt"] as string) ?? "",
      addressLabel: (row["addressLabel"] as string) ?? "",
    };
  }

  private normalizeTaskActivityResources(
    value: unknown,
  ): TaskActivity["distributedResources"] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value.map((resource) => {
      const row = resource as Record<string, unknown>;
      const category = row["category"] as Record<string, unknown> | undefined;
      const products = Array.isArray(row["products"]) ? row["products"] : [];

      return {
        category: {
          id:
            (category?.["id"] as string) ?? (category?.["_id"] as string) ?? "",
          name: (category?.["name"] as string) ?? "",
        },
        products: products.map((productRow) => {
          const product = productRow as Record<string, unknown>;
          const productEntity = product["product"] as
            | Record<string, unknown>
            | undefined;
          const variants = Array.isArray(product["variants"])
            ? product["variants"]
            : [];

          return {
            product: {
              id:
                (productEntity?.["id"] as string) ??
                (productEntity?.["_id"] as string) ??
                "",
              name: (productEntity?.["name"] as string) ?? "",
            },
            quantity: Number(product["quantity"] ?? 0),
            unit: (product["unit"] as string) ?? "",
            productKind: (product["productKind"] as string) ?? "",
            sellingPrice:
              product["sellingPrice"] === undefined
                ? undefined
                : Number(product["sellingPrice"] ?? 0),
            variants: variants.map((variantRow) => {
              const variant = variantRow as Record<string, unknown>;
              const variantEntity = variant["variant"] as
                | Record<string, unknown>
                | undefined;
              const productReference = String(
                variant["productReference"] ??
                  variant["productReferenceUrl"] ??
                  "",
              ).trim();

              return {
                variant: {
                  id:
                    (variantEntity?.["id"] as string) ??
                    (variantEntity?.["_id"] as string) ??
                    "",
                  name: (variantEntity?.["name"] as string) ?? "",
                },
                quantity: Number(variant["quantity"] ?? 0),
                unit: (variant["unit"] as string) ?? "",
                productKind: (variant["productKind"] as string) ?? "",
                sellingPrice:
                  variant["sellingPrice"] === undefined
                    ? undefined
                    : Number(variant["sellingPrice"] ?? 0),
                ...(productReference ? { productReference } : {}),
              };
            }),
          };
        }),
      };
    });
  }

  override deleteCategory(id: string): Promise<void> {
    return firstValueFrom(
      this.http.delete<void>(
        `${environment.apiBaseUrl}/aggregation/inventory/categories/${id}`,
      ),
    );
  }

  override async getInventoryCategories(
    search = "",
  ): Promise<InventoryCategory[]> {
    const params: Record<string, string> = {};
    if (search) params["search"] = search;
    const res = await firstValueFrom(
      this.http.get<ApiCollectionResponse<Record<string, unknown>>>(
        `${environment.apiBaseUrl}/aggregation/inventory/categories`,
        { params },
      ),
    );
    return this.extractCollectionItems(res).map((c) => ({
      id: (c["_id"] as string) ?? (c["id"] as string) ?? "",
      name: (c["name"] as string) ?? "",
      isActive: (c["status"] as string) === "active",
      createdAt: (c["createdAt"] as string) ?? "",
      updatedAt: (c["updatedAt"] as string) ?? "",
    }));
  }

  override async getInventoryCategoriesPaged(
    query: { search?: string; page?: number; limit?: number } = {},
  ): Promise<PagedResult<InventoryCategory>> {
    const params: Record<string, string> = {
      page: String(query.page ?? 1),
      limit: String(query.limit ?? 10),
    };
    if (query.search?.trim()) params["search"] = query.search.trim();
    const res = await firstValueFrom(
      this.http.get<
        | ApiCollectionResponse<Record<string, unknown>>
        | { success?: boolean; data?: Record<string, unknown>[] }
      >(`${environment.apiBaseUrl}/aggregation/inventory/categories`, {
        params,
      }),
    );
    const items = this.extractCollectionItems(
      res as ApiCollectionResponse<InventoryCategory>,
    ).map((item) => {
      const c = item as unknown as Record<string, unknown>;
      return {
        id: (c["_id"] as string) ?? (c["id"] as string) ?? "",
        name: (c["name"] as string) ?? "",
        isActive: (c["status"] as string) === "active",
        createdAt: (c["createdAt"] as string) ?? "",
        updatedAt: (c["updatedAt"] as string) ?? "",
      };
    });
    return this.toPagedCatalogResult(
      res,
      items,
      query.page ?? 1,
      query.limit ?? 10,
    );
  }

  override async getInventoryProducts(
    query: { categoryId?: string; search?: string } = {},
  ): Promise<InventoryProduct[]> {
    const params: Record<string, string> = {};
    if (query.categoryId) params["categoryId"] = query.categoryId;
    if (query.search) params["search"] = query.search;
    const res = await firstValueFrom(
      this.http.get<ApiCollectionResponse<Record<string, unknown>>>(
        `${environment.apiBaseUrl}/aggregation/inventory/products`,
        { params },
      ),
    );
    return this.extractCollectionItems(res)
      .map((p) => ({
        id: (p["id"] as string) ?? (p["_id"] as string) ?? "",
        categoryId: (p["categoryId"] as string) ?? "",
        name: (p["name"] as string) ?? "",
        threshold: Number(p["threshold"] ?? 0),
        isCashProduct: (p["productKind"] as string) === "cash",
        requiresVariant: Boolean(p["requiresVariant"]),
        isActive: (p["status"] as string) === "active",
        createdAt: (p["createdAt"] as string) ?? "",
        updatedAt: (p["updatedAt"] as string) ?? "",
      }))
      .filter((product) => !product.isCashProduct);
  }

  override async getInventoryProductsPaged(
    query: {
      categoryId?: string;
      search?: string;
      page?: number;
      limit?: number;
    } = {},
  ): Promise<PagedResult<InventoryProduct>> {
    const params: Record<string, string> = {
      page: String(query.page ?? 1),
      limit: String(query.limit ?? 10),
    };
    if (query.categoryId) params["categoryId"] = query.categoryId;
    if (query.search?.trim()) params["search"] = query.search.trim();
    const res = await firstValueFrom(
      this.http.get<
        | ApiCollectionResponse<Record<string, unknown>>
        | { success?: boolean; data?: Record<string, unknown>[] }
      >(`${environment.apiBaseUrl}/aggregation/inventory/products`, { params }),
    );
    const items = this.extractCollectionItems(
      res as ApiCollectionResponse<InventoryProduct>,
    ).map((item) => {
      const p = item as unknown as Record<string, unknown>;
      return {
        id: (p["id"] as string) ?? (p["_id"] as string) ?? "",
        categoryId: (p["categoryId"] as string) ?? "",
        name: (p["name"] as string) ?? "",
        threshold: Number(p["threshold"] ?? 0),
        isCashProduct: (p["productKind"] as string) === "cash",
        requiresVariant: Boolean(p["requiresVariant"]),
        isActive: (p["status"] as string) === "active",
        createdAt: (p["createdAt"] as string) ?? "",
        updatedAt: (p["updatedAt"] as string) ?? "",
      };
    });
    return this.toPagedCatalogResult(
      res,
      items,
      query.page ?? 1,
      query.limit ?? 10,
    );
  }

  override async getInventoryVariants(
    query: { productId?: string; categoryId?: string; search?: string } = {},
  ): Promise<InventoryVariant[]> {
    const params: Record<string, string> = {};
    if (query.productId) params["productId"] = query.productId;
    if (query.categoryId) params["categoryId"] = query.categoryId;
    if (query.search) params["search"] = query.search;
    const res = await firstValueFrom(
      this.http.get<{ success?: boolean; data?: Record<string, unknown>[] }>(
        `${environment.apiBaseUrl}/aggregation/inventory/variants`,
        { params },
      ),
    );
    return (res?.data ?? []).map((v) => ({
      id: (v["id"] as string) ?? (v["_id"] as string) ?? "",
      productId: (v["productId"] as string) ?? "",
      name: (v["name"] as string) ?? "",
      sellingPrice:
        v["sellingPrice"] != null ? Number(v["sellingPrice"]) : undefined,
      isActive: (v["status"] as string) === "active",
      createdAt: (v["createdAt"] as string) ?? "",
      updatedAt: (v["updatedAt"] as string) ?? "",
    }));
  }

  override async getInventoryVariantsPaged(
    query: {
      productId?: string;
      categoryId?: string;
      search?: string;
      page?: number;
      limit?: number;
    } = {},
  ): Promise<PagedResult<InventoryVariant>> {
    const params: Record<string, string> = {
      page: String(query.page ?? 1),
      limit: String(query.limit ?? 10),
    };
    if (query.productId) params["productId"] = query.productId;
    if (query.categoryId) params["categoryId"] = query.categoryId;
    if (query.search?.trim()) params["search"] = query.search.trim();
    const res = await firstValueFrom(
      this.http.get<
        | ApiCollectionResponse<Record<string, unknown>>
        | { success?: boolean; data?: Record<string, unknown>[] }
      >(`${environment.apiBaseUrl}/aggregation/inventory/variants`, { params }),
    );
    const items = this.extractCollectionItems(
      res as ApiCollectionResponse<InventoryVariant>,
    ).map((item) => {
      const v = item as unknown as Record<string, unknown>;
      return {
        id: (v["id"] as string) ?? (v["_id"] as string) ?? "",
        productId: (v["productId"] as string) ?? "",
        name: (v["name"] as string) ?? "",
        sellingPrice:
          v["sellingPrice"] != null ? Number(v["sellingPrice"]) : undefined,
        isActive: (v["status"] as string) === "active",
        createdAt: (v["createdAt"] as string) ?? "",
        updatedAt: (v["updatedAt"] as string) ?? "",
      };
    });
    return this.toPagedCatalogResult(
      res,
      items,
      query.page ?? 1,
      query.limit ?? 10,
    );
  }

  override async exportInventoryCatalogExcel(): Promise<InventoryCatalogExcelExport> {
    const response = await firstValueFrom(
      this.http.get<{
        success?: boolean;
        data?: Partial<InventoryCatalogExcelExport>;
      }>(
        `${environment.apiBaseUrl}/aggregation/inventory/catalog/export-excel`,
        { headers: { Accept: "application/json" } },
      ),
    );

    return {
      fileName: response.data?.fileName?.trim() || "inventory-catalog.xlsx",
      mimeType:
        response.data?.mimeType?.trim() ||
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      base64: response.data?.base64?.trim() || "",
    };
  }

  override async getDefaultCentralReference(): Promise<CentralReference> {
    const res = await firstValueFrom(
      this.http.get<{ success?: boolean; data?: Record<string, unknown> }>(
        `${environment.apiBaseUrl}/aggregation/inventory/central/reference`,
      ),
    );

    const data = res?.data ?? {};
    return {
      id: (data["id"] as string) ?? (data["_id"] as string) ?? "",
      name: (data["name"] as string) ?? "",
    };
  }

  override async getCashBalances(query: CashBalanceListQuery = {}): Promise<PagedResult<CashBalance>> {
    const params = this.cleanQueryParams(query);
    const response = await firstValueFrom(
      this.http.get<ApiCollectionResponse<Record<string, unknown>> | Record<string, unknown>[]>(
        `${environment.apiBaseUrl}/aggregation/cash/balances`,
        { params },
      ),
    );

    return this.capPagedResult(
      this.extractPagedCollection(response, (item) => this.normalizeCashBalance(item)),
      query.page,
      query.limit,
    );
  }

  override async getCashBalance(ownerType: CashOwnerType, ownerId: string): Promise<CashBalance | null> {
    const response = await firstValueFrom(
      this.http.get<ApiEntityResponse<Record<string, unknown>> | Record<string, unknown>>(
        `${environment.apiBaseUrl}/aggregation/cash/balances/${ownerType}/${ownerId}`,
      ),
    );
    const entity = this.extractEntity(response);
    return entity ? this.normalizeCashBalance(entity) : null;
  }

  override async getCashMovements(query: CashMovementListQuery = {}): Promise<PagedResult<CashMovement>> {
    const params = this.cleanQueryParams(query);
    const response = await firstValueFrom(
      this.http.get<ApiCollectionResponse<Record<string, unknown>> | Record<string, unknown>[]>(
        `${environment.apiBaseUrl}/aggregation/cash/movements`,
        { params },
      ),
    );

    return this.capPagedResult(
      this.extractPagedCollection(response, (item) => this.normalizeCashMovement(item)),
      query.page,
      query.limit,
    );
  }

  override async createCashMovement(payload: CashMovementPayload): Promise<CashMovement> {
    const requestBody = cleanPayload(payload as unknown as Record<string, unknown>);
    if (Object.prototype.hasOwnProperty.call(payload, "note") && payload.note === null) {
      requestBody["note"] = null;
    }

    const response = await firstValueFrom(
      this.http.post<ApiEntityResponse<Record<string, unknown>> | Record<string, unknown>>(
        `${environment.apiBaseUrl}/aggregation/cash/movements`,
        requestBody,
      ),
    );

    return this.normalizeCashMovement(this.requireEntity(response));
  }

  override async getAgentAssignedStock(
    agentId: string,
  ): Promise<AgentInventoryStockCategoryGroup[]> {
    const res = await firstValueFrom(
      this.http.get<{ success?: boolean; data?: Record<string, unknown>[] | { stock?: Record<string, unknown>[]; cashBalance?: unknown } }>(
        `${environment.apiBaseUrl}/aggregation/inventory/agents/${agentId}/stock`,
      ),
    );

    const stockRows = Array.isArray(res?.data)
      ? res.data
      : Array.isArray(res?.data?.stock)
        ? res.data.stock
        : [];

    return stockRows.map((categoryGroup) => ({
      category: this.normalizeNestedEntity(categoryGroup["category"]),
      products: this.toArray(categoryGroup["products"]).map((productGroup) => ({
        product: {
          ...this.normalizeNestedEntity(productGroup["product"]),
          threshold: Number(
            (productGroup["product"] as Record<string, unknown> | undefined)?.[
              "threshold"
            ] ?? 0,
          ),
        },
        variants: this.toArray(productGroup["variants"]).map((variantRow) => ({
          id: String(variantRow["id"] ?? ""),
          quantity: Number(variantRow["quantity"] ?? 0),
          unit: String(variantRow["unit"] ?? ""),
          isLow: Boolean(variantRow["isLow"]),
          costPrice:
            variantRow["costPrice"] != null
              ? Number(variantRow["costPrice"])
              : undefined,
          updatedAt: String(variantRow["updatedAt"] ?? ""),
          variant: this.normalizeNestedEntity(variantRow["variant"]),
        })),
      })),
    }));
  }

  override async getAgentStockTransactions(
    agentId: string,
  ): Promise<AgentInventoryTransactionRecord[]> {
    const res = await firstValueFrom(
      this.http.get<{ success?: boolean; data?: Record<string, unknown>[] }>(
        `${environment.apiBaseUrl}/aggregation/inventory/agents/${agentId}/transactions`,
      ),
    );

    return (res?.data ?? []).map((record) => {
      const agency = this.normalizeNestedEntity(record["agencyId"]);

      return {
        id: String(record["id"] ?? record["_id"] ?? ""),
        agencyId: agency.id || String(record["agencyId"] ?? ""),
        agencyName: agency.name || String(record["agencyName"] ?? ""),
        agentAuthUserId: String(record["agentAuthUserId"] ?? ""),
        posId: record["posId"] ? String(record["posId"]) : null,
        taskId: record["taskId"] ? String(record["taskId"]) : null,
        transactionType: String(record["transactionType"] ?? ""),
        items: this.toArray(record["items"]).map((categoryGroup) => ({
          category: this.normalizeNestedEntity(categoryGroup["category"]),
          products: this.toArray(categoryGroup["products"]).map(
            (productGroup) => ({
              product: this.normalizeNestedEntity(productGroup["product"]),
              variants: this.toArray(productGroup["variants"]).map(
                (variantRow) => ({
                  variant: this.normalizeNestedEntity(variantRow["variant"]),
                  unit: String(variantRow["unit"] ?? ""),
                  quantity: Number(variantRow["quantity"] ?? 0),
                  costPrice:
                    variantRow["costPrice"] != null
                      ? Number(variantRow["costPrice"])
                      : undefined,
                }),
              ),
            }),
          ),
        })),
        performedByAuthUserId: String(record["performedByAuthUserId"] ?? ""),
        performedByName: String(record["performedByName"] ?? ""),
        status: String(record["status"] ?? ""),
        note: String(record["note"] ?? ""),
        createdAt: String(record["createdAt"] ?? ""),
        updatedAt: String(record["updatedAt"] ?? ""),
      };
    });
  }

  override async addAgentStockTransaction(
    payload: AgentInventoryTransactionPayload,
  ): Promise<boolean> {
    await firstValueFrom(
      this.http.post(
        `${environment.apiBaseUrl}/aggregation/inventory/agent-transactions`,
        cleanPayload(payload as unknown as Record<string, unknown>),
      ),
    );

    return true;
  }

  override async updateAgentStockVariant(
    agencyId: string,
    agentAuthUserId: string,
    variantId: string,
    payload: UpdateAgentStockVariantPayload,
  ): Promise<boolean> {
    await firstValueFrom(
      this.http.put(
        `${environment.apiBaseUrl}/aggregation/inventory/agencies/${agencyId}/agents/${agentAuthUserId}/stock/variants/${variantId}`,
        cleanPayload(payload as unknown as Record<string, unknown>),
      ),
    );

    return true;
  }

  override async deleteAgentStockCategory(
    agentId: string,
    categoryId: string,
  ): Promise<boolean> {
    await firstValueFrom(
      this.http.delete(
        `${environment.apiBaseUrl}/aggregation/inventory/agents/${agentId}/stock/categories/${categoryId}`,
      ),
    );

    return true;
  }

  override async getAgencyAssignedStock(
    agencyId: string,
  ): Promise<AgencyInventoryStockCategoryGroup[]> {
    const res = await firstValueFrom(
      this.http.get<
        | ApiCollectionResponse<Record<string, unknown>>
        | Record<string, unknown>[]
      >(
        `${environment.apiBaseUrl}/aggregation/inventory/agencies/${agencyId}/stock`,
      ),
    );

    return this.extractCollectionItems(res).map((categoryGroup) => ({
      category: this.normalizeNestedEntity(categoryGroup["category"]),
      products: this.toArray(categoryGroup["products"]).map((productGroup) => ({
        product: {
          ...this.normalizeNestedEntity(productGroup["product"]),
          threshold: Number(
            (productGroup["product"] as Record<string, unknown> | undefined)?.[
              "threshold"
            ] ?? 0,
          ),
        },
        totalQuantity: Number(productGroup["totalQuantity"] ?? 0),
        status: String(productGroup["status"] ?? ""),
        isLow: Boolean(productGroup["isLow"]),
        variants: this.toArray(productGroup["variants"]).map((variantRow) => ({
          id: String(variantRow["id"] ?? ""),
          quantity: Number(variantRow["quantity"] ?? 0),
          unit: String(variantRow["unit"] ?? ""),
          variant: {
            ...this.normalizeNestedEntity(variantRow["variant"]),
            unit: String(
              (variantRow["variant"] as Record<string, unknown> | undefined)?.[
                "unit"
              ] ??
                variantRow["unit"] ??
                "",
            ),
          },
        })),
      })),
    }));
  }

  override async getAgencyStockTransactions(
    agencyId: string,
  ): Promise<AgencyInventoryTransactionRecord[]> {
    const res = await firstValueFrom(
      this.http.get<{ success?: boolean; data?: Record<string, unknown>[] }>(
        `${environment.apiBaseUrl}/aggregation/inventory/agencies/${agencyId}/transactions`,
      ),
    );

    return (res?.data ?? []).map((record) => ({
      id: String(record["id"] ?? record["_id"] ?? ""),
      agencyId: String(record["agencyId"] ?? ""),
      fromAgencyId:
        record["fromAgencyId"] != null ? String(record["fromAgencyId"]) : null,
      variantId: String(record["variantId"] ?? ""),
      productId: String(record["productId"] ?? ""),
      categoryId: String(record["categoryId"] ?? ""),
      variantName: String(record["variantName"] ?? ""),
      productName: String(record["productName"] ?? ""),
      categoryName: String(record["categoryName"] ?? ""),
      unit: String(record["unit"] ?? ""),
      transactionType: String(record["transactionType"] ?? ""),
      quantity: Number(record["quantity"] ?? 0),
      performedByAuthUserId: String(record["performedByAuthUserId"] ?? ""),
      performedByName: String(record["performedByName"] ?? ""),
      logisticsTaskId:
        record["logisticsTaskId"] != null
          ? String(record["logisticsTaskId"])
          : null,
      status: String(record["status"] ?? ""),
      note: String(record["note"] ?? ""),
      date: record["date"] != null ? String(record["date"]) : null,
      createdAt: String(record["createdAt"] ?? ""),
      updatedAt: String(record["updatedAt"] ?? ""),
    }));
  }

  override async getAgencyDirectSalesLogs(
    query: AgencyDirectSalesLogQuery,
  ): Promise<PagedResult<AgencyDirectSalesLogRecord>> {
    const params: Record<string, string> = {
      page: String(query.page ?? 1),
      limit: String(query.limit ?? 10),
    };

    if (query.agencyId) params["agencyId"] = query.agencyId;
    if (query.agentAuthUserId) params["agentAuthUserId"] = query.agentAuthUserId;
    if (query.fromDate) params["fromDate"] = query.fromDate;
    if (query.toDate) params["toDate"] = query.toDate;

    const res = await firstValueFrom(
      this.http.get<
        | { success?: boolean; data?: { items?: Record<string, unknown>[] } | Record<string, unknown>[] }
        | Record<string, unknown>[]
      >(
        `${environment.apiBaseUrl}/aggregation/inventory/direct-sales`,
        { params },
      ),
    );

    const items = this.extractCollectionItems(res).map((record): AgencyDirectSalesLogRecord => {
      const agency = this.normalizeNestedEntity(record["agencyId"]);

      return {
      id: String(record["id"] ?? record["_id"] ?? ""),
      agencyId: agency.id || String(record["agencyId"] ?? ""),
      agentAuthUserId: String(record["agentAuthUserId"] ?? ""),
      posId: record["posId"] != null ? String(record["posId"]) : null,
      customerName: String(record["customerName"] ?? ""),
      customerMobileNumber: String(record["customerMobileNumber"] ?? ""),
      items: this.toArray(record["items"]).map((categoryGroup) => ({
        category: this.normalizeNestedEntity(categoryGroup["category"]),
        products: this.toArray(categoryGroup["products"]).map((productGroup) => ({
          product: this.normalizeNestedEntity(productGroup["product"]),
          variants: this.toArray(productGroup["variants"]).map((variantRow) => {
            const productReference = String(
              variantRow["productReference"] ??
                variantRow["productReferenceUrl"] ??
                "",
            ).trim();

            return {
              variant: this.normalizeNestedEntity(variantRow["variant"]),
              productKind: String(variantRow["productKind"] ?? ""),
              unit: String(variantRow["unit"] ?? ""),
              quantity: Number(variantRow["quantity"] ?? 0),
              sellingPrice: Number(variantRow["sellingPrice"] ?? 0),
              lineTotalAmount: Number(variantRow["lineTotalAmount"] ?? 0),
              ...(productReference ? { productReference } : {}),
            };
          }),
        })),
      })),
      totalAmount: Number(record["totalAmount"] ?? 0),
      note: String(record["note"] ?? ""),
      performedByAuthUserId: String(record["performedByAuthUserId"] ?? ""),
      performedByName: String(record["performedByName"] ?? ""),
      createdAt: String(record["createdAt"] ?? ""),
      updatedAt: String(record["updatedAt"] ?? ""),
      };
    });

    const data = !Array.isArray(res) && res?.data && !Array.isArray(res.data) ? res.data as Record<string, unknown> : null;
    const result: PagedResult<AgencyDirectSalesLogRecord> = {
      items,
      total: data ? Number(data["total"] ?? items.length) : items.length,
      page: data ? Number(data["page"] ?? query.page ?? 1) : (query.page ?? 1),
      limit: data ? Number(data["limit"] ?? query.limit ?? items.length) : (query.limit ?? items.length),
    };

    return this.capPagedResult(result, query.page, query.limit);
  }

  override async getCentralStockOverview(filters?: {
    categoryId?: string;
    productId?: string;
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<CentralStockOverviewData | null> {
    const params: Record<string, string> = {};
    if (filters?.categoryId) params["categoryId"] = filters.categoryId;
    if (filters?.productId) params["productId"] = filters.productId;
    if (filters?.search) params["search"] = filters.search;
    if (filters?.page) params["page"] = String(filters.page);
    if (filters?.limit) params["limit"] = String(filters.limit);
    const res = await firstValueFrom(
      this.http.get<
        | { success?: boolean; data?: Record<string, unknown> }
        | Record<string, unknown>
      >(
        `${environment.apiBaseUrl}/aggregation/inventory/central/stock-overview`,
        { params },
      ),
    );
    const raw = this.extractEntity(
      res as
        | { success?: boolean; data?: Record<string, unknown> }
        | Record<string, unknown>,
    );
    if (!raw) {
      return null;
    }

    const central =
      (raw["central"] as Record<string, unknown> | undefined) ?? {};
    const responseFilters =
      (raw["filters"] as Record<string, unknown> | undefined) ?? {};
    const cards = (raw["cards"] as Record<string, unknown> | undefined) ?? {};
    const rows = Array.isArray(raw["rows"])
      ? (raw["rows"] as Array<Record<string, unknown>>)
      : [];

    const page = Number(raw["page"] ?? filters?.page ?? 1);
    const limit = Number(raw["limit"] ?? filters?.limit ?? rows.length);
    const total = Number(raw["total"] ?? raw["totalCount"] ?? rows.length);
    const pagedRows =
      rows.length > limit ? rows.slice((page - 1) * limit, page * limit) : rows;

    return {
      central: {
        id: String(central["id"] ?? central["_id"] ?? ""),
        name: String(central["name"] ?? ""),
      },
      filters: {
        categoryId: this.toNullableString(responseFilters["categoryId"]),
        productId: this.toNullableString(responseFilters["productId"]),
      },
      cards: {
        totalStock: Number(cards["totalStock"] ?? 0),
        pending: Number(cards["pending"] ?? 0),
        inTransit: Number(cards["inTransit"] ?? 0),
      },
      page,
      limit,
      total,
      rows: pagedRows.map((row) => {
        const category =
          (row["category"] as Record<string, unknown> | undefined) ?? {};
        const product =
          (row["product"] as Record<string, unknown> | undefined) ?? {};
        const variant =
          (row["variant"] as Record<string, unknown> | undefined) ?? {};
        return {
          stockId: String(row["stockId"] ?? ""),
          category: {
            id: String(category["id"] ?? category["_id"] ?? ""),
            name: String(category["name"] ?? ""),
          },
          product: {
            id: String(product["id"] ?? product["_id"] ?? ""),
            name: String(product["name"] ?? ""),
            threshold: Number(product["threshold"] ?? 0),
          },
          variant: {
            id: String(variant["id"] ?? variant["_id"] ?? ""),
            name: String(variant["name"] ?? ""),
            unit: String(variant["unit"] ?? ""),
          },
          unit: String(row["unit"] ?? variant["unit"] ?? ""),
          onHandQty: Number(row["onHandQty"] ?? 0),
          pendingQty: Number(row["pendingQty"] ?? 0),
          inTransitQty: Number(row["inTransitQty"] ?? 0),
          totalQty: Number(row["totalQty"] ?? 0),
          note: this.readStockNote(row),
          productReference: this.readProductReferenceUrl(row),
          isLow: Boolean(row["isLow"]),
          lastUpdatedAt: String(row["lastUpdatedAt"] ?? ""),
        };
      }),
    };
  }

  override async getCentralStockList(
    centralId = "",
    query: { search?: string; page?: number; limit?: number } = {},
  ): Promise<PagedResult<CentralStockListRow>> {
    const params: Record<string, string> = {};
    if (query.search) params["search"] = query.search;
    params["page"] = String(query.page ?? 1);
    params["limit"] = String(query.limit ?? 10);
    const res = await firstValueFrom(
      this.http.get<
        | {
            success?: boolean;
            data?: Record<string, unknown> | Record<string, unknown>[];
          }
        | Record<string, unknown>
        | Record<string, unknown>[]
      >(
        `${environment.apiBaseUrl}/aggregation/inventory/centrals/${centralId}/stock`,
        { params },
      ),
    );

    const rawEntity = this.extractEntity(
      res as
        | {
            success?: boolean;
            data?: Record<string, unknown> | Record<string, unknown>[];
          }
        | Record<string, unknown>
        | Record<string, unknown>[],
    );
    const rows = Array.isArray(rawEntity)
      ? rawEntity
      : Array.isArray((rawEntity as Record<string, unknown> | null)?.["rows"])
        ? ((rawEntity as Record<string, unknown>)["rows"] as Array<
            Record<string, unknown>
          >)
        : [];

    let items: CentralStockListRow[];
    if (
      rows.some((row) =>
        Array.isArray(row["products"] as Record<string, unknown>[] | undefined),
      )
    ) {
      items = rows.flatMap((categoryGroup) => {
        const category = this.normalizeNestedEntity(categoryGroup["category"]);

        return this.toArray(categoryGroup["products"]).flatMap(
          (productGroup) => {
            const product =
              (productGroup["product"] as
                | Record<string, unknown>
                | undefined) ?? {};
            const totalQuantity = Number(productGroup["totalQuantity"] ?? 0);
            const status = String(productGroup["status"] ?? "");
            const isLow = Boolean(productGroup["isLow"]);
            const lastUpdatedAt = String(productGroup["lastUpdatedAt"] ?? "");
            const threshold = Number(product["threshold"] ?? 0);

            return this.toArray(productGroup["variants"]).map((variantRow) => {
              const variant =
                (variantRow["variant"] as
                  | Record<string, unknown>
                  | undefined) ?? {};
              const createdByName =
                variantRow["createdByName"] != null
                  ? String(variantRow["createdByName"])
                  : undefined;
              return {
                id: String(variantRow["id"] ?? ""),
                category,
                product: {
                  id: String(product["id"] ?? product["_id"] ?? ""),
                  name: String(product["name"] ?? ""),
                  threshold,
                },
                variant: {
                  id: String(variant["id"] ?? variant["_id"] ?? ""),
                  name: String(variant["name"] ?? ""),
                  unit:
                    variant["unit"] != null
                      ? String(variant["unit"])
                      : undefined,
                },
                unit: String(
                  variantRow["unit"] ??
                    productGroup["unit"] ??
                    variant["unit"] ??
                    "",
                ),
                quantity: Number(variantRow["quantity"] ?? 0),
                totalQuantity,
                status,
                isLow,
                date: String(variantRow["date"] ?? ""),
                updatedAt: String(variantRow["updatedAt"] ?? lastUpdatedAt),
                note: this.readStockNote(
                  variantRow,
                  productGroup,
                  categoryGroup,
                ),
                productReference: this.readProductReferenceUrl(
                  variantRow,
                  productGroup,
                  categoryGroup,
                ),
                enteredBy: createdByName ?? "-",
                createdByName,
              };
            });
          },
        );
      });
    } else {
      items = rows.map((row) => {
        const category =
          (row["category"] as Record<string, unknown> | undefined) ?? {};
        const product =
          (row["product"] as Record<string, unknown> | undefined) ?? {};
        const variant =
          (row["variant"] as Record<string, unknown> | undefined) ?? {};
        return {
          id: String(row["id"] ?? row["stockId"] ?? ""),
          category: {
            id: String(category["id"] ?? category["_id"] ?? ""),
            name: String(category["name"] ?? ""),
          },
          product: {
            id: String(product["id"] ?? product["_id"] ?? ""),
            name: String(product["name"] ?? ""),
            threshold:
              product["threshold"] != null
                ? Number(product["threshold"])
                : undefined,
          },
          variant: {
            id: String(variant["id"] ?? variant["_id"] ?? ""),
            name: String(variant["name"] ?? ""),
            unit: variant["unit"] != null ? String(variant["unit"]) : undefined,
          },
          unit: String(row["unit"] ?? variant["unit"] ?? ""),
          quantity: Number(
            row["quantity"] ?? row["totalQty"] ?? row["onHandQty"] ?? 0,
          ),
          totalQuantity:
            row["totalQuantity"] != null
              ? Number(row["totalQuantity"])
              : undefined,
          status: row["status"] != null ? String(row["status"]) : undefined,
          isLow: Boolean(row["isLow"]),
          date: String(row["date"] ?? ""),
          updatedAt: String(row["updatedAt"] ?? row["lastUpdatedAt"] ?? ""),
          note: this.readStockNote(row),
          productReference: this.readProductReferenceUrl(row),
          enteredBy: String(
            row["enteredBy"] ??
              row["createdByName"] ??
              row["addedBy"] ??
              row["performedByName"] ??
              "-",
          ),
        };
      });
    }

    const entity = rawEntity as Record<string, unknown> | null;
    const page = Number(entity?.["page"] ?? query.page ?? 1);
    const limit = Number(entity?.["limit"] ?? query.limit ?? 10);
    const pagedItems =
      items.length > limit
        ? items.slice((page - 1) * limit, page * limit)
        : items;

    return {
      items: pagedItems,
      page,
      limit,
      total: Number(
        entity?.["total"] ?? entity?.["totalCount"] ?? items.length,
      ),
    };
  }

  /**
   * BUSINESS LOGIC: readStockNote
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private readStockNote(...sources: Array<Record<string, unknown>>): string {
    for (const source of sources) {
      const value = source["note"] ?? source["notes"] ?? source["description"];
      if (typeof value === "string" && value.trim()) {
        return value.trim();
      }
    }

    return "";
  }

  private readProductReferenceUrl(
    ...sources: Array<Record<string, unknown>>
  ): string {
    for (const source of sources) {
      const value =
        source["productReference"] ??
        source["productReferenceUrl"] ??
        source["product_reference"] ??
        source["productReferenceS3Url"] ??
        source["productReferenceS3URL"] ??
        source["Product Reference"];

      if (typeof value === "string" && value.trim()) {
        return value.trim();
      }
    }

    return "";
  }

  private normalizeUploadedFileResult(
    response: Record<string, unknown> | null | undefined,
    fallbackFileName: string,
  ): UploadedFileResult | null {
    const entity = this.extractEntity(response ?? {}) ?? {};
    const url = this.readFirstString(entity, [
      "originalImage",
      "url",
      "fileUrl",
      "s3Url",
      "s3URL",
      "location",
      "Location",
      "path",
    ]);

    if (!url) {
      return null;
    }

    return {
      url,
      originalImage: url,
      fileName:
        this.readFirstString(entity, [
          "fileName",
          "filename",
          "name",
          "originalName",
        ]) || fallbackFileName,
    };
  }

  private readFirstString(
    source: Record<string, unknown>,
    keys: string[],
  ): string {
    for (const key of keys) {
      const value = source[key];
      if (typeof value === "string" && value.trim()) {
        return value.trim();
      }
    }

    return "";
  }

  override async getCentralTransfers(query?: {
    status?: "pending" | "in-transit" | "delivered";
    page?: number;
    limit?: number;
    search?: string;
  }): Promise<PagedResult<CentralTransferListRow>> {
    const params: Record<string, string> = {};
    if (query?.status) params["status"] = query.status;
    if (query?.page) params["page"] = String(query.page);
    if (query?.limit) params["limit"] = String(query.limit);
    if (query?.search) params["search"] = query.search;

    const res = await firstValueFrom(
      this.http.get<
        | {
            success?: boolean;
            data?: Record<string, unknown> | Record<string, unknown>[];
          }
        | Record<string, unknown>
        | Record<string, unknown>[]
      >(`${environment.apiBaseUrl}/aggregation/inventory/central/transfers`, {
        params,
      }),
    );

    const rawEntity = this.extractEntity(
      res as
        | {
            success?: boolean;
            data?: Record<string, unknown> | Record<string, unknown>[];
          }
        | Record<string, unknown>
        | Record<string, unknown>[],
    );
    const rows = Array.isArray(rawEntity)
      ? rawEntity
      : Array.isArray((rawEntity as Record<string, unknown> | null)?.["items"])
        ? ((rawEntity as Record<string, unknown>)["items"] as Array<
            Record<string, unknown>
          >)
        : Array.isArray((rawEntity as Record<string, unknown> | null)?.["rows"])
          ? ((rawEntity as Record<string, unknown>)["rows"] as Array<
              Record<string, unknown>
            >)
          : [];

    const items = rows.map((row) => {
      const source =
        (row["source"] as Record<string, unknown> | undefined) ?? {};
      const destination =
        (row["destination"] as Record<string, unknown> | undefined) ?? {};
      const category =
        (row["category"] as Record<string, unknown> | undefined) ?? {};
      const product =
        (row["product"] as Record<string, unknown> | undefined) ?? {};
      const variant =
        (row["variant"] as Record<string, unknown> | undefined) ?? {};
      const initiatedBy =
        (row["initiatedBy"] as Record<string, unknown> | undefined) ?? {};
      const logisticsUser =
        (row["logisticsUser"] as Record<string, unknown> | undefined) ?? {};
      const transferCode = String(
        row["transferCode"] ??
          row["code"] ??
          row["transferNo"] ??
          row["id"] ??
          "",
      );
      const status = String(row["status"] ?? "");

      return {
        id: String(row["id"] ?? row["_id"] ?? transferCode),
        transferCode,
        type: String(row["type"] ?? row["transferType"] ?? "Central Transfer"),
        source: String(
          source["name"] ??
            row["sourceName"] ??
            row["source"] ??
            "Central Inventory",
        ),
        destination: String(
          destination["name"] ??
            row["destinationName"] ??
            row["destination"] ??
            "-",
        ),
        initiatedBy: String(
          initiatedBy["name"] ??
            row["initiatedByName"] ??
            row["createdByName"] ??
            "-",
        ),
        logisticsUser: String(
          logisticsUser["name"] ??
            row["logisticsUserName"] ??
            row["assignedToName"] ??
            "-",
        ),
        requestDate: String(
          row["requestDate"] ?? row["createdAt"] ?? row["date"] ?? "",
        ),
        category: String(
          category["name"] ?? row["categoryName"] ?? row["category"] ?? "-",
        ),
        product: String(
          product["name"] ?? row["productName"] ?? row["product"] ?? "-",
        ),
        variant: String(
          variant["name"] ?? row["variantName"] ?? row["variant"] ?? "-",
        ),
        reservedQty: Number(
          row["reservedQty"] ?? row["quantity"] ?? row["qty"] ?? 0,
        ),
        status,
        deliveryStatus: String(
          row["deliveryStatus"] ?? this.humanizeTransferStatus(status),
        ),
        approvals: String(row["approvals"] ?? row["approvalStatus"] ?? "-"),
        adminOnlyFlow: String(
          row["adminOnlyFlow"] ?? row["isAdminOnly"] ?? "No",
        ),
        lifecycle: String(
          row["lifecycle"] ??
            row["note"] ??
            "Stock movement between central inventory and agencies.",
        ),
        note: String(
          row["note"] ??
            row["notes"] ??
            row["description"] ??
            row["comment"] ??
            "-",
        ),
        createdAt: String(
          row["createdAt"] ?? row["requestDate"] ?? row["date"] ?? "-",
        ),
      };
    });

    const entity = rawEntity as Record<string, unknown> | null;
    const page = Number(entity?.["page"] ?? query?.page ?? 1);
    const limit = Number(entity?.["limit"] ?? query?.limit ?? 10);
    const pagedItems =
      items.length > limit
        ? items.slice((page - 1) * limit, page * limit)
        : items;

    return {
      items: pagedItems,
      page,
      limit,
      total: Number(
        entity?.["total"] ?? entity?.["totalCount"] ?? items.length,
      ),
    };
  }

  override async getCentralBankingNotes(query?: {
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<PagedResult<CentralBankingNoteRow>> {
    const params: Record<string, string> = {
      page: String(query?.page ?? 1),
      limit: String(query?.limit ?? 20),
    };
    if (query?.search?.trim()) {
      params["search"] = query.search.trim();
    }
    const res = await firstValueFrom(
      this.http.get<
        | {
            success?: boolean;
            data?: Record<string, unknown> | Record<string, unknown>[];
          }
        | Record<string, unknown>
        | Record<string, unknown>[]
      >(
        `${environment.apiBaseUrl}/aggregation/inventory/central/banking-notes`,
        { params },
      ),
    );
    const rawEntity = this.extractEntity(
      res as
        | {
            success?: boolean;
            data?: Record<string, unknown> | Record<string, unknown>[];
          }
        | Record<string, unknown>
        | Record<string, unknown>[],
    );
    const rows = Array.isArray(rawEntity)
      ? rawEntity
      : Array.isArray((rawEntity as Record<string, unknown> | null)?.["items"])
        ? ((rawEntity as Record<string, unknown>)["items"] as Array<
            Record<string, unknown>
          >)
        : Array.isArray((rawEntity as Record<string, unknown> | null)?.["rows"])
          ? ((rawEntity as Record<string, unknown>)["rows"] as Array<
              Record<string, unknown>
            >)
          : [];

    const items = rows.map((row) => this.normalizeCentralBankingNote(row));
    const entity = rawEntity as Record<string, unknown> | null;
    const page = Number(entity?.["page"] ?? query?.page ?? 1);
    const limit = Number(entity?.["limit"] ?? query?.limit ?? 20);
    const pagedItems =
      items.length > limit
        ? items.slice((page - 1) * limit, page * limit)
        : items;

    return {
      items: pagedItems,
      page,
      limit,
      total: Number(
        entity?.["total"] ?? entity?.["totalCount"] ?? items.length,
      ),
    };
  }

  override async createCentralBankingNote(
    payload: CentralBankingNotePayload,
  ): Promise<CentralBankingNoteRow | null> {
    const res = await firstValueFrom(
      this.http.post<
        | { success?: boolean; data?: Record<string, unknown> }
        | Record<string, unknown>
      >(
        `${environment.apiBaseUrl}/aggregation/inventory/central/banking-notes`,
        payload,
      ),
    );
    const raw = this.extractEntity(
      res as
        | { success?: boolean; data?: Record<string, unknown> }
        | Record<string, unknown>,
    );
    return raw ? this.normalizeCentralBankingNote(raw) : null;
  }

  override async updateCentralBankingNote(
    id: string,
    payload: CentralBankingNotePayload,
  ): Promise<CentralBankingNoteRow | null> {
    const res = await firstValueFrom(
      this.http.put<
        | { success?: boolean; data?: Record<string, unknown> }
        | Record<string, unknown>
      >(
        `${environment.apiBaseUrl}/aggregation/inventory/central/banking-notes/${id}`,
        payload,
      ),
    );
    const raw = this.extractEntity(
      res as
        | { success?: boolean; data?: Record<string, unknown> }
        | Record<string, unknown>,
    );
    return raw ? this.normalizeCentralBankingNote(raw) : null;
  }

  override async deleteCentralBankingNote(id: string): Promise<boolean> {
    await firstValueFrom(
      this.http.delete<void>(
        `${environment.apiBaseUrl}/aggregation/inventory/central/banking-notes/${id}`,
      ),
    );
    return true;
  }

  override async getAgencyReferences(
    search = "",
  ): Promise<AgencyReferenceOption[]> {
    const params: Record<string, string> = {};
    if (search.trim()) {
      params["search"] = search.trim();
    }

    const res = await firstValueFrom(
      this.http.get<
        | {
            success?: boolean;
            data?: Record<string, unknown> | Record<string, unknown>[];
          }
        | Record<string, unknown>
        | Record<string, unknown>[]
      >(`${environment.apiBaseUrl}/aggregation/agencies/reference`, { params }),
    );

    const rawEntity = this.extractEntity(
      res as
        | {
            success?: boolean;
            data?: Record<string, unknown> | Record<string, unknown>[];
          }
        | Record<string, unknown>
        | Record<string, unknown>[],
    );
    const rows = Array.isArray(rawEntity)
      ? rawEntity
      : Array.isArray((rawEntity as Record<string, unknown> | null)?.["items"])
        ? ((rawEntity as Record<string, unknown>)["items"] as Array<
            Record<string, unknown>
          >)
        : Array.isArray((rawEntity as Record<string, unknown> | null)?.["rows"])
          ? ((rawEntity as Record<string, unknown>)["rows"] as Array<
              Record<string, unknown>
            >)
          : [];

    return rows
      .map((row) => ({
        id: String(row["id"] ?? row["_id"] ?? row["referenceId"] ?? ""),
        name: String(row["name"] ?? row["agencyName"] ?? row["label"] ?? ""),
      }))
      .filter((row) => row.id && row.name);
  }

  override async getStockThresholds(
    query?: StockThresholdQuery,
  ): Promise<PagedResult<StockThresholdRow>> {
    const params: Record<string, string> = {};
    if (query?.institutionType?.trim())
      params["institutionType"] = query.institutionType.trim();
    if (query?.referenceId?.trim())
      params["referenceId"] = query.referenceId.trim();
    if (query?.categoryId?.trim())
      params["categoryId"] = query.categoryId.trim();
    if (query?.productId?.trim()) params["productId"] = query.productId.trim();
    if (query?.search?.trim()) params["search"] = query.search.trim();
    params["page"] = String(query?.page ?? 1);
    params["limit"] = String(query?.limit ?? 10);

    const res = await firstValueFrom(
      this.http.get<
        | {
            success?: boolean;
            data?: Record<string, unknown> | Record<string, unknown>[];
          }
        | Record<string, unknown>
        | Record<string, unknown>[]
      >(`${environment.apiBaseUrl}/aggregation/inventory/stock-thresholds`, {
        params,
      }),
    );

    const rawEntity = this.extractEntity(
      res as
        | {
            success?: boolean;
            data?: Record<string, unknown> | Record<string, unknown>[];
          }
        | Record<string, unknown>
        | Record<string, unknown>[],
    );
    const rows = Array.isArray(rawEntity)
      ? rawEntity
      : Array.isArray((rawEntity as Record<string, unknown> | null)?.["items"])
        ? ((rawEntity as Record<string, unknown>)["items"] as Array<
            Record<string, unknown>
          >)
        : Array.isArray((rawEntity as Record<string, unknown> | null)?.["rows"])
          ? ((rawEntity as Record<string, unknown>)["rows"] as Array<
              Record<string, unknown>
            >)
          : [];

    const items = rows.map((row) => this.normalizeStockThresholdRow(row));
    const entity = rawEntity as Record<string, unknown> | null;
    const requestedPage = query?.page ?? 1;
    const requestedLimit = query?.limit ?? 10;
    const hasPagingMetadata =
      !!entity &&
      (entity["total"] !== undefined ||
        entity["totalCount"] !== undefined ||
        entity["page"] !== undefined ||
        entity["limit"] !== undefined);
    const pagedItems = hasPagingMetadata
      ? items
      : items.slice(
          (requestedPage - 1) * requestedLimit,
          requestedPage * requestedLimit,
        );

    return {
      items: pagedItems,
      page: Number(entity?.["page"] ?? requestedPage),
      limit: Number(entity?.["limit"] ?? requestedLimit),
      total: Number(
        entity?.["total"] ?? entity?.["totalCount"] ?? items.length,
      ),
    };
  }

  override async createStockThreshold(
    payload: CreateStockThresholdPayload,
  ): Promise<StockThresholdRow | null> {
    const res = await firstValueFrom(
      this.http.post<
        | { success?: boolean; data?: Record<string, unknown> }
        | Record<string, unknown>
      >(
        `${environment.apiBaseUrl}/aggregation/inventory/stock-thresholds`,
        payload,
      ),
    );

    const raw = this.extractEntity(
      res as
        | { success?: boolean; data?: Record<string, unknown> }
        | Record<string, unknown>,
    );
    return raw ? this.normalizeStockThresholdRow(raw) : null;
  }

  override async updateStockThreshold(
    id: string,
    payload: CreateStockThresholdPayload,
  ): Promise<StockThresholdRow | null> {
    const res = await firstValueFrom(
      this.http.put<
        | { success?: boolean; data?: Record<string, unknown> }
        | Record<string, unknown>
      >(
        `${environment.apiBaseUrl}/aggregation/inventory/stock-thresholds/${id}`,
        payload,
      ),
    );

    const raw = this.extractEntity(
      res as
        | { success?: boolean; data?: Record<string, unknown> }
        | Record<string, unknown>,
    );
    return raw ? this.normalizeStockThresholdRow(raw) : null;
  }

  override async deleteStockThreshold(id: string): Promise<boolean> {
    await firstValueFrom(
      this.http.delete<void>(
        `${environment.apiBaseUrl}/aggregation/inventory/stock-thresholds/${id}`,
      ),
    );
    return true;
  }

  override async uploadFile(file: File, _context = "common"): Promise<UploadedFileResult> {
    const formData = new FormData();
    formData.append("file", file);

    const response = await firstValueFrom(
      this.http.post<Record<string, unknown>>(
        `${environment.apiBaseUrl}/upload`,
        formData,
      ),
    );

    const normalized = this.normalizeUploadedFileResult(response, file.name);
    if (!normalized) {
      throw new Error("Upload response missing file URL");
    }

    return normalized;
  }

  override async addCentralStock(
    payload: CentralAddStockPayload,
  ): Promise<boolean> {
    await firstValueFrom(
      this.http.post(
        `${environment.apiBaseUrl}/aggregation/inventory/central/add-stock`,
        payload,
      ),
    );

    return true;
  }

  override async addAgencyStockTransaction(
    payload: AgencyInventoryTransactionPayload,
  ): Promise<boolean> {
    await firstValueFrom(
      this.http.post(
        `${environment.apiBaseUrl}/aggregation/inventory/agency-transactions`,
        cleanPayload(payload as unknown as Record<string, unknown>),
      ),
    );

    return true;
  }

  override async updateAgencyStockVariant(
    agencyId: string,
    variantId: string,
    payload: UpdateAgencyStockVariantPayload,
  ): Promise<boolean> {
    await firstValueFrom(
      this.http.put(
        `${environment.apiBaseUrl}/aggregation/inventory/agencies/${agencyId}/stock/variants/${variantId}`,
        cleanPayload(payload as unknown as Record<string, unknown>),
      ),
    );

    return true;
  }

  override async deleteAgencyStockCategory(
    agencyId: string,
    categoryId: string,
  ): Promise<boolean> {
    await firstValueFrom(
      this.http.delete(
        `${environment.apiBaseUrl}/aggregation/inventory/agencies/${agencyId}/stock/categories/${categoryId}`,
      ),
    );

    return true;
  }

  override async createCategory(
    payload: CreateCategoryPayload,
  ): Promise<InventoryCategory> {
    const res = await firstValueFrom(
      this.http.post<
        | { success?: boolean; data?: Record<string, unknown> }
        | Record<string, unknown>
      >(`${environment.apiBaseUrl}/aggregation/inventory/categories`, {
        name: payload.name,
        status: "active",
      }),
    );
    const raw = this.extractEntity(
      res as
        | ApiEntityResponse<Record<string, unknown>>
        | Record<string, unknown>,
    );
    if (!raw) throw new Error("Create category response missing");
    return {
      id: (raw["_id"] as string) ?? (raw["id"] as string) ?? "",
      name: (raw["name"] as string) ?? "",
      isActive: (raw["status"] as string) === "active",
    };
  }

  override async updateCategory(
    id: string,
    payload: CreateCategoryPayload,
  ): Promise<InventoryCategory> {
    const res = await firstValueFrom(
      this.http.put<
        | { success?: boolean; data?: Record<string, unknown> }
        | Record<string, unknown>
      >(`${environment.apiBaseUrl}/aggregation/inventory/categories/${id}`, {
        name: payload.name,
        status: payload.status,
      }),
    );
    const raw = this.extractEntity(
      res as
        | ApiEntityResponse<Record<string, unknown>>
        | Record<string, unknown>,
    );
    if (!raw) throw new Error("Update category response missing");
    return {
      id: (raw["_id"] as string) ?? (raw["id"] as string) ?? "",
      name: (raw["name"] as string) ?? "",
      isActive: (raw["status"] as string) === "active",
    };
  }

  override async createProduct(
    payload: CreateProductPayload,
  ): Promise<InventoryProduct> {
    const res = await firstValueFrom(
      this.http.post<
        | { success?: boolean; data?: Record<string, unknown> }
        | Record<string, unknown>
      >(`${environment.apiBaseUrl}/aggregation/inventory/products`, {
        categoryId: payload.categoryId,
        name: payload.name,
        threshold: payload.threshold,
        productKind: payload.productKind,
        requiresVariant: payload.requiresVariant,
        requiresSellingPrice: payload.requiresSellingPrice,
        status: "active",
      }),
    );
    const raw = this.extractEntity(
      res as
        | ApiEntityResponse<Record<string, unknown>>
        | Record<string, unknown>,
    );
    if (!raw) throw new Error("Create product response missing");
    return this.normalizeProductRecord(raw);
  }

  override async updateProduct(
    id: string,
    payload: CreateProductPayload,
  ): Promise<InventoryProduct> {
    const res = await firstValueFrom(
      this.http.put<
        | { success?: boolean; data?: Record<string, unknown> }
        | Record<string, unknown>
      >(`${environment.apiBaseUrl}/aggregation/inventory/products/${id}`, {
        categoryId: payload.categoryId,
        name: payload.name,
        threshold: payload.threshold,
        productKind: payload.productKind,
        requiresVariant: payload.requiresVariant,
        requiresSellingPrice: payload.requiresSellingPrice,
        status: payload.status,
      }),
    );
    const raw = this.extractEntity(
      res as
        | ApiEntityResponse<Record<string, unknown>>
        | Record<string, unknown>,
    );
    if (!raw) throw new Error("Update product response missing");
    return this.normalizeProductRecord(raw);
  }

  override deleteProduct(id: string): Promise<void> {
    return firstValueFrom(
      this.http.delete<void>(
        `${environment.apiBaseUrl}/aggregation/inventory/products/${id}`,
      ),
    );
  }

  override async createVariant(
    payload: CreateVariantPayload,
  ): Promise<InventoryVariant> {
    const res = await firstValueFrom(
      this.http.post<
        | { success?: boolean; data?: Record<string, unknown> }
        | Record<string, unknown>
      >(`${environment.apiBaseUrl}/aggregation/inventory/variants`, {
        productId: payload.productId,
        name: payload.name,
        unit: payload.unit,
        sellingPrice: payload.sellingPrice,
        status: "active",
      }),
    );
    const raw = this.extractEntity(
      res as
        | ApiEntityResponse<Record<string, unknown>>
        | Record<string, unknown>,
    );
    if (!raw) throw new Error("Create variant response missing");
    return this.normalizeVariantRecord(raw);
  }

  override async updateVariant(
    id: string,
    payload: CreateVariantPayload,
  ): Promise<InventoryVariant> {
    const res = await firstValueFrom(
      this.http.put<
        | { success?: boolean; data?: Record<string, unknown> }
        | Record<string, unknown>
      >(`${environment.apiBaseUrl}/aggregation/inventory/variants/${id}`, {
        productId: payload.productId,
        name: payload.name,
        unit: payload.unit,
        sellingPrice: payload.sellingPrice,
        status: payload.status,
      }),
    );
    const raw = this.extractEntity(
      res as
        | ApiEntityResponse<Record<string, unknown>>
        | Record<string, unknown>,
    );
    if (!raw) throw new Error("Update variant response missing");
    return this.normalizeVariantRecord(raw);
  }

  override deleteVariant(id: string): Promise<void> {
    return firstValueFrom(
      this.http.delete<void>(
        `${environment.apiBaseUrl}/aggregation/inventory/variants/${id}`,
      ),
    );
  }

  override async getBtlUsers(
    query: {
      page?: number;
      limit?: number;
      search?: string;
      accountStatus?: string;
      approvalStatus?: string;
      agency?: string;
      agencyId?: string;
    } = {},
  ): Promise<PagedResult<BtlUserApiRecord>> {
    const params: Record<string, string> = {};
    if (query.page !== undefined) params["page"] = String(query.page);
    if (query.limit !== undefined) params["limit"] = String(query.limit);
    if (query.search) params["search"] = query.search;
    if (query.accountStatus) params["accountStatus"] = query.accountStatus;
    if (query.approvalStatus) params["approvalStatus"] = query.approvalStatus;
    if (query.agency) params["agency"] = query.agency;
    if (query.agencyId) params["agencyId"] = query.agencyId;

    const res = await firstValueFrom(
      this.http.get<{
        success: boolean;
        data: {
          items: Record<string, unknown>[];
          page: number;
          limit: number;
          total: number;
        };
      }>(`${environment.apiBaseUrl}/auth/btl-users`, { params }),
    );

    const items: BtlUserApiRecord[] = (res.data?.items ?? []).map((u) => {
      const contact = (u["contact"] as Record<string, unknown>) ?? {};
      const phone = (contact["phone"] as Record<string, unknown>) ?? {};
      const assignedAgency = this.readAssignedAgencyFromUser(u, "btl");
      const assignedSupervisor =
        (u["assignedSupervisor"] as Record<string, unknown>) ?? null;
      const btlProfile = (u["btlProfile"] as Record<string, unknown>) ?? {};
      const campaignSummary =
        (u["campaignSummary"] as Record<string, unknown>) ?? {};

      return {
        id: (u["id"] as string) ?? (u["_id"] as string) ?? "",
        fullName:
          (u["fullName"] as string) ||
          (u["name"] as string) ||
          `${u["firstName"] ?? ""} ${u["lastName"] ?? ""}`.trim(),
        firstName: (u["firstName"] as string) ?? "",
        lastName: (u["lastName"] as string) ?? "",
        email: (u["email"] as string) ?? "",
        accountStatus: (u["accountStatus"] as string) ?? "active",
        contactPhoneDisplay: (u["contactPhoneDisplay"] as string) ?? "",
        contact: {
          phone: {
            dialCode: Number(phone["dialCode"] ?? 0),
            iso2: (phone["iso2"] as string) ?? "",
            country: (phone["country"] as string) ?? "",
            number: (phone["number"] as string) ?? "",
          },
        },
        assignedAgency: assignedAgency
          ? {
              id: assignedAgency.id,
              name: assignedAgency.name,
              city: assignedAgency.city ?? "",
              area: assignedAgency.area ?? "",
            }
          : null,
        assignedSupervisor: assignedSupervisor
          ? {
              id: (assignedSupervisor["id"] as string) ?? "",
              fullName: (assignedSupervisor["fullName"] as string) ?? "",
              email: (assignedSupervisor["email"] as string) ?? "",
            }
          : null,
        btlProfile: {
          employeeCode: (btlProfile["employeeCode"] as string) ?? "",
          designation: (btlProfile["designation"] as string) ?? "",
          joinedAt: (btlProfile["joinedAt"] as string) ?? "",
          lastActivityDate: (btlProfile["lastActivityDate"] as string) ?? "",
        },
        campaignSummary: {
          totalCampaigns: Number(campaignSummary["totalCampaigns"] ?? 0),
          upcomingCampaigns: Number(campaignSummary["upcomingCampaigns"] ?? 0),
          activeCampaigns: Number(campaignSummary["activeCampaigns"] ?? 0),
          completedCampaigns: Number(
            campaignSummary["completedCampaigns"] ?? 0,
          ),
          totalEntries: Number(campaignSummary["totalEntries"] ?? 0),
        },
        createdAt: (u["createdAt"] as string) ?? "",
        updatedAt: (u["updatedAt"] as string) ?? "",
      };
    });

    return this.capPagedResult(
      {
        items,
        page: res.data?.page ?? query.page ?? 1,
        limit: res.data?.limit ?? query.limit ?? items.length,
        total: res.data?.total ?? items.length,
      },
      query.page,
      query.limit,
    );
  }

  override async getBtlUserById(id: string): Promise<BtlUserApiRecord | null> {
    try {
      const res = await firstValueFrom(
        this.http.get<{ success: boolean; data: Record<string, unknown> }>(
          `${environment.apiBaseUrl}/auth/btl-users/${id}`,
        ),
      );
      const u = res.data;
      if (!u) return null;
      const contact = (u["contact"] as Record<string, unknown>) ?? {};
      const phone = (contact["phone"] as Record<string, unknown>) ?? {};
      const assignedAgency = this.readAssignedAgencyFromUser(u, "btl");
      const assignedSupervisor =
        (u["assignedSupervisor"] as Record<string, unknown>) ?? null;
      const btlProfile = (u["btlProfile"] as Record<string, unknown>) ?? {};
      const campaignSummary =
        (u["campaignSummary"] as Record<string, unknown>) ?? {};
      const rawAddress = (u["address"] as Record<string, unknown>) ?? {};
      const geoObj = (rawAddress["geo"] as Record<string, unknown>) ?? {};
      const coords = (geoObj["coordinates"] as [number, number]) ?? [0, 0];
      return {
        id: (u["id"] as string) ?? (u["_id"] as string) ?? "",
        fullName:
          (u["fullName"] as string) ||
          `${u["firstName"] ?? ""} ${u["lastName"] ?? ""}`.trim(),
        firstName: (u["firstName"] as string) ?? "",
        lastName: (u["lastName"] as string) ?? "",
        email: (u["email"] as string) ?? "",
        accountStatus: (u["accountStatus"] as string) ?? "active",
        contactPhoneDisplay: (u["contactPhoneDisplay"] as string) ?? "",
        contact: {
          phone: {
            dialCode: Number(phone["dialCode"] ?? 0),
            iso2: (phone["iso2"] as string) ?? "",
            country: (phone["country"] as string) ?? "",
            number: (phone["number"] as string) ?? "",
          },
        },
        assignedAgency: assignedAgency
          ? {
              id: assignedAgency.id,
              name: assignedAgency.name,
              city: assignedAgency.city ?? "",
              area: assignedAgency.area ?? "",
            }
          : null,
        assignedSupervisor: assignedSupervisor
          ? {
              id: (assignedSupervisor["id"] as string) ?? "",
              fullName: (assignedSupervisor["fullName"] as string) ?? "",
              email: (assignedSupervisor["email"] as string) ?? "",
            }
          : null,
        btlProfile: {
          employeeCode: (btlProfile["employeeCode"] as string) ?? "",
          designation: (btlProfile["designation"] as string) ?? "",
          joinedAt: (btlProfile["joinedAt"] as string) ?? "",
          lastActivityDate: (btlProfile["lastActivityDate"] as string) ?? "",
        },
        campaignSummary: {
          totalCampaigns: Number(campaignSummary["totalCampaigns"] ?? 0),
          upcomingCampaigns: Number(campaignSummary["upcomingCampaigns"] ?? 0),
          activeCampaigns: Number(campaignSummary["activeCampaigns"] ?? 0),
          completedCampaigns: Number(
            campaignSummary["completedCampaigns"] ?? 0,
          ),
          totalEntries: Number(campaignSummary["totalEntries"] ?? 0),
        },
        address: {
          formattedAddress: (rawAddress["formattedAddress"] as string) ?? "",
          streetAddress: (rawAddress["streetAddress"] as string) ?? "",
          locality: (rawAddress["locality"] as string) ?? "",
          subLocality: (rawAddress["subLocality"] as string) ?? "",
          region: (rawAddress["region"] as string) ?? "",
          country: (rawAddress["country"] as string) ?? "",
          postalCode: (rawAddress["postalCode"] as string) ?? "",
          area: (rawAddress["area"] as string) ?? "",
          geo: { coordinates: coords },
        },
        createdAt: (u["createdAt"] as string) ?? "",
        updatedAt: (u["updatedAt"] as string) ?? "",
      };
    } catch {
      return null;
    }
  }

  override async getBtlCampaignById(
    id: string,
  ): Promise<BtlCampaignApiRecord | null> {
    try {
      const res = await firstValueFrom(
        this.http.get<
          | { success?: boolean; data?: Record<string, unknown> }
          | Record<string, unknown>
        >(`${environment.apiBaseUrl}/aggregation/btl/campaigns/${id}`),
      );
      const raw = this.extractEntity(
        res as
          | ApiEntityResponse<Record<string, unknown>>
          | Record<string, unknown>,
      );
      return raw ? this.normalizeBtlCampaignRecord(raw) : null;
    } catch {
      return null;
    }
  }

  override async getBtlCampaigns(
    query: BtlCampaignListQuery = {},
  ): Promise<PagedResult<BtlCampaignListItem>> {
    const params: Record<string, string> = {
      page: String(query.page ?? 1),
      limit: String(query.limit ?? 20),
    };

    if (query.search) {
      params["search"] = query.search;
    }
    if (query.campaignType) {
      params["campaignType"] = query.campaignType;
    }
    if (query.status) {
      params["status"] = query.status;
    }
    if (query.assignedUser) {
      params["assignedUser"] = query.assignedUser;
    }

    const res = await firstValueFrom(
      this.http.get<{
        success: boolean;
        data: {
          items: Record<string, unknown>[];
          page: number;
          limit: number;
          total: number;
        };
      }>(`${environment.apiBaseUrl}/aggregation/btl/campaigns`, { params }),
    );

    const items: BtlCampaignListItem[] = (res.data?.items ?? []).map(
      (item) => ({
        id: (item["id"] as string) ?? (item["_id"] as string) ?? "",
        campaignName: (item["campaignName"] as string) ?? "",
        campaignType: (item["campaignType"] as string) ?? "",
        location: (item["location"] as string) ?? "",
        startDate: (item["startDate"] as string) ?? "",
        endDate: (item["endDate"] as string) ?? "",
        totalTasks: Number(item["totalTasks"] ?? 0),
        progressPercent:
          item["progressPercent"] === null ||
          item["progressPercent"] === undefined
            ? null
            : Number(item["progressPercent"]),
        status: (item["status"] as string) ?? "",
        targetStockToSell: Number(item["targetStockToSell"] ?? 0),
        targetPeopleToOnboard: Number(item["targetPeopleToOnboard"] ?? 0),
        stockSold: Number(item["stockSold"] ?? 0),
        peopleOnboarded: Number(item["peopleOnboarded"] ?? 0),
        assignedUsersCount: Number(
          item["assignedUsersCount"] ??
            (Array.isArray(item["assignedUsers"])
              ? item["assignedUsers"].length
              : 0),
        ),
        assignedUserNames: this.normalizeBtlCampaignAssignedUserNames(
          item["assignedUsers"],
        ),
        submissions: this.normalizeBtlCampaignSubmissions(item["submissions"]),
      }),
    );

    return this.capPagedResult(
      {
        items,
        page: Number(res.data?.page ?? 1),
        limit: Number(res.data?.limit ?? items.length),
        total: Number(res.data?.total ?? items.length),
      },
      query.page,
      query.limit,
    );
  }

  /**
   * BUSINESS LOGIC: normalizeBtlCampaignAssignedUserNames
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private normalizeBtlCampaignAssignedUserNames(value: unknown): string[] {
    if (!Array.isArray(value)) return [];
    return value
      .map((user) => {
        if (typeof user === "string") return user.trim();
        if (typeof user !== "object" || user === null) return "";
        const row = user as Record<string, unknown>;
        return String(
          row["fullName"] ?? row["name"] ?? row["btlUserName"] ?? "",
        ).trim();
      })
      .filter(Boolean);
  }

  private normalizeBtlCampaignSubmissions(
    value: unknown,
  ): BtlCampaignListItem["submissions"] {
    if (!Array.isArray(value)) return [];
    return value
      .filter(
        (submission): submission is Record<string, unknown> =>
          typeof submission === "object" && submission !== null,
      )
      .map((submission) => ({
        id: String(submission["id"] ?? submission["_id"] ?? ""),
        btlUserName: String(submission["btlUserName"] ?? ""),
        date: String(submission["date"] ?? ""),
        taskCompleted: String(
          submission["taskCompleted"] ??
            submission["taskName"] ??
            "",
        ),
        status: String(submission["status"] ?? ""),
        unitsDistributed: Number(submission["unitsDistributed"] ?? 0),
        consumerInteractions: Number(submission["consumerInteractions"] ?? 0),
        notes: String(submission["notes"] ?? ""),
        itemsSummary: this.normalizeBtlCampaignSubmissionItems(
          submission["items"],
        ),
        evidencePhotos: Array.isArray(submission["evidencePhotos"])
          ? (submission["evidencePhotos"] as unknown[])
              .map((photo) => String(photo ?? "").trim())
              .filter(Boolean)
          : [],
      }));
  }

  /**
   * BUSINESS LOGIC: normalizeBtlCampaignSubmissionItems
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private normalizeBtlCampaignSubmissionItems(value: unknown): string {
    if (!Array.isArray(value)) return "";
    return value
      .filter(
        (category): category is Record<string, unknown> =>
          typeof category === "object" && category !== null,
      )
      .flatMap((category) => {
        const products = Array.isArray(category["products"])
          ? category["products"]
          : [];
        return products
          .filter(
            (product): product is Record<string, unknown> =>
              typeof product === "object" && product !== null,
          )
          .flatMap((product) => {
            const productName = this.readEntityName(product["product"]);
            const variants = Array.isArray(product["variants"])
              ? product["variants"]
              : [];
            return variants
              .filter(
                (variant): variant is Record<string, unknown> =>
                  typeof variant === "object" && variant !== null,
              )
              .map((variant) => {
                const variantName = this.readEntityName(variant["variant"]);
                const quantity = Number(variant["quantity"] ?? 0);
                const unit = String(variant["unit"] ?? "").trim();
                const productReference = String(
                  variant["productReference"] ??
                    variant["productReferenceUrl"] ??
                    "",
                ).trim();
                return (
                  [productName, variantName, productReference].filter(Boolean).join(" / ") +
                  ` (${quantity}${unit ? ` ${unit}` : ""})`
                );
              });
          });
      })
      .filter(Boolean)
      .join("\n");
  }

  /**
   * BUSINESS LOGIC: readEntityName
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private readEntityName(value: unknown): string {
    if (typeof value === "string") return value.trim();
    if (typeof value !== "object" || value === null) return "";
    const row = value as Record<string, unknown>;
    return String(row["name"] ?? row["fullName"] ?? row["label"] ?? "").trim();
  }

  override async getBtlDashboardSummary(
    agencyId?: string,
  ): Promise<BtlDashboardSummary> {
    const params: Record<string, string> = {};
    if (agencyId) params["agencyId"] = agencyId;
    const res = await firstValueFrom(
      this.http.get<{ success: boolean; data: BtlDashboardSummary }>(
        `${environment.apiBaseUrl}/aggregation/btl/dashboard`,
        { params },
      ),
    );
    return res.data;
  }

  override async getLogisticsDashboardSummary(
    agencyId?: string,
  ): Promise<LogisticsDashboardSummary> {
    const params: Record<string, string> = {};
    if (agencyId) params["agencyId"] = agencyId;
    const res = await firstValueFrom(
      this.http.get<{ success: boolean; data: Record<string, unknown> }>(
        `${environment.apiBaseUrl}/aggregation/logistics/dashboard`,
        { params },
      ),
    );
    const d = res.data ?? {};
    /**
     * BUSINESS LOGIC: node
     * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
     * Output: Returns the documented value or updates component/service state without changing the public contract.
     * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
     */
    const node = (key: string) => (d[key] as Record<string, unknown>) ?? {};
    /**
     * BUSINESS LOGIC: sub
     * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
     * Output: Returns the documented value or updates component/service state without changing the public contract.
     * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
     */
    const sub = (parent: Record<string, unknown>, key: string) =>
      (parent[key] as Record<string, unknown>) ?? {};
    const tasks = node("logisticsTasks");
    const fuel = node("fuelManagement");
    const vehicles = node("vehicleManagement");
    const maintenance = node("vehicleMaintenance");
    return {
      totalLogisticsUsers: {
        value: Number(node("totalLogisticsUsers")["value"] ?? 0),
        secondaryValue: Number(node("totalLogisticsUsers")["activeUsers"] ?? 0),
      },
      activeUsers: {
        value: Number(node("totalLogisticsUsers")["activeUsers"] ?? 0),
        secondaryValue: 0,
      },
      usersOnRoute: {
        value: Number(node("usersOnRoute")["value"] ?? 0),
        secondaryValue: Number(node("usersOnRoute")["liveTasks"] ?? 0),
      },
      pendingApprovals: {
        value: Number(node("pendingReviews")["value"] ?? 0),
        secondaryValue:
          Number(node("pendingReviews")["fuelRequests"] ?? 0) +
          Number(node("pendingReviews")["maintenanceRequests"] ?? 0),
      },
      assignedVehicles: {
        value: Number(node("assignedVehicles")["value"] ?? 0),
        secondaryValue: Number(node("assignedVehicles")["totalVehicles"] ?? 0),
      },
      logisticsTasks: {
        totalTasks: Number(sub(tasks, "totalTasks")["value"] ?? 0),
        openTasks: Number(sub(tasks, "openTasks")["value"] ?? 0),
        pendingApproval: Number(sub(tasks, "pendingApproval")["value"] ?? 0),
        closedApprovalPending: Number(
          sub(tasks, "closedApprovalPending")["value"] ?? 0,
        ),
      },
      fuelManagement: {
        fuelEntries: Number(sub(fuel, "fuelEntries")["value"] ?? 0),
        pendingApproval: Number(
          sub(fuel, "fuelEntries")["pendingApproval"] ?? 0,
        ),
        approvedEntries: Number(sub(fuel, "approvedEntries")["value"] ?? 0),
        fuelCardUsage: Number(sub(fuel, "fuelCardUsage")["value"] ?? 0),
        linkedTaskEntries: Number(sub(fuel, "linkedTaskEntries")["value"] ?? 0),
      },
      vehicleManagement: {
        allVehicles: Number(sub(vehicles, "allVehicles")["value"] ?? 0),
        activeVehicles: Number(sub(vehicles, "activeVehicles")["value"] ?? 0),
        assignedVehicles: Number(
          sub(vehicles, "assignedVehicles")["value"] ?? 0,
        ),
        vehiclesWithDocuments: Number(
          sub(vehicles, "vehiclesWithDocuments")["value"] ?? 0,
        ),
        vehiclesWithoutDocuments: Number(
          sub(vehicles, "vehiclesWithoutDocuments")["value"] ?? 0,
        ),
      },
      vehicleMaintenance: {
        maintenanceRequests: Number(
          sub(maintenance, "maintenanceRequests")["value"] ?? 0,
        ),
        pendingReview: Number(
          sub(maintenance, "maintenanceRequests")["pendingReview"] ?? 0,
        ),
        highPriority: Number(sub(maintenance, "highPriority")["value"] ?? 0),
        completedRepairs: Number(
          sub(maintenance, "completedRepairs")["value"] ?? 0,
        ),
        vehicleLinkedRequests: Number(
          sub(maintenance, "vehicleLinkedRequests")["value"] ?? 0,
        ),
        distinctVehicles: Number(
          sub(maintenance, "vehicleLinkedRequests")["distinctVehicles"] ?? 0,
        ),
      },
    };
  }

  override async getLogisticsTasks(
    query: LogisticsTaskListQuery = {},
  ): Promise<PagedResult<LogisticsTaskListItem>> {
    const params: Record<string, string> = {
      page: String(query.page ?? 1),
      limit: String(query.limit ?? 20),
    };
    const trimmedSearch = query.search?.trim();
    if (trimmedSearch) {
      params["search"] = trimmedSearch;
    }
    if (query.status) {
      params["status"] = query.status;
    }
    if (query.movementType) {
      params["movementType"] = query.movementType;
    }

    const res = await firstValueFrom(
      this.http.get<{
        success: boolean;
        data: {
          items: Record<string, unknown>[];
          total: number;
          page: number;
          limit: number;
        };
      }>(`${environment.apiBaseUrl}/aggregation/logistics/tasks`, { params }),
    );

    const data = res.data ?? {
      items: [],
      total: 0,
      page: Number(params["page"]),
      limit: Number(params["limit"]),
    };
    return this.capPagedResult(
      {
        items: (data.items ?? []).map((item) => ({
          id: String(item["id"] ?? item["_id"] ?? ""),
          title: String(item["title"] ?? ""),
          assignedToAuthUserId: item["assignedToAuthUserId"]
            ? String(item["assignedToAuthUserId"])
            : undefined,
          assignedToName: item["assignedToName"]
            ? String(item["assignedToName"])
            : undefined,
          vehicleId: item["vehicleId"] ? String(item["vehicleId"]) : undefined,
          vehicleName: item["vehicleName"]
            ? String(item["vehicleName"])
            : undefined,
          createdByAuthUserId: item["createdByAuthUserId"]
            ? String(item["createdByAuthUserId"])
            : undefined,
          createdByName: item["createdByName"]
            ? String(item["createdByName"])
            : undefined,
          status: item["status"] ? String(item["status"]) : undefined,
          approval:
            typeof item["approval"] === "object" && item["approval"] !== null
              ? {
                  status: (item["approval"] as Record<string, unknown>)[
                    "status"
                  ]
                    ? String(
                        (item["approval"] as Record<string, unknown>)["status"],
                      )
                    : undefined,
                }
              : undefined,
          parallelLegs:
            typeof item["parallelLegs"] === "boolean"
              ? item["parallelLegs"]
              : undefined,
          scheduledDate: item["scheduledDate"]
            ? String(item["scheduledDate"])
            : undefined,
          note: item["note"] ? String(item["note"]) : undefined,
          legCount: Number(item["legCount"] ?? 0),
          completedLegCount: Number(item["completedLegCount"] ?? 0),
          lastActivityAt: item["lastActivityAt"]
            ? String(item["lastActivityAt"])
            : undefined,
          createdAt: item["createdAt"] ? String(item["createdAt"]) : undefined,
          updatedAt: item["updatedAt"] ? String(item["updatedAt"]) : undefined,
        })),
        total: Number(data.total ?? 0),
        page: Number(data.page ?? params["page"]),
        limit: Number(data.limit ?? params["limit"]),
      },
      query.page,
      query.limit,
    );
  }

  override async getLogisticsTaskById(
    id: string,
  ): Promise<LogisticsTaskDetailApiRecord | null> {
    const res = await firstValueFrom(
      this.http.get<{
        success: boolean;
        data?: Record<string, unknown> | null;
      }>(`${environment.apiBaseUrl}/aggregation/logistics/tasks/${id}`),
    );

    const rawData = this.extractEntity(
      res as
        | ApiEntityResponse<Record<string, unknown>>
        | Record<string, unknown>,
    );
    const item =
      rawData && typeof rawData === "object"
        ? ((rawData["task"] as Record<string, unknown> | undefined) ?? rawData)
        : null;
    if (!item) {
      return null;
    }

    const legs = Array.isArray(item["legs"])
      ? item["legs"]
      : Array.isArray(item["taskLegs"])
        ? item["taskLegs"]
        : Array.isArray(item["routeLegs"])
          ? item["routeLegs"]
          : Array.isArray(item["routes"])
            ? item["routes"]
            : [];

    return {
      id: String(item["id"] ?? item["_id"] ?? ""),
      title: String(item["title"] ?? ""),
      assignedToAuthUserId:
        this.readEntityId(
          item["assignedToAuthUserId"] ??
            item["assignedToAuthUser"] ??
            item["assignedUser"],
        ) ?? undefined,
      assignedToName: item["assignedToName"]
        ? String(item["assignedToName"])
        : undefined,
      vehicleId: this.readEntityId(
        item["vehicleId"] ?? item["vehicle"] ?? item["assignedVehicle"],
      ),
      vehicleName: item["vehicleName"]
        ? String(item["vehicleName"])
        : undefined,
      createdByAuthUserId:
        this.readEntityId(item["createdByAuthUserId"] ?? item["createdBy"]) ??
        undefined,
      createdByName: item["createdByName"]
        ? String(item["createdByName"])
        : undefined,
      status: item["status"] ? String(item["status"]) : undefined,
      approval:
        typeof item["approval"] === "object" && item["approval"] !== null
          ? {
              status: (item["approval"] as Record<string, unknown>)["status"]
                ? String(
                    (item["approval"] as Record<string, unknown>)["status"],
                  )
                : undefined,
            }
          : undefined,
      parallelLegs:
        typeof item["parallelLegs"] === "boolean"
          ? item["parallelLegs"]
          : undefined,
      scheduledDate: item["scheduledDate"]
        ? String(item["scheduledDate"])
        : undefined,
      note: item["note"] ? String(item["note"]) : undefined,
      legCount: Number(item["legCount"] ?? legs.length),
      completedLegCount: Number(item["completedLegCount"] ?? 0),
      lastActivityAt: item["lastActivityAt"]
        ? String(item["lastActivityAt"])
        : undefined,
      createdAt: item["createdAt"] ? String(item["createdAt"]) : undefined,
      updatedAt: item["updatedAt"] ? String(item["updatedAt"]) : undefined,
      legs: legs.map((leg) => {
        const wrapper = (leg ?? {}) as Record<string, unknown>;
        const nestedLeg = [
          wrapper["leg"],
          wrapper["taskLeg"],
          wrapper["routeLeg"],
          wrapper["route"],
        ].find((value) => typeof value === "object" && value !== null) as
          | Record<string, unknown>
          | undefined;
        const row = nestedLeg ?? wrapper;
        const fromLocation =
          typeof row["fromLocation"] === "object" &&
          row["fromLocation"] !== null
            ? (row["fromLocation"] as Record<string, unknown>)
            : null;
        const toLocation =
          typeof row["toLocation"] === "object" && row["toLocation"] !== null
            ? (row["toLocation"] as Record<string, unknown>)
            : null;
        const stockCargoRows = Array.isArray(row["stockCargo"])
          ? (row["stockCargo"] as Record<string, unknown>[])
          : typeof row["stockCargo"] === "object" && row["stockCargo"] !== null
            ? [row["stockCargo"] as Record<string, unknown>]
            : [];
        const peopleCargo =
          typeof row["peopleCargo"] === "object" && row["peopleCargo"] !== null
            ? (row["peopleCargo"] as Record<string, unknown>)
            : null;
        const cashCargo =
          typeof row["cashCargo"] === "object" && row["cashCargo"] !== null
            ? (row["cashCargo"] as Record<string, unknown>)
            : null;

        const persistedLegId =
          this.readEntityId(
            row["id"] ??
              row["_id"] ??
              row["taskLegId"] ??
              row["routeLegId"] ??
              row["legId"] ??
              wrapper["id"] ??
              wrapper["_id"] ??
              wrapper["taskLegId"] ??
              wrapper["routeLegId"] ??
              wrapper["legId"],
          ) ?? undefined;

        return {
          id: persistedLegId,
          taskLegId:
            this.readEntityId(row["taskLegId"] ?? wrapper["taskLegId"]) ??
            persistedLegId,
          routeLegId:
            this.readEntityId(row["routeLegId"] ?? wrapper["routeLegId"]) ??
            persistedLegId,
          taskId:
            this.readEntityId(row["taskId"] ?? wrapper["taskId"]) ?? undefined,
          legOrder: Number(row["legOrder"] ?? 0) || undefined,
          movementType: row["movementType"]
            ? String(row["movementType"])
            : undefined,
          fromLocationId:
            this.readEntityId(row["fromLocationId"] ?? row["fromLocation"]) ??
            undefined,
          fromLocationType: row["fromLocationType"]
            ? String(row["fromLocationType"])
            : fromLocation?.["locationType"]
              ? String(fromLocation["locationType"])
              : fromLocation?.["type"]
                ? String(fromLocation["type"])
                : undefined,
          fromLocationName: row["fromLocationName"]
            ? String(row["fromLocationName"])
            : fromLocation?.["name"]
              ? String(fromLocation["name"])
              : fromLocation?.["locationName"]
                ? String(fromLocation["locationName"])
                : row["fromLocationLabel"]
                  ? String(row["fromLocationLabel"])
                  : undefined,
          fromLocationLabel: row["fromLocationLabel"]
            ? String(row["fromLocationLabel"])
            : undefined,
          toLocationId:
            this.readEntityId(row["toLocationId"] ?? row["toLocation"]) ??
            undefined,
          toLocationType: row["toLocationType"]
            ? String(row["toLocationType"])
            : toLocation?.["locationType"]
              ? String(toLocation["locationType"])
              : toLocation?.["type"]
                ? String(toLocation["type"])
                : undefined,
          toLocationName: row["toLocationName"]
            ? String(row["toLocationName"])
            : toLocation?.["name"]
              ? String(toLocation["name"])
              : toLocation?.["locationName"]
                ? String(toLocation["locationName"])
                : row["toLocationLabel"]
                  ? String(row["toLocationLabel"])
                  : undefined,
          toLocationLabel: row["toLocationLabel"]
            ? String(row["toLocationLabel"])
            : undefined,
          stockCargo: stockCargoRows.map((stockCargo) => ({
            variantId:
              this.readEntityId(
                stockCargo["variantId"] ?? stockCargo["variant"],
              ) ?? undefined,
            productId:
              this.readEntityId(
                stockCargo["productId"] ?? stockCargo["product"],
              ) ?? undefined,
            categoryId:
              this.readEntityId(
                stockCargo["categoryId"] ?? stockCargo["category"],
              ) ?? undefined,
            variantName: stockCargo["variantName"]
              ? String(stockCargo["variantName"])
              : undefined,
            productName: stockCargo["productName"]
              ? String(stockCargo["productName"])
              : undefined,
            categoryName: stockCargo["categoryName"]
              ? String(stockCargo["categoryName"])
              : undefined,
            unit: stockCargo["unit"] ? String(stockCargo["unit"]) : undefined,
            qtyAssigned: Number(stockCargo["qtyAssigned"] ?? 0),
            qtyPickedUp: Number(stockCargo["qtyPickedUp"] ?? 0),
            qtyDelivered: Number(stockCargo["qtyDelivered"] ?? 0),
            qtyLost: Number(stockCargo["qtyLost"] ?? 0),
            qtyDamaged: Number(stockCargo["qtyDamaged"] ?? 0),
            qtyReturning: Number(stockCargo["qtyReturning"] ?? 0),
          })),
          peopleCargo: peopleCargo
            ? {
                passengerType: peopleCargo["passengerType"]
                  ? String(peopleCargo["passengerType"])
                  : undefined,
                passengerIds: Array.isArray(peopleCargo["passengerIds"])
                  ? (peopleCargo["passengerIds"] as unknown[])
                      .map((value) => this.readEntityId(value) ?? "")
                      .filter(Boolean)
                  : [],
                passengerCount: Number(peopleCargo["passengerCount"] ?? 0),
              }
            : undefined,
          cashCargo: cashCargo
            ? {
                amountAssigned: Number(cashCargo["amountAssigned"] ?? 0),
                note: cashCargo["note"] ? String(cashCargo["note"]) : undefined,
              }
            : undefined,
          note: row["note"] ? String(row["note"]) : undefined,
          status: row["status"] ? String(row["status"]) : undefined,
        };
      }),
    };
  }

  override async createLogisticsTask(
    payload: LogisticsTaskUpsertPayload,
  ): Promise<unknown> {
    return firstValueFrom(
      this.http.post(
        `${environment.apiBaseUrl}/aggregation/logistics/tasks`,
        payload,
      ),
    );
  }

  override async updateLogisticsTask(
    id: string,
    payload: LogisticsTaskUpsertPayload,
  ): Promise<unknown> {
    return firstValueFrom(
      this.http.put(
        `${environment.apiBaseUrl}/aggregation/logistics/tasks/${id}`,
        payload,
      ),
    );
  }

  override async deleteLogisticsTask(id: string): Promise<void> {
    await firstValueFrom(
      this.http.delete<void>(
        `${environment.apiBaseUrl}/aggregation/logistics/tasks/${id}`,
      ),
    );
  }

  override async getLogisticsUsers(
    query: {
      page?: number;
      limit?: number;
      search?: string;
      accountStatus?: string;
      approvalStatus?: string;
      agency?: string;
      agencyId?: string;
    } = {},
  ): Promise<PagedResult<LogisticsUserApiRecord>> {
    const params: Record<string, string> = {};
    if (query.page !== undefined) params["page"] = String(query.page);
    if (query.limit !== undefined) params["limit"] = String(query.limit);
    if (query.search) params["search"] = query.search;
    if (query.accountStatus) params["accountStatus"] = query.accountStatus;
    if (query.approvalStatus) params["approvalStatus"] = query.approvalStatus;
    if (query.agency) params["agency"] = query.agency;
    if (query.agencyId) params["agencyId"] = query.agencyId;

    const res = await firstValueFrom(
      this.http.get<{
        success: boolean;
        data: {
          items: Record<string, unknown>[];
          page: number;
          limit: number;
          total: number;
        };
      }>(`${environment.apiBaseUrl}/auth/logistics-users`, { params }),
    );

    const items: LogisticsUserApiRecord[] = (res.data?.items ?? []).map((u) => {
      const contact = (u["contact"] as Record<string, unknown>) ?? {};
      const phone = (contact["phone"] as Record<string, unknown>) ?? {};
      const addressRaw = (u["address"] as Record<string, unknown>) ?? {};
      const assignedAgency = this.readAssignedAgencyFromUser(u, "logistics");
      const assignedSupervisor =
        (u["assignedSupervisor"] as Record<string, unknown>) ?? null;
      const profile = (u["profile"] as Record<string, unknown>) ?? {};
      const logisticsProfile =
        (u["logisticsProfile"] as Record<string, unknown>) ??
        (profile["logistics"] as Record<string, unknown>) ??
        {};
      const taskSummary = (u["taskSummary"] as Record<string, unknown>) ?? {};
      const assignedVehicle =
        logisticsProfile["assignedVehicle"] ??
        logisticsProfile["assignedVehicleId"] ??
        logisticsProfile["vehicleId"] ??
        u["assignedVehicle"] ??
        u["assignedVehicleId"] ??
        u["vehicleId"];

      return {
        id: (u["id"] as string) ?? (u["_id"] as string) ?? "",
        fullName:
          (u["fullName"] as string) ||
          `${u["firstName"] ?? ""} ${u["lastName"] ?? ""}`.trim(),
        firstName: (u["firstName"] as string) ?? "",
        lastName: (u["lastName"] as string) ?? "",
        email: (u["email"] as string) ?? "",
        accountStatus: (u["accountStatus"] as string) ?? "active",
        contactPhoneDisplay: (u["contactPhoneDisplay"] as string) ?? "",
        contact: {
          phone: {
            dialCode: Number(phone["dialCode"] ?? 0),
            iso2: (phone["iso2"] as string) ?? "",
            country: (phone["country"] as string) ?? "",
            number: (phone["number"] as string) ?? "",
          },
        },
        assignedAgency: assignedAgency
          ? {
              id: assignedAgency.id,
              name: assignedAgency.name,
              city: assignedAgency.city ?? "",
              area: assignedAgency.area ?? "",
            }
          : null,
        assignedSupervisor: assignedSupervisor
          ? {
              id: (assignedSupervisor["id"] as string) ?? "",
              fullName: (assignedSupervisor["fullName"] as string) ?? "",
              email: (assignedSupervisor["email"] as string) ?? "",
            }
          : null,
        logisticsProfile: {
          employeeCode: (logisticsProfile["employeeCode"] as string) ?? "",
          baseHub: (logisticsProfile["baseHub"] as string) ?? "",
          shift: (logisticsProfile["shift"] as string) ?? "",
          assignedVehicle: this.readEntityId(assignedVehicle),
          assignedVehicleLabel:
            (logisticsProfile["assignedVehicleLabel"] as string) ?? "",
          joinedAt: (logisticsProfile["joinedAt"] as string) ?? "",
        },
        taskSummary: {
          total: Number(taskSummary["total"] ?? 0),
          open: Number(taskSummary["open"] ?? 0),
          inProgress: Number(taskSummary["inProgress"] ?? 0),
          completed: Number(taskSummary["completed"] ?? 0),
        },
        address: {
          formattedAddress: (addressRaw["formattedAddress"] as string) ?? "",
          streetAddress: (addressRaw["streetAddress"] as string) ?? "",
          locality: (addressRaw["locality"] as string) ?? "",
          subLocality: (addressRaw["subLocality"] as string) ?? "",
          region: (addressRaw["region"] as string) ?? "",
          country: (addressRaw["country"] as string) ?? "",
          postalCode: (addressRaw["postalCode"] as string) ?? "",
          area: (addressRaw["area"] as string) ?? "",
        },
        city: (u["city"] as string) ?? "",
        area: (u["area"] as string) ?? "",
        weeklyAnalysis: (u["weeklyAnalysis"] as string) ?? "",
        monthlyAnalysis: (u["monthlyAnalysis"] as string) ?? "",
        monthToDateAnalysis: (u["monthToDateAnalysis"] as string) ?? "",
        createdAt: (u["createdAt"] as string) ?? "",
        updatedAt: (u["updatedAt"] as string) ?? "",
      };
    });

    return this.capPagedResult(
      {
        items,
        page: res.data?.page ?? query.page ?? 1,
        limit: res.data?.limit ?? query.limit ?? items.length,
        total: res.data?.total ?? items.length,
      },
      query.page,
      query.limit,
    );
  }

  override async getLogisticsUserById(
    id: string,
  ): Promise<LogisticsUserApiRecord | null> {
    try {
      const res = await firstValueFrom(
        this.http.get<{ success: boolean; data: Record<string, unknown> }>(
          `${environment.apiBaseUrl}/auth/logistics-users/${id}`,
        ),
      );
      const u = res.data;
      if (!u) return null;
      const contact = (u["contact"] as Record<string, unknown>) ?? {};
      const phone = (contact["phone"] as Record<string, unknown>) ?? {};
      const addr = (u["address"] as Record<string, unknown>) ?? null;
      const addrGeo = (addr?.["geo"] as Record<string, unknown>) ?? null;
      const addrCoords = (addrGeo?.["coordinates"] as [number, number]) ?? [
        0, 0,
      ];
      const assignedAgency = this.readAssignedAgencyFromUser(u, "logistics");
      const assignedSupervisor =
        (u["assignedSupervisor"] as Record<string, unknown>) ?? null;
      const profile = (u["profile"] as Record<string, unknown>) ?? {};
      const logisticsProfile =
        (u["logisticsProfile"] as Record<string, unknown>) ??
        (profile["logistics"] as Record<string, unknown>) ??
        {};
      const taskSummary = (u["taskSummary"] as Record<string, unknown>) ?? {};
      const assignedVehicle =
        logisticsProfile["assignedVehicle"] ??
        logisticsProfile["assignedVehicleId"] ??
        logisticsProfile["vehicleId"] ??
        u["assignedVehicle"] ??
        u["assignedVehicleId"] ??
        u["vehicleId"];
      return {
        id: (u["id"] as string) ?? (u["_id"] as string) ?? "",
        fullName:
          (u["fullName"] as string) ||
          `${u["firstName"] ?? ""} ${u["lastName"] ?? ""}`.trim(),
        firstName: (u["firstName"] as string) ?? "",
        lastName: (u["lastName"] as string) ?? "",
        email: (u["email"] as string) ?? "",
        accountStatus: (u["accountStatus"] as string) ?? "active",
        contactPhoneDisplay: (u["contactPhoneDisplay"] as string) ?? "",
        contact: {
          email: (contact["email"] as string) ?? "",
          phone: {
            dialCode: Number(phone["dialCode"] ?? 0),
            iso2: (phone["iso2"] as string) ?? "",
            country: (phone["country"] as string) ?? "",
            number: (phone["number"] as string) ?? "",
          },
        },
        address: addr
          ? {
              formattedAddress: (addr["formattedAddress"] as string) ?? "",
              streetAddress: (addr["streetAddress"] as string) ?? "",
              locality: (addr["locality"] as string) ?? "",
              subLocality: (addr["subLocality"] as string) ?? "",
              region: (addr["region"] as string) ?? "",
              country: (addr["country"] as string) ?? "",
              postalCode: (addr["postalCode"] as string) ?? "",
              area: (addr["area"] as string) ?? "",
              geo: { type: "Point", coordinates: addrCoords },
            }
          : undefined,
        assignedAgency: assignedAgency
          ? {
              id: assignedAgency.id,
              name: assignedAgency.name,
              city: assignedAgency.city ?? "",
              area: assignedAgency.area ?? "",
            }
          : null,
        assignedSupervisor: assignedSupervisor
          ? {
              id: (assignedSupervisor["id"] as string) ?? "",
              fullName: (assignedSupervisor["fullName"] as string) ?? "",
              email: (assignedSupervisor["email"] as string) ?? "",
            }
          : null,
        logisticsProfile: {
          employeeCode: (logisticsProfile["employeeCode"] as string) ?? "",
          baseHub: (logisticsProfile["baseHub"] as string) ?? "",
          shift: (logisticsProfile["shift"] as string) ?? "",
          assignedVehicle: this.readEntityId(assignedVehicle),
          joinedAt: (logisticsProfile["joinedAt"] as string) ?? "",
        },
        taskSummary: {
          total: Number(taskSummary["total"] ?? 0),
          open: Number(taskSummary["open"] ?? 0),
          inProgress: Number(taskSummary["inProgress"] ?? 0),
          completed: Number(taskSummary["completed"] ?? 0),
        },
        weeklyAnalysis: (u["weeklyAnalysis"] as string) ?? "",
        monthlyAnalysis: (u["monthlyAnalysis"] as string) ?? "",
        monthToDateAnalysis: (u["monthToDateAnalysis"] as string) ?? "",
        createdAt: (u["createdAt"] as string) ?? "",
        updatedAt: (u["updatedAt"] as string) ?? "",
      };
    } catch {
      return null;
    }
  }

  override async createBtlCampaign(
    payload: BtlCampaignUpsertPayload,
  ): Promise<BtlCampaignApiRecord | null> {
    const res = await firstValueFrom(
      this.http.post<
        | { success?: boolean; data?: Record<string, unknown> }
        | Record<string, unknown>
      >(
        `${environment.apiBaseUrl}/aggregation/btl/campaigns`,
        cleanPayload(payload as unknown as Record<string, unknown>),
      ),
    );
    const raw = this.extractEntity(
      res as
        | ApiEntityResponse<Record<string, unknown>>
        | Record<string, unknown>,
    );
    return raw ? this.normalizeBtlCampaignRecord(raw) : null;
  }

  override async updateBtlCampaign(
    id: string,
    payload: BtlCampaignUpsertPayload,
  ): Promise<BtlCampaignApiRecord | null> {
    const res = await firstValueFrom(
      this.http.patch<
        | { success?: boolean; data?: Record<string, unknown> }
        | Record<string, unknown>
      >(
        `${environment.apiBaseUrl}/aggregation/btl/campaigns/${id}`,
        cleanPayload(payload as unknown as Record<string, unknown>),
      ),
    );
    const raw = this.extractEntity(
      res as
        | ApiEntityResponse<Record<string, unknown>>
        | Record<string, unknown>,
    );
    return raw ? this.normalizeBtlCampaignRecord(raw) : null;
  }

  override async deleteBtlCampaign(id: string): Promise<boolean> {
    await firstValueFrom(
      this.http.delete(
        `${environment.apiBaseUrl}/aggregation/btl/campaigns/${id}`,
      ),
    );
    return true;
  }

  override async getVehicles(
    query: {
      page?: number;
      limit?: number;
      search?: string;
      assignedToAuthUserId?: string;
      status?: string;
      isActive?: string;
      type?: string;
    } = {},
  ): Promise<PagedResult<VehicleApiRecord>> {
    const params: Record<string, string> = {
      page: String(query.page ?? 1),
      limit: String(query.limit ?? 20),
    };
    if (query.search) params["search"] = query.search;
    if (query.assignedToAuthUserId)
      params["assignedToAuthUserId"] = query.assignedToAuthUserId;
    if (query.status) params["status"] = query.status;
    if (query.isActive !== undefined && query.isActive !== "")
      params["isActive"] = query.isActive;
    if (query.type) params["type"] = query.type;

    const res = await firstValueFrom(
      this.http.get<{
        success: boolean;
        data: {
          items: Record<string, unknown>[];
          page: number;
          limit: number;
          total: number;
        };
      }>(`${environment.apiBaseUrl}/aggregation/logistics/vehicles`, {
        params,
      }),
    );

    const items: VehicleApiRecord[] = (res.data?.items ?? []).map((v) => ({
      id: (v["id"] as string) ?? (v["_id"] as string) ?? "",
      registrationNumber: (v["registrationNumber"] as string) ?? "",
      make: (v["make"] as string) ?? "",
      modelName: (v["modelName"] as string) ?? "",
      vehicleName: (v["vehicleName"] as string) ?? "",
      year: Number(v["year"] ?? 0),
      type: (v["type"] as string) ?? "",
      capacity: Number(v["capacity"] ?? 0),
      capacityUnit: (v["capacityUnit"] as string) ?? "",
      assignedToAuthUserId: this.readEntityId(
        v["assignedToAuthUserId"] ?? v["assignedUser"] ?? v["assignedUserId"],
      ),
      currentAgencyId: this.readEntityId(
        v["currentAgencyId"] ?? v["currentAgency"],
      ),
      status: (v["status"] as string) ?? "",
      fuelType: (v["fuelType"] as string) ?? "",
      isActive: Boolean(v["isActive"]),
      createdAt: (v["createdAt"] as string) ?? "",
      updatedAt: (v["updatedAt"] as string) ?? "",
    }));

    return this.capPagedResult(
      {
        items,
        page: res.data.page,
        limit: res.data.limit,
        total: res.data.total,
      },
      query.page,
      query.limit,
    );
  }

  override async getVehicleById(id: string): Promise<VehicleApiRecord | null> {
    try {
      const res = await firstValueFrom(
        this.http.get<{ success: boolean; data: Record<string, unknown> }>(
          `${environment.apiBaseUrl}/aggregation/logistics/vehicles/${id}`,
        ),
      );
      const v = res.data;
      if (!v) return null;
      return {
        id: (v["id"] as string) ?? (v["_id"] as string) ?? "",
        registrationNumber: (v["registrationNumber"] as string) ?? "",
        make: (v["make"] as string) ?? "",
        modelName: (v["modelName"] as string) ?? "",
        vehicleName: (v["vehicleName"] as string) ?? "",
        year: Number(v["year"] ?? 0),
        type: (v["type"] as string) ?? "",
        capacity: Number(v["capacity"] ?? 0),
        capacityUnit: (v["capacityUnit"] as string) ?? "",
        assignedToAuthUserId: this.readEntityId(
          v["assignedToAuthUserId"] ?? v["assignedUser"] ?? v["assignedUserId"],
        ),
        currentAgencyId: this.readEntityId(
          v["currentAgencyId"] ?? v["currentAgency"],
        ),
        status: (v["status"] as string) ?? "",
        fuelType: (v["fuelType"] as string) ?? "",
        isActive: Boolean(v["isActive"]),
        documents: ((v["documents"] as Record<string, unknown>[]) ?? []).map(
          (d) => ({
            imageUris: (d["imageUris"] as string[]) ?? [],
            expiryDate: (d["expiryDate"] as string) ?? "",
            category: (d["category"] as string) ?? "",
            categoryName: (d["categoryName"] as string) ?? "",
          }),
        ),
        createdAt: (v["createdAt"] as string) ?? "",
        updatedAt: (v["updatedAt"] as string) ?? "",
      };
    } catch {
      return null;
    }
  }

  override async getFuelById(id: string): Promise<FuelRequestApiRecord | null> {
    try {
      const res = await firstValueFrom(
        this.http.get<{ success: boolean; data: Record<string, unknown> }>(
          `${environment.apiBaseUrl}/aggregation/logistics/fuel-requests/${id}`,
        ),
      );
      const f = res.data;
      if (!f) return null;
      const loc = (f["fuelStationLocation"] as Record<string, unknown>) ?? null;
      return {
        id: (f["id"] as string) ?? (f["_id"] as string) ?? "",
        logisticsUserId: (f["logisticsUserId"] as string) ?? "",
        logisticsUserName: (f["logisticsUserName"] as string) ?? "",
        vehicleId: (f["vehicleId"] as string) ?? "",
        vehicleName: (f["vehicleName"] as string) ?? "",
        fuelStationName: (f["fuelStationName"] as string) ?? "",
        fuelStationLocation: loc
          ? {
              formattedAddress: (loc["formattedAddress"] as string) ?? "",
              streetAddress: (loc["streetAddress"] as string) ?? "",
              locality: (loc["locality"] as string) ?? "",
              region: (loc["region"] as string) ?? "",
              country: (loc["country"] as string) ?? "",
              postalCode: (loc["postalCode"] as string) ?? "",
              area: (loc["area"] as string) ?? "",
            }
          : undefined,
        date: (f["date"] as string) ?? "",
        odometerReadingKm: Number(f["odometerReadingKm"] ?? 0),
        amountFilledLitres: Number(f["amountFilledLitres"] ?? 0),
        totalCost: Number(f["totalCost"] ?? 0),
        fuelCardUsed: (f["fuelCardUsed"] as string) ?? "",
        receiptPhotoUri: (f["receiptPhotoUri"] as string) ?? "",
        receiptUploaded: Boolean(f["receiptUploaded"]),
        status: (f["status"] as string) ?? "pending",
        createdAt: (f["createdAt"] as string) ?? "",
        updatedAt: (f["updatedAt"] as string) ?? "",
      };
    } catch {
      return null;
    }
  }

  override async actionFuel(
    id: string,
    payload: fuelActionPayload,
  ): Promise<FuelRequestApiRecord | null> {
    try {
      const res = await firstValueFrom(
        this.http.post<{ success: boolean; data: Record<string, unknown> }>(
          `${environment.apiBaseUrl}/aggregation/logistics/fuel-requests/${id}/review`,
          payload,
        ),
      );
      const f = res?.data;
      if (!f) return null;
      const loc = (f["fuelStationLocation"] as Record<string, unknown>) ?? null;
      return {
        id: (f["id"] as string) ?? (f["_id"] as string) ?? id,
        logisticsUserId: (f["logisticsUserId"] as string) ?? "",
        logisticsUserName: (f["logisticsUserName"] as string) ?? "",
        vehicleId: (f["vehicleId"] as string) ?? "",
        vehicleName: (f["vehicleName"] as string) ?? "",
        fuelStationName: (f["fuelStationName"] as string) ?? "",
        fuelStationLocation: loc
          ? {
              formattedAddress: (loc["formattedAddress"] as string) ?? "",
              streetAddress: (loc["streetAddress"] as string) ?? "",
              locality: (loc["locality"] as string) ?? "",
              region: (loc["region"] as string) ?? "",
              country: (loc["country"] as string) ?? "",
              postalCode: (loc["postalCode"] as string) ?? "",
              area: (loc["area"] as string) ?? "",
            }
          : undefined,
        date: (f["date"] as string) ?? "",
        odometerReadingKm: Number(f["odometerReadingKm"] ?? 0),
        amountFilledLitres: Number(f["amountFilledLitres"] ?? 0),
        totalCost: Number(f["totalCost"] ?? 0),
        fuelCardUsed: (f["fuelCardUsed"] as string) ?? "",
        receiptPhotoUri: (f["receiptPhotoUri"] as string) ?? "",
        receiptUploaded: Boolean(f["receiptUploaded"]),
        status:
          (f["status"] as string) ??
          (payload.decision === "approve" ? "approved" : "rejected"),
        createdAt: (f["createdAt"] as string) ?? "",
        updatedAt: (f["updatedAt"] as string) ?? "",
      };
    } catch {
      return null;
    }
  }

  override async createFuelRequest(
    payload: CreateFuelRequestPayload,
  ): Promise<FuelRequestApiRecord | null> {
    try {
      const res = await firstValueFrom(
        this.http.post<{ success: boolean; data: Record<string, unknown> }>(
          `${environment.apiBaseUrl}/aggregation/logistics/fuel-requests`,
          payload,
        ),
      );
      const f = res.data;
      if (!f) return null;
      const loc = (f["fuelStationLocation"] as Record<string, unknown>) ?? null;
      return {
        id: (f["id"] as string) ?? (f["_id"] as string) ?? "",
        logisticsUserId: (f["logisticsUserId"] as string) ?? "",
        logisticsUserName: (f["logisticsUserName"] as string) ?? "",
        vehicleId: (f["vehicleId"] as string) ?? "",
        vehicleName: (f["vehicleName"] as string) ?? "",
        fuelStationName: (f["fuelStationName"] as string) ?? "",
        fuelStationLocation: loc
          ? {
              formattedAddress: (loc["formattedAddress"] as string) ?? "",
              streetAddress: (loc["streetAddress"] as string) ?? "",
              locality: (loc["locality"] as string) ?? "",
              region: (loc["region"] as string) ?? "",
              country: (loc["country"] as string) ?? "",
              postalCode: (loc["postalCode"] as string) ?? "",
              area: (loc["area"] as string) ?? "",
            }
          : undefined,
        date: (f["date"] as string) ?? "",
        odometerReadingKm: Number(f["odometerReadingKm"] ?? 0),
        amountFilledLitres: Number(f["amountFilledLitres"] ?? 0),
        totalCost: Number(f["totalCost"] ?? 0),
        fuelCardUsed: (f["fuelCardUsed"] as string) ?? "",
        receiptPhotoUri: (f["receiptPhotoUri"] as string) ?? "",
        receiptUploaded: Boolean(f["receiptUploaded"]),
        status: (f["status"] as string) ?? "pending",
        createdAt: (f["createdAt"] as string) ?? "",
        updatedAt: (f["updatedAt"] as string) ?? "",
      };
    } catch {
      return null;
    }
  }

  override async updateFuelRequest(
    id: string,
    payload: UpdateFuelRequestPayload,
  ): Promise<FuelRequestApiRecord | null> {
    try {
      const res = await firstValueFrom(
        this.http.put<{ success: boolean; data: Record<string, unknown> }>(
          `${environment.apiBaseUrl}/aggregation/logistics/fuel-requests/${id}`,
          payload,
        ),
      );
      const f = res.data;
      if (!f) return null;
      const loc = (f["fuelStationLocation"] as Record<string, unknown>) ?? null;
      return {
        id: (f["id"] as string) ?? (f["_id"] as string) ?? id,
        logisticsUserId: (f["logisticsUserId"] as string) ?? "",
        logisticsUserName: (f["logisticsUserName"] as string) ?? "",
        vehicleId: (f["vehicleId"] as string) ?? "",
        vehicleName: (f["vehicleName"] as string) ?? "",
        fuelStationName: (f["fuelStationName"] as string) ?? "",
        fuelStationLocation: loc
          ? {
              formattedAddress: (loc["formattedAddress"] as string) ?? "",
              streetAddress: (loc["streetAddress"] as string) ?? "",
              locality: (loc["locality"] as string) ?? "",
              region: (loc["region"] as string) ?? "",
              country: (loc["country"] as string) ?? "",
              postalCode: (loc["postalCode"] as string) ?? "",
              area: (loc["area"] as string) ?? "",
            }
          : undefined,
        date: (f["date"] as string) ?? "",
        odometerReadingKm: Number(f["odometerReadingKm"] ?? 0),
        amountFilledLitres: Number(f["amountFilledLitres"] ?? 0),
        totalCost: Number(f["totalCost"] ?? 0),
        fuelCardUsed: (f["fuelCardUsed"] as string) ?? "",
        receiptPhotoUri: (f["receiptPhotoUri"] as string) ?? "",
        receiptUploaded: Boolean(f["receiptUploaded"]),
        status: (f["status"] as string) ?? "pending",
        createdAt: (f["createdAt"] as string) ?? "",
        updatedAt: (f["updatedAt"] as string) ?? "",
      };
    } catch {
      return null;
    }
  }

  override async getFuelRequests(
    query: FuelRequestListQuery = {},
  ): Promise<PagedResult<FuelRequestApiRecord>> {
    const params: Record<string, string> = {
      page: String(query.page ?? 1),
      limit: String(query.limit ?? 20),
    };
    if (query.search) params["search"] = query.search;
    if (query.logisticsUserId)
      params["logisticsUserId"] = query.logisticsUserId;
    if (query.vehicleId) params["vehicleId"] = query.vehicleId;
    if (query.status) params["status"] = query.status;

    const res = await firstValueFrom(
      this.http.get<{
        success: boolean;
        data: {
          items: Record<string, unknown>[];
          page: number;
          limit: number;
          total: number;
        };
      }>(`${environment.apiBaseUrl}/aggregation/logistics/fuel-requests`, {
        params,
      }),
    );

    const items: FuelRequestApiRecord[] = (res.data?.items ?? []).map((f) => {
      const loc = (f["fuelStationLocation"] as Record<string, unknown>) ?? null;
      return {
        id: (f["id"] as string) ?? (f["_id"] as string) ?? "",
        logisticsUserId: (f["logisticsUserId"] as string) ?? "",
        logisticsUserName: (f["logisticsUserName"] as string) ?? "",
        vehicleId: (f["vehicleId"] as string) ?? "",
        vehicleName: (f["vehicleName"] as string) ?? "",
        fuelStationName: (f["fuelStationName"] as string) ?? "",
        fuelStationLocation: loc
          ? {
              formattedAddress: (loc["formattedAddress"] as string) ?? "",
              streetAddress: (loc["streetAddress"] as string) ?? "",
              locality: (loc["locality"] as string) ?? "",
              region: (loc["region"] as string) ?? "",
              country: (loc["country"] as string) ?? "",
              postalCode: (loc["postalCode"] as string) ?? "",
              area: (loc["area"] as string) ?? "",
            }
          : undefined,
        date: (f["date"] as string) ?? "",
        odometerReadingKm: Number(f["odometerReadingKm"] ?? 0),
        amountFilledLitres: Number(f["amountFilledLitres"] ?? 0),
        totalCost: Number(f["totalCost"] ?? 0),
        fuelCardUsed: (f["fuelCardUsed"] as string) ?? "",
        receiptPhotoUri: (f["receiptPhotoUri"] as string) ?? "",
        receiptUploaded: Boolean(f["receiptUploaded"]),
        status: (f["status"] as string) ?? "pending",
        createdAt: (f["createdAt"] as string) ?? "",
        updatedAt: (f["updatedAt"] as string) ?? "",
      };
    });

    return this.capPagedResult(
      {
        items,
        page: res.data.page,
        limit: res.data.limit,
        total: res.data.total,
      },
      query.page,
      query.limit,
    );
  }

  override async getMaintenanceTypes(
    query: { search?: string; page?: number; limit?: number } = {},
  ): Promise<PagedResult<MaintenanceTypeRecord>> {
    const params: Record<string, string> = {
      page: String(query.page ?? 1),
      limit: String(query.limit ?? 10),
    };
    if (query.search?.trim()) params["search"] = query.search.trim();
    const res = await firstValueFrom(
      this.http.get<{
        success: boolean;
        data: {
          items: Record<string, unknown>[];
          page: number;
          limit: number;
          total: number;
        };
      }>(`${environment.apiBaseUrl}/aggregation/logistics/maintenance-types`, {
        params,
      }),
    );
    const items: MaintenanceTypeRecord[] = (res.data?.items ?? []).map((r) =>
      this.normalizeMaintenanceTypeRecord(r),
    );
    const page = res.data?.page ?? query.page ?? 1;
    const limit = res.data?.limit ?? query.limit ?? 10;
    const total = res.data?.total ?? items.length;
    const pagedItems =
      items.length > limit
        ? items.slice((page - 1) * limit, page * limit)
        : items;
    return { items: pagedItems, page, limit, total };
  }

  override async getMaintenanceTypeById(
    id: string,
  ): Promise<MaintenanceTypeRecord | null> {
    const res = await firstValueFrom(
      this.http.get<{ success?: boolean; data?: Record<string, unknown> }>(
        `${environment.apiBaseUrl}/aggregation/logistics/maintenance-types/${id}`,
      ),
    ).catch(() => null);
    if (!res?.data) return null;
    return this.normalizeMaintenanceTypeRecord(res.data);
  }

  override async createMaintenanceType(payload: {
    name: string;
  }): Promise<MaintenanceTypeRecord> {
    const res = await firstValueFrom(
      this.http.post<
        | { success?: boolean; data?: Record<string, unknown> }
        | Record<string, unknown>
      >(`${environment.apiBaseUrl}/aggregation/logistics/maintenance-types`, {
        name: payload.name,
      }),
    );
    const raw = this.extractEntity(
      res as
        | ApiEntityResponse<Record<string, unknown>>
        | Record<string, unknown>,
    );
    if (!raw) throw new Error("Create maintenance type response missing");
    return this.normalizeMaintenanceTypeRecord(raw);
  }

  override async updateMaintenanceType(
    id: string,
    payload: { name: string },
  ): Promise<MaintenanceTypeRecord> {
    const res = await firstValueFrom(
      this.http.put<
        | { success?: boolean; data?: Record<string, unknown> }
        | Record<string, unknown>
      >(
        `${environment.apiBaseUrl}/aggregation/logistics/maintenance-types/${id}`,
        { name: payload.name },
      ),
    );
    const raw = this.extractEntity(
      res as
        | ApiEntityResponse<Record<string, unknown>>
        | Record<string, unknown>,
    );
    if (!raw) throw new Error("Update maintenance type response missing");
    return this.normalizeMaintenanceTypeRecord(raw);
  }

  override deleteMaintenanceType(id: string): Promise<void> {
    return firstValueFrom(
      this.http.delete<void>(
        `${environment.apiBaseUrl}/aggregation/logistics/maintenance-types/${id}`,
      ),
    );
  }

  override async getMaintenanceRequests(
    query: MaintenanceRequestListQuery = {},
  ): Promise<PagedResult<MaintenanceRequestApiRecord>> {
    const params: Record<string, string> = {
      page: String(query.page ?? 1),
      limit: String(query.limit ?? 20),
    };
    if (query.search) params["search"] = query.search;
    if (query.vehicleId) params["vehicleId"] = query.vehicleId;
    if (query.raisedByAuthUserId)
      params["raisedByAuthUserId"] = query.raisedByAuthUserId;
    if (query.status) params["status"] = query.status;
    if (query.priority) params["priority"] = query.priority;
    if (query.maintenanceType)
      params["maintenanceType"] = query.maintenanceType;

    const res = await firstValueFrom(
      this.http.get<{
        success: boolean;
        data: {
          items: Record<string, unknown>[];
          page: number;
          limit: number;
          total: number;
        };
      }>(
        `${environment.apiBaseUrl}/aggregation/logistics/maintenance-requests`,
        { params },
      ),
    );

    const items: MaintenanceRequestApiRecord[] = (res.data?.items ?? []).map(
      (r) => ({
        id: (r["id"] as string) ?? (r["_id"] as string) ?? "",
        vehicleId: (r["vehicleId"] as string) ?? "",
        vehicleName: (r["vehicleName"] as string) ?? "",
        maintenanceTypeId: (r["maintenanceTypeId"] as string) ?? "",
        maintenanceTypeName: (r["maintenanceTypeName"] as string) ?? "",
        issueDescription: (r["issueDescription"] as string) ?? "",
        urgency: (r["urgency"] as string) ?? "",
        cost: Number(r["cost"] ?? 0),
        status: (r["status"] as string) ?? "pending",
        submittedAt: (r["submittedAt"] as string) ?? "",
        raisedByAuthUserId: (r["raisedByAuthUserId"] as string) ?? "",
        raisedByName: (r["raisedByName"] as string) ?? "",
        evidencePhotoUris: (r["evidencePhotoUris"] as string[]) ?? [],
      }),
    );

    return this.capPagedResult(
      {
        items,
        page: res.data.page,
        limit: res.data.limit,
        total: res.data.total,
      },
      query.page,
      query.limit,
    );
  }

  override async getMaintenanceRequestById(
    vehicleId: string,
    maintenanceRequestId: string,
  ): Promise<MaintenanceRequestApiRecord | null> {
    const res = await firstValueFrom(
      this.http.get<{ success: boolean; data: Record<string, unknown> }>(
        `${environment.apiBaseUrl}/aggregation/logistics/vehicles/${vehicleId}/maintenance/${maintenanceRequestId}`,
      ),
    ).catch(() => null);
    const r = res?.data;
    if (!r) return null;
    return {
      id: (r["id"] as string) ?? (r["_id"] as string) ?? "",
      vehicleId: (r["vehicleId"] as string) ?? "",
      vehicleName: (r["vehicleName"] as string) ?? "",
      maintenanceTypeId: (r["maintenanceTypeId"] as string) ?? "",
      maintenanceTypeName: (r["maintenanceTypeName"] as string) ?? "",
      issueDescription: (r["issueDescription"] as string) ?? "",
      urgency: (r["urgency"] as string) ?? "",
      cost: Number(r["cost"] ?? 0),
      status: (r["status"] as string) ?? "pending",
      submittedAt: (r["submittedAt"] as string) ?? "",
      raisedByAuthUserId: (r["raisedByAuthUserId"] as string) ?? "",
      raisedByName: (r["raisedByName"] as string) ?? "",
      evidencePhotoUris: (r["evidencePhotoUris"] as string[]) ?? [],
    };
  }

  override async deleteMaintenanceRequest(
    vehicleId: string,
    maintenanceRequestId: string,
  ): Promise<boolean> {
    try {
      await firstValueFrom(
        this.http.delete<void>(
          `${environment.apiBaseUrl}/aggregation/logistics/vehicles/${vehicleId}/maintenance/${maintenanceRequestId}`,
        ),
      );
      return true;
    } catch {
      return false;
    }
  }

  override async approveMaintenance(
    vehicleId: string,
    maintenanceRequestId: string,
    payload: maintenanceActionPayload,
  ): Promise<MaintenanceRequestApiRecord | null> {
    try {
      const res = await firstValueFrom(
        this.http.post<{ success: boolean; data: Record<string, unknown> }>(
          `${environment.apiBaseUrl}/aggregation/logistics/vehicles/${vehicleId}/maintenance/${maintenanceRequestId}/review`,
          payload,
        ),
      );
      const r = res?.data;
      if (!r) return null;
      return {
        id: (r["id"] as string) ?? (r["_id"] as string) ?? "",
        vehicleId: (r["vehicleId"] as string) ?? "",
        vehicleName: (r["vehicleName"] as string) ?? "",
        maintenanceTypeId: (r["maintenanceTypeId"] as string) ?? "",
        maintenanceTypeName: (r["maintenanceTypeName"] as string) ?? "",
        issueDescription: (r["issueDescription"] as string) ?? "",
        urgency: (r["urgency"] as string) ?? "",
        cost: Number(r["cost"] ?? 0),
        status: (r["status"] as string) ?? payload.decision,
        submittedAt: (r["submittedAt"] as string) ?? "",
        raisedByAuthUserId: (r["raisedByAuthUserId"] as string) ?? "",
        raisedByName: (r["raisedByName"] as string) ?? "",
        evidencePhotoUris: (r["evidencePhotoUris"] as string[]) ?? [],
      };
    } catch {
      return null;
    }
  }

  override async reviewLogisticsTask(
    taskId: string,
    payload: LogisticsTaskReviewPayload,
  ): Promise<LogisticsTaskReviewResult | null> {
    try {
      const res = await firstValueFrom(
        this.http.post<{ success: boolean; data: Record<string, unknown> }>(
          `${environment.apiBaseUrl}/aggregation/logistics/tasks/${taskId}/review`,
          payload,
        ),
      );
      const d = res?.data;
      if (!d) return null;
      const approval = d["approval"] as Record<string, unknown> | undefined;
      return {
        id: (d["id"] as string) ?? (d["_id"] as string) ?? taskId,
        status: (d["status"] as string) ?? undefined,
        approval: approval
          ? { status: (approval["status"] as string) ?? undefined }
          : undefined,
      };
    } catch {
      return null;
    }
  }

  override async createMaintenanceRequest(
    payload: CreateMaintenanceRequestPayload,
  ): Promise<MaintenanceRequestApiRecord | null> {
    try {
      const res = await firstValueFrom(
        this.http.post<{ success: boolean; data: Record<string, unknown> }>(
          `${environment.apiBaseUrl}/aggregation/logistics/maintenance-requests`,
          payload,
        ),
      );
      const r = res.data;
      if (!r) return null;
      return {
        id: (r["id"] as string) ?? (r["_id"] as string) ?? "",
        vehicleId: (r["vehicleId"] as string) ?? "",
        vehicleName: (r["vehicleName"] as string) ?? "",
        maintenanceTypeId: (r["maintenanceTypeId"] as string) ?? "",
        maintenanceTypeName: (r["maintenanceTypeName"] as string) ?? "",
        issueDescription: (r["issueDescription"] as string) ?? "",
        urgency: (r["urgency"] as string) ?? "",
        cost: Number(r["cost"] ?? 0),
        status: (r["status"] as string) ?? "pending",
        submittedAt: (r["submittedAt"] as string) ?? "",
        raisedByAuthUserId: (r["raisedByAuthUserId"] as string) ?? "",
        raisedByName: (r["raisedByName"] as string) ?? "",
        evidencePhotoUris: (r["evidencePhotoUris"] as string[]) ?? [],
      };
    } catch {
      return null;
    }
  }

  override async updateMaintenanceRequest(
    vehicleId: string,
    maintenanceRequestId: string,
    payload: UpdateMaintenanceRequestPayload,
  ): Promise<MaintenanceRequestApiRecord | null> {
    try {
      const res = await firstValueFrom(
        this.http.put<{ success: boolean; data: Record<string, unknown> }>(
          `${environment.apiBaseUrl}/aggregation/logistics/vehicles/${vehicleId}/maintenance/${maintenanceRequestId}`,
          payload,
        ),
      );
      const r = res.data;
      if (!r) return null;
      return {
        id: (r["id"] as string) ?? (r["_id"] as string) ?? "",
        vehicleId: (r["vehicleId"] as string) ?? "",
        vehicleName: (r["vehicleName"] as string) ?? "",
        maintenanceTypeId: (r["maintenanceTypeId"] as string) ?? "",
        maintenanceTypeName: (r["maintenanceTypeName"] as string) ?? "",
        issueDescription: (r["issueDescription"] as string) ?? "",
        urgency: (r["urgency"] as string) ?? "",
        cost: Number(r["cost"] ?? 0),
        status: (r["status"] as string) ?? "pending",
        submittedAt: (r["submittedAt"] as string) ?? "",
        raisedByAuthUserId: (r["raisedByAuthUserId"] as string) ?? "",
        raisedByName: (r["raisedByName"] as string) ?? "",
        evidencePhotoUris: (r["evidencePhotoUris"] as string[]) ?? [],
      };
    } catch {
      return null;
    }
  }

  private normalizeMaintenanceTypeRecord(
    r: Record<string, unknown>,
  ): MaintenanceTypeRecord {
    return {
      id: (r["id"] as string) ?? (r["_id"] as string) ?? "",
      name: (r["name"] as string) ?? "",
      createdAt: (r["createdAt"] as string) ?? "",
      updatedAt: (r["updatedAt"] as string) ?? "",
    };
  }

  private normalizeAgencyProfileConfiguration(
    r: Record<string, unknown>,
  ): AgencyProfileConfiguration {
    return {
      id: (r["id"] as string) ?? (r["_id"] as string) ?? "",
      name: (r["name"] as string) ?? "",
      createdAt: (r["createdAt"] as string) ?? "",
      updatedAt: (r["updatedAt"] as string) ?? "",
    };
  }

  private normalizeProductRecord(
    raw: Record<string, unknown>,
  ): InventoryProduct {
    return {
      id: (raw["_id"] as string) ?? (raw["id"] as string) ?? "",
      categoryId: (raw["categoryId"] as string) ?? "",
      name: (raw["name"] as string) ?? "",
      threshold: Number(raw["threshold"] ?? 0),
      isCashProduct: (raw["productKind"] as string) === "cash",
      requiresVariant: (raw["requiresVariant"] as boolean) ?? false,
      isActive: (raw["status"] as string) === "active",
      createdAt: (raw["createdAt"] as string) ?? "",
      updatedAt: (raw["updatedAt"] as string) ?? "",
    };
  }

  private normalizeVariantRecord(
    raw: Record<string, unknown>,
  ): InventoryVariant {
    return {
      id: (raw["_id"] as string) ?? (raw["id"] as string) ?? "",
      productId: (raw["productId"] as string) ?? "",
      name: (raw["name"] as string) ?? "",
      sellingPrice:
        raw["sellingPrice"] != null ? Number(raw["sellingPrice"]) : undefined,
      isActive: (raw["status"] as string) === "active",
      createdAt: (raw["createdAt"] as string) ?? "",
      updatedAt: (raw["updatedAt"] as string) ?? "",
    };
  }

  /**
   * BUSINESS LOGIC: cleanQueryParams
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private cleanQueryParams(query: object): Record<string, string> {
    return Object.entries(query).reduce<Record<string, string>>((params, [key, value]) => {
      if (value === undefined || value === null || value === "") {
        return params;
      }

      params[key] = String(value);
      return params;
    }, {});
  }

  /**
   * BUSINESS LOGIC: normalizeCashOwner
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private normalizeCashOwner(value: unknown, fallbackType?: CashOwnerType, fallbackId?: string): CashOwnerRef | undefined {
    const row = value && typeof value === "object" ? value as Record<string, unknown> : {};
    const ownerType = String(row["ownerType"] ?? row["type"] ?? fallbackType ?? "").toLowerCase() as CashOwnerType;
    const ownerId = String(row["ownerId"] ?? row["id"] ?? row["_id"] ?? fallbackId ?? "");

    if (!ownerType && !ownerId) {
      return undefined;
    }

    return {
      ownerType,
      ownerId,
      ownerName: String(row["ownerName"] ?? row["name"] ?? "").trim() || undefined,
    };
  }

  /**
   * BUSINESS LOGIC: normalizeCashBalance
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private normalizeCashBalance(raw: Record<string, unknown>): CashBalance {
    const owner = this.normalizeCashOwner(
      raw,
      String(raw["ownerType"] ?? raw["type"] ?? "").toLowerCase() as CashOwnerType,
      String(raw["ownerId"] ?? raw["id"] ?? raw["_id"] ?? ""),
    );

    return {
      ownerType: owner?.ownerType ?? "agency",
      ownerId: owner?.ownerId ?? "",
      ownerName: owner?.ownerName,
      balance: Number(raw["balance"] ?? raw["amount"] ?? raw["cashBalance"] ?? 0),
      updatedAt: String(raw["updatedAt"] ?? raw["lastUpdatedAt"] ?? "").trim() || undefined,
    };
  }

  /**
   * BUSINESS LOGIC: normalizeCashMovement
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private normalizeCashMovement(raw: Record<string, unknown>): CashMovement {
    return {
      id: String(raw["id"] ?? raw["_id"] ?? ""),
      movementType: String(raw["movementType"] ?? raw["type"] ?? ""),
      amount: Number(raw["amount"] ?? raw["value"] ?? 0),
      direction: String(raw["direction"] ?? ""),
      source: this.normalizeCashOwner(raw["source"] ?? {
        ownerType: raw["sourceOwnerType"],
        ownerId: raw["sourceOwnerId"],
        ownerName: raw["sourceOwnerName"],
      }),
      destination: this.normalizeCashOwner(raw["destination"] ?? {
        ownerType: raw["destinationOwnerType"],
        ownerId: raw["destinationOwnerId"],
        ownerName: raw["destinationOwnerName"],
      }),
      note: String(raw["note"] ?? raw["notes"] ?? "").trim() || undefined,
      referenceId: String(raw["referenceId"] ?? raw["reference"] ?? "").trim() || undefined,
      createdAt: String(raw["createdAt"] ?? raw["date"] ?? "").trim() || undefined,
      createdByName: String(raw["createdByName"] ?? raw["createdBy"] ?? "").trim() || undefined,
      performedByName: String(raw["performedByName"] ?? "").trim() || undefined,
    };
  }

  private normalizeAgencyRecord(
    agency: Record<string, unknown> | undefined,
  ): AgencyRecord | null {
    if (!agency) {
      return null;
    }

    const address = this.resolveAgencyAddress(agency);
    const contact =
      (agency["contactDetails"] as Record<string, unknown>) ??
      (typeof agency["contact"] === "object" && agency["contact"] !== null
        ? (agency["contact"] as Record<string, unknown>)
        : {});
    const phone = (contact["phone"] as Record<string, unknown>) ?? {};
    const geoCoords = this.readGeoCoordinates(address["geo"]);
    const assignedPos = this.readEntityIds(
      agency["assignedPos"],
      agency["assignedPosDetails"],
    );
    const assignedPosDetails = this.readAgencyAssignedPosSummaries(
      agency["assignedPosDetails"],
      agency["assignedPos"],
    );

    return {
      id: (agency["id"] as string) ?? (agency["_id"] as string) ?? "",
      name: (agency["name"] as string) ?? "",
      code: (agency["code"] as string) ?? "",
      city: (agency["city"] as string) ?? (address["locality"] as string) ?? "",
      area: (agency["area"] as string) ?? (address["area"] as string) ?? "",
      address:
        (typeof agency["address"] === "string" ? agency["address"] : null) ??
        (address["formattedAddress"] as string) ??
        (address["streetAddress"] as string) ??
        "",
      contact:
        (typeof agency["contact"] === "string" ? agency["contact"] : null) ??
        (phone["number"] as string) ??
        "",
      status:
        ((agency["status"] as string) ?? "active") === "inactive"
          ? "inactive"
          : "active",
      profile: (agency["profile"] as string) ?? undefined,
      profileName: (agency["profileName"] as string) ?? "",
      stockStatus: (agency["stockStatus"] as AgencyStockStatus) ?? "ok",
      supervisors: Number(agency["supervisors"] ?? 0),
      agents: Number(agency["agents"] ?? 0),
      posCount: this.readCount(
        agency["posCount"],
        assignedPosDetails.length || undefined,
        assignedPos.length || undefined,
      ),
      agencyChief: (agency["agencyChief"] as string) ?? "",
      officeNumber: (agency["officeNumber"] as string) ?? "",
      posNumber: (agency["posNumber"] as string) ?? "",
      contactDetails: {
        email: (contact["email"] as string) ?? "",
        phone: {
          dialCode: Number(phone["dialCode"] ?? 0),
          iso2: (phone["iso2"] as string) ?? "",
          country: (phone["country"] as string) ?? "",
          number: (phone["number"] as string) ?? "",
        },
      },
      addressDetails: {
        formattedAddress: (address["formattedAddress"] as string) ?? "",
        streetAddress: (address["streetAddress"] as string) ?? "",
        locality: (address["locality"] as string) ?? "",
        subLocality: (address["subLocality"] as string) ?? "",
        region: (address["region"] as string) ?? "",
        country: (address["country"] as string) ?? "",
        postalCode: (address["postalCode"] as string) ?? "",
        area: (address["area"] as string) ?? "",
        geo: geoCoords,
      },
      assignedSupervisors: this.readEntityIds(
        agency["assignedSupervisors"],
        agency["assignedSupervisorDetails"],
      ),
      assignedSupervisorDetails: this.readAgencyAssignedUserSummaries(
        agency["assignedSupervisorDetails"],
        agency["assignedSupervisors"],
      ),
      assignedSecondarySupervisors: this.readEntityIds(
        agency["assignedSecondarySupervisors"],
        agency["assignedSecondarySupervisorDetails"],
      ),
      assignedSecondarySupervisorDetails: this.readAgencyAssignedUserSummaries(
        agency["assignedSecondarySupervisorDetails"],
        agency["assignedSecondarySupervisors"],
      ),
      assignedAgents: this.readEntityIds(
        agency["assignedAgents"],
        agency["assignedAgentDetails"],
      ),
      assignedAgentDetails: this.readAgencyAssignedUserSummaries(
        agency["assignedAgentDetails"],
        agency["assignedAgents"],
      ),
      assignedSecondaryAgents: this.readEntityIds(
        agency["assignedSecondaryAgents"],
        agency["assignedSecondaryAgentDetails"],
      ),
      assignedSecondaryAgentDetails: this.readAgencyAssignedUserSummaries(
        agency["assignedSecondaryAgentDetails"],
        agency["assignedSecondaryAgents"],
      ),
      assignedBtlUsers: this.readEntityIds(
        agency["assignedBtlUsers"],
        agency["assignedBtlUserDetails"],
      ),
      assignedBtlUserDetails: this.readAgencyAssignedUserSummaries(
        agency["assignedBtlUserDetails"],
        agency["assignedBtlUsers"],
      ),
      assignedSecondaryBtlUsers: this.readEntityIds(
        agency["assignedSecondaryBtlUsers"],
        agency["assignedSecondaryBtlUserDetails"],
      ),
      assignedSecondaryBtlUserDetails: this.readAgencyAssignedUserSummaries(
        agency["assignedSecondaryBtlUserDetails"],
        agency["assignedSecondaryBtlUsers"],
      ),
      assignedLogisticsUsers: this.readEntityIds(
        agency["assignedLogisticsUsers"],
        agency["assignedLogisticsUserDetails"],
      ),
      assignedLogisticsUserDetails: this.readAgencyAssignedUserSummaries(
        agency["assignedLogisticsUserDetails"],
        agency["assignedLogisticsUsers"],
      ),
      assignedSecondaryLogisticsUsers: this.readEntityIds(
        agency["assignedSecondaryLogisticsUsers"],
        agency["assignedSecondaryLogisticsUserDetails"],
      ),
      assignedSecondaryLogisticsUserDetails:
        this.readAgencyAssignedUserSummaries(
          agency["assignedSecondaryLogisticsUserDetails"],
          agency["assignedSecondaryLogisticsUsers"],
        ),
      assignedPos,
      assignedPosDetails,
    };
  }

  override async getUserById(id: string): Promise<ApiUserSummary | null> {
    const res = await firstValueFrom(
      this.http.get<unknown>(`${environment.apiBaseUrl}/auth/users/${id}`),
    );
    return this.extractSingleUser(res);
  }

  override async getAdminById(id: string): Promise<ApiUserSummary | null> {
    const res = await firstValueFrom(
      this.http.get<unknown>(`${environment.apiBaseUrl}/auth/admins/${id}`),
    );
    return this.extractSingleUser(res);
  }

  override async getUsers(
    params: UsersListParams = {},
  ): Promise<UsersListResult> {
    const {
      page = 1,
      limit = 10,
      search,
      accountStatus,
      approvalStatus,
    } = params;
    const httpParams: Record<string, string> = {
      page: String(page),
      limit: String(limit),
    };
    if (search) httpParams["search"] = search;
    if (accountStatus) httpParams["accountStatus"] = accountStatus;
    if (approvalStatus) httpParams["approvalStatus"] = approvalStatus;

    const res = await firstValueFrom(
      this.http.get<unknown>(`${environment.apiBaseUrl}/auth/admins`, {
        params: httpParams,
      }),
    );
    return this.capPagedResult(this.extractUsersList(res), page, limit);
  }

  override async getDashboardActiveSummary(
    query: { assignedAgencyId?: string } = {},
  ): Promise<DashboardActiveSummary> {
    const res = await firstValueFrom(
      this.http.get<unknown>(
        `${environment.apiBaseUrl}/auth/users/dashboard/active-summary${this.toQueryString(query)}`,
      ),
    );

    return this.extractDashboardActiveSummary(res);
  }

  override async getDashboardSummary(
    query: { agencyId?: string } = {},
  ): Promise<DashboardSummary> {
    const res = await firstValueFrom(
      this.http.get<unknown>(
        `${environment.apiBaseUrl}/aggregation/dashboard/summary${this.toQueryString(query)}`,
      ),
    );

    return this.extractDashboardSummary(res);
  }

  override async getSupervisorDashboardSummary(query: SupervisorDashboardSummaryQuery = {}): Promise<SupervisorDashboardSummary> {
    const res = await firstValueFrom(
      this.http.get<unknown>(
        `${environment.apiBaseUrl}/aggregation/dashboard/summary/supervisor${this.toQueryString(query)}`,
      ),
    );

    return this.extractSupervisorDashboardSummary(res);
  }

  override async exportDashboardPdf(
    query: { agencyId?: string } = {},
  ): Promise<DashboardPdfExport> {
    const response = await firstValueFrom(
      this.http.get<{ success?: boolean; data?: Partial<DashboardPdfExport> }>(
        `${environment.apiBaseUrl}/aggregation/dashboard/export-pdf${this.toQueryString(query)}`,
        { headers: { Accept: "application/json" } },
      ),
    );

    return {
      fileName: response.data?.fileName?.trim() || "dashboard.pdf",
      mimeType: response.data?.mimeType?.trim() || "application/pdf",
      base64: response.data?.base64?.trim() || "",
    };
  }

  override async exportSupervisorDashboardPdf(): Promise<DashboardPdfExport> {
    const response = await firstValueFrom(
      this.http.get<{ success?: boolean; data?: Partial<DashboardPdfExport> }>(
        `${environment.apiBaseUrl}/aggregation/dashboard/export-pdf/supervisor`,
        { headers: { Accept: "application/json" } },
      ),
    );

    return {
      fileName: response.data?.fileName?.trim() || "supervisor-dashboard.pdf",
      mimeType: response.data?.mimeType?.trim() || "application/pdf",
      base64: response.data?.base64?.trim() || "",
    };
  }

  /**
   * BUSINESS LOGIC: toPosApiPayload
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private toPosApiPayload(payload: PosUpsertPayload): Record<string, unknown> {
    return {
      ...payload,
      stockDetails: this.toPosStockDetailsApiPayload(payload.stockDetails),
      address: {
        ...payload.address,
        geo: {
          type: "Point",
          coordinates: payload.address.geo,
        },
      },
    } as Record<string, unknown>;
  }

  private toProspectApiPayload(
    payload: ProspectUpsertPayload,
  ): Record<string, unknown> {
    const { stockDetails: _stockDetails, ...rest } = payload;

    return {
      ...rest,
      stockDetails: this.toPosStockDetailsApiPayload(payload.stockDetails),
      address: {
        ...rest.address,
        geo: {
          type: "Point",
          coordinates: rest.address.geo,
        },
      },
    } as Record<string, unknown>;
  }

  /**
   * API NORMALIZATION: toCleanProspectApiPayload
   * Inputs: Accepts the prospect upsert payload already shaped by the form/service boundary.
   * Output: Returns a backend-ready payload while preserving explicit blank `posType`.
   * Processing: Uses the shared cleaner for ordinary empty fields, but keeps `posType: ""` because
   * Prospect POS Type is user-owned and must not be defaulted or silently removed when unselected.
   */
  private toCleanProspectApiPayload(
    payload: ProspectUpsertPayload,
  ): Partial<Record<string, unknown>> {
    const apiPayload = cleanPayload(this.toProspectApiPayload(payload));

    if (payload.posType === "") {
      return {
        ...apiPayload,
        posType: "",
      };
    }

    return apiPayload;
  }

  private toPosStockDetailsApiPayload(
    stockDetails: PosStockDetails,
  ): PosStockDetails {
    return {
      ...stockDetails,
      entries: (stockDetails.entries ?? []).map(
        (entry) =>
          ({
            id: entry.id,
            dateTime: entry.dateTime,
            agent: entry.agent,
            productAsset: entry.productAsset,
            quantity: entry.quantity,
            entryStatus: entry.entryStatus,
            remarks: entry.remarks,
            ...(entry.proofRef ? { proofRef: entry.proofRef } : {}),
            ...(entry.balanceAfter !== undefined
              ? { balanceAfter: entry.balanceAfter }
              : {}),
          }) as PosStockEntry,
      ),
    };
  }

  private normalizeAgentAgencies(
    value: unknown,
  ): { id: string; name: string; city?: string; area?: string }[] {
    const source =
      value && typeof value === "object"
        ? (value as Record<string, unknown>)
        : null;
    const assignedAgency = source
      ? this.readAssignedAgencyFromUser(source, "agent")
      : null;
    const rawAgencies = Array.isArray(value)
      ? value
      : Array.isArray(source?.["assignedAgencies"])
        ? (source["assignedAgencies"] as unknown[])
        : [];

    if (rawAgencies.length === 0) {
      return assignedAgency ? [assignedAgency] : [];
    }

    const agencies: AssignedAgencySummary[] = [];

    for (const item of rawAgencies) {
      const summary = this.readAssignedAgencySummary(item);
      if (!summary) {
        continue;
      }

      agencies.push(
        assignedAgency?.id === summary.id
          ? {
              ...summary,
              name: summary.name || assignedAgency.name,
              city: summary.city ?? assignedAgency.city,
              area: summary.area ?? assignedAgency.area,
            }
          : summary,
      );
    }

    return agencies.length > 0 ? agencies : assignedAgency ? [assignedAgency] : [];
  }

  /**
   * API NORMALIZATION:
   * Agent responses may return assigned POS as embedded rows or ID strings. Preserve detail
   * when present, but always keep a stable assignedPos array for counts and drawers.
   */
  private readAgentAssignedPos(
    value: unknown,
    primaryAgency?: { id: string; name: string },
  ): AgentRecord["assignedPos"] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value
      .map((item): AgentRecord["assignedPos"][number] | null => {
        if (typeof item === "string") {
          const id = item.trim();
          return id
            ? {
                id,
                name: id,
                agencyId: primaryAgency?.id ?? "",
                agencyName: primaryAgency?.name ?? "",
              }
            : null;
        }

        if (!item || typeof item !== "object") {
          return null;
        }

        const pos = item as Record<string, unknown>;
        const id = String(pos["_id"] ?? pos["id"] ?? pos["posId"] ?? "").trim();

        if (!id) {
          return null;
        }

        return {
          id,
          name: String(pos["name"] ?? pos["posName"] ?? ""),
          posNumber: String(pos["posNumber"] ?? ""),
          phone: String(pos["phone"] ?? pos["contactPhoneDisplay"] ?? ""),
          city: String(pos["city"] ?? ""),
          area: String(pos["area"] ?? ""),
          agencyId: String(pos["agencyId"] ?? primaryAgency?.id ?? ""),
          agencyName: String(pos["agencyName"] ?? primaryAgency?.name ?? ""),
        };
      })
      .filter((pos): pos is AgentRecord["assignedPos"][number] => pos !== null);
  }

  /**
   * BUSINESS LOGIC: readCount
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private readCount(...values: unknown[]): number {
    for (const value of values) {
      const count = Number(value);

      if (
        value !== undefined &&
        value !== null &&
        value !== "" &&
        Number.isFinite(count) &&
        count >= 0
      ) {
        return count;
      }
    }

    return 0;
  }

  /**
   * Extracts list items from the several collection wrappers currently returned by the backend.
   */
  private extractCollectionItems<T>(
    response: ApiCollectionResponse<T> | T[],
  ): T[] {
    if (Array.isArray(response)) {
      return response;
    }

    if (Array.isArray(response?.items)) {
      return response.items;
    }

    if (Array.isArray(response?.data)) {
      return response.data;
    }

    if (Array.isArray(response?.data?.items)) {
      return response.data.items;
    }

    return [];
  }

  /**
   * Normalizes list responses into one paged frontend contract even when the backend omits
   * wrapper metadata and returns a plain array.
   */
  private extractPagedCollection<T, U = T>(
    response: ApiCollectionResponse<T> | T[],
    normalize: (item: T) => U,
  ): PagedResult<U> {
    const items = this.extractCollectionItems(response).map(normalize);

    if (Array.isArray(response)) {
      return {
        items,
        total: items.length,
        page: 1,
        limit: items.length,
      };
    }

    return {
      items,
      total:
        response?.data && !Array.isArray(response.data)
          ? Number(response.data.total ?? items.length)
          : items.length,
      page:
        response?.data && !Array.isArray(response.data)
          ? Number(response.data.page ?? 1)
          : 1,
      limit:
        response?.data && !Array.isArray(response.data)
          ? Number(response.data.limit ?? items.length)
          : items.length,
    };
  }

  private capPagedResult<T>(
    result: PagedResult<T>,
    requestedPage?: number,
    requestedLimit?: number,
  ): PagedResult<T> {
    const page = Number(requestedPage ?? result.page ?? 1) || 1;
    const limit =
      Number(requestedLimit ?? result.limit ?? result.items.length) ||
      result.items.length;
    const total = Number(result.total ?? result.items.length);
    const shouldSlice =
      Number.isFinite(limit) && limit > 0 && result.items.length > limit;
    const items = shouldSlice
      ? result.items.slice((page - 1) * limit, page * limit)
      : result.items;

    return { ...result, items, page, limit, total };
  }

  private toPagedCatalogResult<T>(
    response: unknown,
    items: T[],
    requestedPage: number,
    requestedLimit: number,
  ): PagedResult<T> {
    const data =
      typeof response === "object" && response !== null
        ? (response as { data?: unknown }).data
        : null;
    const entity =
      data && typeof data === "object" && !Array.isArray(data)
        ? (data as Record<string, unknown>)
        : null;

    if (entity) {
      return this.capPagedResult(
        {
          items,
          total: Number(
            entity["total"] ?? entity["totalCount"] ?? items.length,
          ),
          page: Number(entity["page"] ?? requestedPage),
          limit: Number(entity["limit"] ?? requestedLimit),
        },
        requestedPage,
        requestedLimit,
      );
    }

    const start = (requestedPage - 1) * requestedLimit;
    return {
      items: items.slice(start, start + requestedLimit),
      total: items.length,
      page: requestedPage,
      limit: requestedLimit,
    };
  }

  private extractEntity<T>(response: ApiEntityResponse<T> | T): T | null {
    if (!response || typeof response !== "object") {
      return null;
    }

    if ("data" in response) {
      return response.data ?? null;
    }

    return response as T;
  }

  private requireEntity<T>(response: ApiEntityResponse<T> | T): T {
    const entity = this.extractEntity(response);

    if (!entity) {
      throw new Error("Entity response payload missing");
    }

    return entity;
  }

  /**
   * Reads one entity ID from either a scalar ID, nested object, Mongo `_id`, or auth-linked shape.
   * API NORMALIZATION: forms and shared selectors rely on receiving plain string IDs.
   */
  private readEntityId(value: unknown): string | null {
    if (typeof value === "string") {
      return value.trim() || null;
    }

    if (!value || typeof value !== "object") {
      return null;
    }

    const record = value as Record<string, unknown>;
    return this.readEntityId(
      record["id"] ??
        record["_id"] ??
        record["authUserId"] ??
        record["vehicleId"],
    );
  }

  /**
   * Reads assignment arrays defensively when APIs may send either IDs or expanded entity objects.
   */
  private readEntityIds(primary: unknown, fallback?: unknown): string[] {
    const source =
      Array.isArray(primary) && primary.length > 0
        ? primary
        : Array.isArray(fallback)
          ? fallback
          : [];
    return source
      .map((value) => this.readEntityId(value) ?? "")
      .filter(Boolean);
  }

  private readAgencyAssignedUserSummaries(
    primary: unknown,
    fallback?: unknown,
  ): AgencyAssignedUserSummary[] {
    const source =
      Array.isArray(primary) && primary.length > 0
        ? primary
        : Array.isArray(fallback)
          ? fallback
          : [];

    return source
      .map((value) => this.readAgencyAssignedUserSummary(value))
      .filter((value): value is AgencyAssignedUserSummary => value !== null);
  }

  private readAgencyAssignedPosSummaries(
    primary: unknown,
    fallback?: unknown,
  ): AgencyAssignedPosSummary[] {
    const source =
      Array.isArray(primary) && primary.length > 0
        ? primary
        : Array.isArray(fallback)
          ? fallback
          : [];

    return source
      .map((value) => this.readAgencyAssignedPosSummary(value))
      .filter((value): value is AgencyAssignedPosSummary => value !== null);
  }

  private readAgencyAssignedUserSummary(
    value: unknown,
  ): AgencyAssignedUserSummary | null {
    if (typeof value === "string") {
      const normalized = value.trim();
      return normalized ? { id: normalized, name: normalized } : null;
    }

    if (!value || typeof value !== "object") {
      return null;
    }

    const record = value as Record<string, unknown>;
    const id = this.readEntityId(record);

    if (!id) {
      return null;
    }

    const contact =
      (record["contact"] as Record<string, unknown> | undefined) ?? {};
    const phone =
      (contact["phone"] as Record<string, unknown> | undefined) ?? {};
    const firstName =
      typeof record["firstName"] === "string" ? record["firstName"].trim() : "";
    const lastName =
      typeof record["lastName"] === "string" ? record["lastName"].trim() : "";
    const fullName = [firstName, lastName].filter(Boolean).join(" ").trim();
    const name =
      this.toNullableString(record["name"]) ??
      this.toNullableString(record["fullName"]) ??
      (fullName || null) ??
      id;

    return {
      id,
      name,
      phone:
        this.toNullableString(record["phoneDisplay"]) ??
        this.toNullableString(record["phone"]) ??
        this.toNullableString(record["phoneNumber"]) ??
        this.toNullableString(phone["number"]) ??
        undefined,
      email:
        this.toNullableString(record["email"]) ??
        this.toNullableString(contact["email"]) ??
        undefined,
      status:
        this.toNullableString(record["status"]) ??
        this.toNullableString(record["accountStatus"]) ??
        undefined,
    };
  }

  private readAgencyAssignedPosSummary(
    value: unknown,
  ): AgencyAssignedPosSummary | null {
    if (typeof value === "string") {
      const normalized = value.trim();
      return normalized ? { id: normalized, name: normalized } : null;
    }

    if (!value || typeof value !== "object") {
      return null;
    }

    const record = value as Record<string, unknown>;
    const id = this.readEntityId(record);

    if (!id) {
      return null;
    }

    const assignedAgent =
      (record["assignedAgent"] as Record<string, unknown> | undefined) ?? {};

    return {
      id,
      name: this.toNullableString(record["name"]) ?? id,
      city: this.toNullableString(record["city"]) ?? undefined,
      area: this.toNullableString(record["area"]) ?? undefined,
      status:
        this.toNullableString(record["status"]) ??
        this.toNullableString(record["accountStatus"]) ??
        undefined,
      stockStatus: this.toNullableString(record["stockStatus"]) as
        | AgencyAssignedPosSummary["stockStatus"]
        | undefined,
      assignedAgentName:
        this.toNullableString(record["assignedAgentName"]) ??
        this.toNullableString(record["primaryAgent"]) ??
        this.toNullableString(assignedAgent["name"]) ??
        undefined,
      primaryAgent:
        this.toNullableString(record["primaryAgent"]) ??
        this.toNullableString(record["assignedAgentName"]) ??
        this.toNullableString(assignedAgent["name"]) ??
        undefined,
    };
  }

  /**
   * BUSINESS LOGIC: resolveMongoId
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private resolveMongoId(u: Record<string, unknown>): string {
    const isHex24 = (v: unknown): v is string =>
      typeof v === "string" && /^[0-9a-fA-F]{24}$/.test(v);
    const raw = u["_id"] ?? u["id"];
    return isHex24(raw) ? raw : String(raw ?? "").trim();
  }

  /**
   * BUSINESS LOGIC: toNullableString
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private toNullableString(value: unknown): string | null {
    if (typeof value !== "string") {
      return null;
    }

    const normalized = value.trim();
    return normalized ? normalized : null;
  }

  /**
   * BUSINESS LOGIC: humanizeTransferStatus
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private humanizeTransferStatus(value: string): string {
    return value
      .split("-")
      .map((part) => (part ? part[0].toUpperCase() + part.slice(1) : ""))
      .join(" ");
  }

  private normalizeCentralBankingNote(
    row: Record<string, unknown>,
  ): CentralBankingNoteRow {
    const createdBy =
      (row["createdBy"] as Record<string, unknown> | undefined) ?? {};
    const enteredBy =
      (row["enteredBy"] as Record<string, unknown> | undefined) ?? {};
    return {
      id: String(row["id"] ?? row["_id"] ?? ""),
      bankingNote: String(row["bankingNote"] ?? row["note"] ?? ""),
      date: String(row["date"] ?? row["createdAt"] ?? ""),
      enteredBy: String(
        createdBy["name"] ??
          enteredBy["name"] ??
          row["enteredByName"] ??
          row["createdByName"] ??
          "-",
      ),
      referenceNumber: String(
        row["referenceNumber"] ??
          row["referenceNo"] ??
          row["id"] ??
          row["_id"] ??
          "",
      ),
    };
  }

  private normalizeStockThresholdRow(
    row: Record<string, unknown>,
  ): StockThresholdRow {
    const category =
      (row["category"] as Record<string, unknown> | undefined) ?? {};
    const product =
      (row["product"] as Record<string, unknown> | undefined) ?? {};
    const reference =
      (row["reference"] as Record<string, unknown> | undefined) ??
      (row["agency"] as Record<string, unknown> | undefined) ??
      {};

    return {
      id: String(row["id"] ?? row["_id"] ?? ""),
      institutionType: String(row["institutionType"] ?? "agency"),
      referenceId: String(
        row["referenceId"] ?? reference["id"] ?? reference["_id"] ?? "",
      ),
      referenceName: String(
        row["referenceName"] ?? reference["name"] ?? row["agencyName"] ?? "-",
      ),
      categoryId: String(
        row["categoryId"] ?? category["id"] ?? category["_id"] ?? "",
      ),
      category: String(row["categoryName"] ?? category["name"] ?? "-"),
      productId: String(
        row["productId"] ?? product["id"] ?? product["_id"] ?? "",
      ),
      product: String(row["productName"] ?? product["name"] ?? "-"),
      quantity: Number(row["quantity"] ?? row["threshold"] ?? 0),
      updatedAt: String(row["updatedAt"] ?? row["createdAt"] ?? ""),
      createdAt: String(row["createdAt"] ?? row["updatedAt"] ?? ""),
    };
  }

  private normalizeStockEntries(
    entriesSource: unknown[] | null | undefined,
  ): PosStockEntry[] {
    return (entriesSource ?? []).map((entry) => {
      const row = entry as Record<string, unknown>;
      const category =
        (row["category"] as Record<string, unknown> | undefined) ?? {};
      const product =
        (row["product"] as Record<string, unknown> | undefined) ?? {};
      const variant =
        (row["variant"] as Record<string, unknown> | undefined) ?? {};
      const rawProductAsset = String(row["productAsset"] ?? "");
      const separator = " - ";
      const separatorIndex = rawProductAsset.indexOf(separator);
      const productName = String(
        row["productName"] ??
          product["name"] ??
          (separatorIndex >= 0
            ? rawProductAsset.slice(0, separatorIndex)
            : rawProductAsset),
      );
      const variantName = String(
        row["variantName"] ??
          variant["name"] ??
          (separatorIndex >= 0
            ? rawProductAsset.slice(separatorIndex + separator.length)
            : ""),
      );
      const productAsset =
        rawProductAsset ||
        [productName, variantName].filter(Boolean).join(separator);
      // API NORMALIZATION: keep the backend-provided category name on the entry so the POS/prospect
      // stock cards can label and group the category directly, instead of re-resolving the id against
      // the inventory catalog, which showed "N/A" after an edit redirect when the catalog lacked the
      // id and merged distinct categories into one bucket (issue #5/#7).
      const categoryName = String(
        row["categoryName"] ?? category["name"] ?? "",
      );

      return {
        id: String(row["id"] ?? row["_id"] ?? ""),
        dateTime: String(row["dateTime"] ?? row["createdAt"] ?? ""),
        agent: String(row["agent"] ?? row["createdByName"] ?? ""),
        productAsset,
        categoryId: String(
          row["categoryId"] ?? category["id"] ?? category["_id"] ?? "",
        ),
        categoryName,
        productId: String(
          row["productId"] ?? product["id"] ?? product["_id"] ?? "",
        ),
        variantId: String(
          row["variantId"] ?? variant["id"] ?? variant["_id"] ?? "",
        ),
        productName,
        variantName,
        isCashProduct: Boolean(
          product["isCashProduct"] ?? row["isCashProduct"] ?? false,
        ),
        sellingPrice:
          row["sellingPrice"] != null ? Number(row["sellingPrice"]) : null,
        quantity: Number(row["quantity"] ?? 0),
        entryStatus: String(row["entryStatus"] ?? "counted"),
        remarks: String(row["remarks"] ?? ""),
        proofRef: row["proofRef"] != null ? String(row["proofRef"]) : undefined,
        balanceAfter:
          row["balanceAfter"] != null ? Number(row["balanceAfter"]) : undefined,
      };
    });
  }

  private normalizeStockDetailsEntity(
    data: Record<string, unknown> | null | undefined,
  ): PosStockDetails {
    const stockDetails = ((data?.["stockDetails"] as
      | Record<string, unknown>
      | undefined) ?? data) as Partial<PosStockDetails> | null | undefined;
    return this.normalizeStockDetails(stockDetails);
  }

  private normalizeStockTransactionsEntity(
    data: Record<string, unknown> | null | undefined,
  ): PosStockEntry[] {
    const transactions = Array.isArray(data?.["transactions"])
      ? data?.["transactions"]
      : [];
    return this.normalizeStockEntries(transactions);
  }

  private normalizeStockDetails(
    data: Partial<PosStockDetails> | null | undefined,
  ): PosStockDetails {
    const entries = this.normalizeStockEntries(
      Array.isArray(data?.entries) ? data.entries : [],
    );

    return {
      stockOnHand: Number(data?.stockOnHand ?? 0),
      reorderLevel: Number(data?.reorderLevel ?? 0),
      stockStatus: this.normalizePosStockStatus(data?.stockStatus),
      lastStockUpdate: String(
        data?.lastStockUpdate ?? entries[0]?.dateTime?.slice(0, 10) ?? "",
      ),
      notes: String(data?.notes ?? ""),
      entries,
    };
  }

  private normalizePosStockStatus(
    value: unknown,
  ): PosStockDetails["stockStatus"] {
    return value === "low" || value === "out-of-stock" || value === "ok"
      ? value
      : "ok";
  }

  /**
   * BUSINESS LOGIC: normalizePosRecord
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private normalizePosRecord(record: PosRecord): PosRecord {
    const rawRecord = record as unknown as Record<string, unknown>;
    const primaryAgencySource =
      rawRecord["primaryAgency"] ?? rawRecord["agency"];
    const entries = (record.stockDetails?.entries ?? []).map((entry) => {
      const raw = entry as unknown as Record<string, unknown>;
      const productAsset = String(raw["productAsset"] ?? "");
      const separatorIndex = productAsset.indexOf(" — ");
      const productName = String(
        raw["productName"] ??
          (separatorIndex >= 0
            ? productAsset.slice(0, separatorIndex)
            : productAsset),
      );
      const variantName = String(
        raw["variantName"] ??
          (separatorIndex >= 0 ? productAsset.slice(separatorIndex + 3) : ""),
      );
      return {
        id: String(raw["id"] ?? ""),
        dateTime: String(raw["dateTime"] ?? ""),
        agent: String(raw["agent"] ?? ""),
        productAsset,
        categoryId: String(raw["categoryId"] ?? ""),
        categoryName: String(
          raw["categoryName"] ??
            (raw["category"] as Record<string, unknown> | undefined)?.[
              "name"
            ] ??
            "",
        ),
        productId: String(raw["productId"] ?? ""),
        variantId: String(raw["variantId"] ?? ""),
        productName,
        variantName,
        sellingPrice:
          raw["sellingPrice"] != null ? Number(raw["sellingPrice"]) : null,
        quantity: Number(raw["quantity"] ?? 0),
        entryStatus: String(raw["entryStatus"] ?? "counted"),
        remarks: String(raw["remarks"] ?? ""),
        proofRef: raw["proofRef"] != null ? String(raw["proofRef"]) : undefined,
        balanceAfter:
          raw["balanceAfter"] != null ? Number(raw["balanceAfter"]) : undefined,
      };
    });

    return {
      ...record,
      primaryAgencyId:
        this.readEntityId(
          rawRecord["primaryAgencyId"] ?? primaryAgencySource,
        ) ?? record.primaryAgencyId,
      primaryAgency:
        this.toNullableString(rawRecord["primaryAgencyName"]) ||
        this.readEntityName(primaryAgencySource) ||
        record.primaryAgency,
      address: this.normalizeAddress(record.address),
      stockDetails: {
        ...record.stockDetails,
        entries,
      },
    };
  }

  /**
   * BUSINESS LOGIC: normalizeProspectRecord
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private normalizeProspectRecord(record: ProspectRecord): ProspectRecord {
    const raw = record as unknown as Record<string, unknown>;
    const entries = (record.stockDetails?.entries ?? []).map((entry) => {
      const raw = entry as unknown as Record<string, unknown>;
      const productAsset = String(raw["productAsset"] ?? "");
      const separatorIndex = productAsset.indexOf(" — ");
      const productName = String(
        raw["productName"] ??
          (separatorIndex >= 0
            ? productAsset.slice(0, separatorIndex)
            : productAsset),
      );
      const variantName = String(
        raw["variantName"] ??
          (separatorIndex >= 0 ? productAsset.slice(separatorIndex + 3) : ""),
      );
      return {
        id: String(raw["id"] ?? ""),
        dateTime: String(raw["dateTime"] ?? ""),
        agent: String(raw["agent"] ?? ""),
        productAsset,
        categoryId: String(raw["categoryId"] ?? ""),
        categoryName: String(
          raw["categoryName"] ??
            (raw["category"] as Record<string, unknown> | undefined)?.[
              "name"
            ] ??
            "",
        ),
        productId: String(raw["productId"] ?? ""),
        variantId: String(raw["variantId"] ?? ""),
        productName,
        variantName,
        sellingPrice:
          raw["sellingPrice"] != null ? Number(raw["sellingPrice"]) : null,
        quantity: Number(raw["quantity"] ?? 0),
        entryStatus: String(raw["entryStatus"] ?? "counted"),
        remarks: String(raw["remarks"] ?? ""),
        proofRef: raw["proofRef"] != null ? String(raw["proofRef"]) : undefined,
        balanceAfter:
          raw["balanceAfter"] != null ? Number(raw["balanceAfter"]) : undefined,
      };
    });

    return {
      ...record,
      id: String(raw["_id"] ?? raw["id"] ?? ""),
      address: this.normalizeAddress(record.address),
      stockDetails: {
        ...(record.stockDetails ?? {
          stockOnHand: 0,
          reorderLevel: 0,
          stockStatus: "ok",
          lastStockUpdate: "",
          notes: "",
        }),
        entries,
      },
    };
  }

  private normalizeAddress(
    address: PosRecord["address"] | ProspectRecord["address"],
  ): PosRecord["address"] {
    const rawGeo = address?.geo;
    const geoObject = rawGeo as { coordinates?: unknown } | null;
    const coordinates = Array.isArray(rawGeo)
      ? rawGeo
      : geoObject && Array.isArray(geoObject.coordinates)
        ? geoObject.coordinates
        : [0, 0];

    return {
      ...address,
      geo: [Number(coordinates[0]) || 0, Number(coordinates[1]) || 0],
    };
  }

  private normalizeBtlCampaignRecord(
    row: Record<string, unknown>,
  ): BtlCampaignApiRecord {
    const address = ((row["address"] as Record<string, unknown> | undefined) ??
      {}) as Record<string, unknown>;
    const geoRaw = address["geo"] as
      | { coordinates?: unknown }
      | number[]
      | undefined;
    const coordinates = Array.isArray(geoRaw)
      ? geoRaw
      : geoRaw && Array.isArray(geoRaw.coordinates)
        ? geoRaw.coordinates
        : [0, 0];
    const items = this.normalizeBtlCampaignItems(row["items"]);
    const assignedBtlUserIds = this.readEntityIds(
      row["assignedBtlUserIds"],
      row["assignedUsers"] ??
        row["assignedBtlUsers"] ??
        row["assignedBtlUserDetails"],
    );

    return {
      id: String(row["id"] ?? row["_id"] ?? ""),
      campaignName: String(row["campaignName"] ?? ""),
      campaignType: String(row["campaignType"] ?? ""),
      agencyId: this.readEntityId(row["agencyId"]) ?? undefined,
      agency: String(row["agency"] ?? ""),
      purpose: String(row["purpose"] ?? ""),
      address: {
        formattedAddress: String(address["formattedAddress"] ?? ""),
        streetAddress: String(address["streetAddress"] ?? ""),
        locality: String(address["locality"] ?? ""),
        subLocality: String(address["subLocality"] ?? ""),
        region: String(address["region"] ?? ""),
        country: String(address["country"] ?? ""),
        postalCode: String(address["postalCode"] ?? ""),
        area: String(address["area"] ?? ""),
        geo: {
          type: "Point",
          coordinates: [
            Number(coordinates[0]) || 0,
            Number(coordinates[1]) || 0,
          ],
        },
      },
      durationDays: Number(row["durationDays"] ?? 0),
      startDate: String(row["startDate"] ?? ""),
      endDate: String(row["endDate"] ?? ""),
      targetStockToSell: Number(row["targetStockToSell"] ?? 0),
      targetPeopleToOnboard: Number(row["targetPeopleToOnboard"] ?? 0),
      checklistTargets: Array.isArray(row["checklistTargets"])
        ? (row["checklistTargets"] as unknown[])
            .map((value) => String(value ?? ""))
            .filter(Boolean)
        : [],
      assignedBtlUserIds,
      items,
      status: String(row["status"] ?? "upcoming"),
      createdAt: String(row["createdAt"] ?? ""),
      updatedAt: String(row["updatedAt"] ?? ""),
    };
  }

  private normalizeTargetPerformanceAnalytics(
    data: Record<string, unknown>,
    query: TargetPerformanceAnalyticsQuery
  ): AgentTargetAnalyticsResult {
    const rawSections = Array.isArray(data['sections']) ? data['sections'] as Record<string, unknown>[] : [];

    return {
      sections: rawSections.map((section) => this.normalizeTargetAnalyticsSection(section)),
      page: Number(data['page'] ?? query.page ?? 1),
      limit: Number(data['limit'] ?? query.limit ?? 20),
      total: Number(data['total'] ?? 0)
    };
  }

  /**
   * BUSINESS LOGIC: normalizeTargetAnalyticsSection
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private normalizeTargetAnalyticsSection(section: Record<string, unknown>): AgentTargetAnalyticsSection {
    const rows = Array.isArray(section['rows']) ? section['rows'] as Record<string, unknown>[] : [];
    const targetType = String(section['targetType'] ?? 'stock') as AgentTargetAnalyticsSection['targetType'];

    return {
      targetType,
      title: String(section['title'] ?? targetType),
      sourceStatus: String(section['sourceStatus'] ?? 'not-started'),
      rows: rows.map((row) => this.normalizeTargetAnalyticsRow(row, targetType)),
      total: this.normalizeTargetAnalyticsMetrics(section['total'] as Record<string, unknown> | undefined)
    };
  }

  /**
   * BUSINESS LOGIC: normalizeTargetAnalyticsRow
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private normalizeTargetAnalyticsRow(row: Record<string, unknown>, fallbackType: AgentTargetAnalyticsSection['targetType']): AgentTargetAnalyticsRow {
    return {
      ...this.normalizeTargetAnalyticsMetrics(row),
      id: this.readEntityId(row) ?? String(row['targetId'] ?? ''),
      userType: String(row['userType'] ?? 'AGENT'),
      userId: this.readEntityId(row['userId'] ?? row['targetUserId']) ?? '',
      agencyId: this.readEntityId(row['agencyId']) ?? '',
      targetType: String(row['targetType'] ?? fallbackType) as AgentTargetAnalyticsRow['targetType'],
      categoryId: this.readEntityId(row['categoryId']) ?? null,
      productId: this.readEntityId(row['productId']) ?? null,
      sourceStatus: String(row['sourceStatus'] ?? 'not-started'),
      dailyTarget: Number(row['dailyTarget'] ?? 0),
      weeklyTarget: Number(row['weeklyTarget'] ?? 0),
      monthlyTarget: Number(row['monthlyTarget'] ?? 0),
      notes: String(row['notes'] ?? ''),
      createdAt: String(row['createdAt'] ?? ''),
      updatedAt: String(row['updatedAt'] ?? ''),
      userName: String(row['userName'] ?? row['targetUserName'] ?? row['agentName'] ?? ''),
      agencyName: String(row['agencyName'] ?? ''),
      categoryName: String(row['categoryName'] ?? ''),
      productName: String(row['productName'] ?? '')
    };
  }

  /**
   * BUSINESS LOGIC: normalizeTargetAnalyticsMetrics
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private normalizeTargetAnalyticsMetrics(row: Record<string, unknown> | undefined): AgentTargetAnalyticsMetrics {
    const source = row ?? {};
    return {
      todayAchievement: Number(source['todayAchievement'] ?? 0),
      previousDay: Number(source['previousDay'] ?? 0),
      dodProgress: this.toNullableNumber(source['dodProgress']),
      actualWeek: Number(source['actualWeek'] ?? 0),
      previousWeek: Number(source['previousWeek'] ?? 0),
      wowProgress: this.toNullableNumber(source['wowProgress']),
      mtdActual: Number(source['mtdActual'] ?? 0),
      mtdPrevious: Number(source['mtdPrevious'] ?? 0),
      momProgress: this.toNullableNumber(source['momProgress']),
      mtdTarget: Number(source['mtdTarget'] ?? 0),
      actualVsMtdTarget: this.toNullableNumber(source['actualVsMtdTarget']),
      gapMtdActualVsMtdTarget: Number(source['gapMtdActualVsMtdTarget'] ?? 0)
    };
  }

  /**
   * BUSINESS LOGIC: toNullableNumber
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private toNullableNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
  }

  /**
   * BUSINESS LOGIC: normalizeAgentTargetRecord
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private normalizeAgentTargetRecord(row: Record<string, unknown>): AgentTargetRecord {
    const user = (row['targetUser'] ?? row['user'] ?? row['assignedUser']) as Record<string, unknown> | undefined;
    const agency = (row['agency'] ?? row['assignedAgency']) as Record<string, unknown> | undefined;
    const rawSummary = Array.isArray(row['targetSummary']) ? row['targetSummary'] as Record<string, unknown>[] : [];
    const firstSummary = rawSummary[0];
    const category = (firstSummary?.['category'] ?? row['category']) as Record<string, unknown> | undefined;
    const product = (firstSummary?.['product'] ?? row['product']) as Record<string, unknown> | undefined;
    const progress = row['progress'] as Record<string, unknown> | undefined;
    const dailyTarget = Number(firstSummary?.['dailyTarget'] ?? row['dailyTarget'] ?? row['dailyTargets'] ?? row['daily'] ?? 0);
    const weeklyTarget = Number(firstSummary?.['weeklyTarget'] ?? row['weeklyTarget'] ?? row['weeklyTargets'] ?? row['weekly'] ?? 0);
    const monthlyTarget = Number(firstSummary?.['monthlyTarget'] ?? row['monthlyTarget'] ?? row['monthlyTargets'] ?? row['monthly'] ?? 0);
    const targetTotal = row['targetValue'] ?? row['target'] ?? row['quantity'] ?? progress?.['target'];
    const targetValue = Number(targetTotal ?? (monthlyTarget || weeklyTarget || dailyTarget || 0));
    const achieved = Number(progress?.['achieved'] ?? row['achieved'] ?? row['currentValue'] ?? 0);
    const percent = Number(progress?.['percent'] ?? row['progressPercent'] ?? (targetValue > 0 ? Math.round((achieved / targetValue) * 100) : 0));
    const status = String(progress?.['status'] ?? row['progressStatus'] ?? row['status'] ?? 'not-started') as AgentTargetRecord['status'];
    const targetSummary = rawSummary.map((summary, index) => {
      const summaryCategory = summary['category'] as Record<string, unknown> | undefined;
      const summaryProduct = summary['product'] as Record<string, unknown> | undefined;
      return {
        id: this.readEntityId(summary) ?? `${String(summary['targetType'] ?? summary['type'] ?? 'target')}-${index}`,
        targetType: String(summary['targetType'] ?? summary['type'] ?? 'stock') as AgentTargetRecord['type'],
        categoryId: this.readEntityId(summary['categoryId'] ?? summaryCategory) ?? null,
        categoryName: String(summary['categoryName'] ?? summaryCategory?.['name'] ?? ''),
        productId: this.readEntityId(summary['productId'] ?? summaryProduct) ?? null,
        productName: String(summary['productName'] ?? summaryProduct?.['name'] ?? ''),
        dailyTarget: Number(summary['dailyTarget'] ?? 0),
        weeklyTarget: Number(summary['weeklyTarget'] ?? 0),
        monthlyTarget: Number(summary['monthlyTarget'] ?? 0)
      };
    });

    return {
      id: this.readEntityId(row) ?? String(row['targetId'] ?? ''),
      targetUserId: this.readEntityId(row['targetUserId'] ?? row['userId'] ?? row['assignedToAuthUserId'] ?? user) ?? '',
      targetUserName: String(row['targetUserName'] ?? row['userName'] ?? user?.['fullName'] ?? user?.['name'] ?? ''),
      userBucket: 'AGENT',
      agencyId: this.readEntityId(row['agencyId'] ?? agency) ?? '',
      agencyName: String(row['agencyName'] ?? agency?.['name'] ?? agency?.['agencyName'] ?? ''),
      type: String(firstSummary?.['targetType'] ?? row['type'] ?? row['targetType'] ?? 'stock') as AgentTargetRecord['type'],
      period: String(row['period'] ?? row['progressPeriod'] ?? 'monthly') as AgentTargetRecord['period'],
      targetValue,
      dailyTarget,
      weeklyTarget,
      monthlyTarget,
      categoryId: this.readEntityId(firstSummary?.['categoryId'] ?? row['categoryId'] ?? category) ?? undefined,
      categoryName: String(firstSummary?.['categoryName'] ?? row['categoryName'] ?? category?.['name'] ?? ''),
      productId: this.readEntityId(firstSummary?.['productId'] ?? row['productId'] ?? product) ?? undefined,
      productName: String(firstSummary?.['productName'] ?? row['productName'] ?? product?.['name'] ?? ''),
      targetSummary,
      notes: String(row['notes'] ?? row['note'] ?? ''),
      startDate: String(row['startDate'] ?? row['startsAt'] ?? ''),
      endDate: String(row['endDate'] ?? row['endsAt'] ?? ''),
      progress: {
        achieved,
        target: Number(progress?.['target'] ?? targetValue),
        percent,
        status
      },
      status,
      createdAt: String(row['createdAt'] ?? ''),
      updatedAt: String(row['updatedAt'] ?? '')
    };
  }

  /**
   * BUSINESS LOGIC: toTargetPayload
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private toTargetPayload(payload: AgentTargetPayload): Record<string, unknown> {
    return cleanPayload({
      targetUserId: payload.targetUserId,
      userId: payload.targetUserId,
      assignedToAuthUserId: payload.targetUserId,
      userBucket: payload.userBucket,
      userType: payload.userBucket,
      ownerType: payload.userBucket,
      agencyId: payload.agencyId,
      type: payload.type,
      targetType: payload.type,
      dailyTarget: payload.dailyTarget,
      weeklyTarget: payload.weeklyTarget,
      monthlyTarget: payload.monthlyTarget,
      categoryId: payload.categoryId,
      productId: payload.productId,
      targetSummary: payload.targetSummary,
      notes: payload.notes
    });
  }

  /**
   * API NORMALIZATION:
   * BTL campaign detail responses may send target items either as already-flattened rows
   * or grouped category -> product -> variant objects. Edit-mode forms need one stable flat shape.
   */
  private normalizeBtlCampaignItems(
    rawItems: unknown,
  ): BtlCampaignApiRecord["items"] {
    if (!Array.isArray(rawItems)) {
      return [];
    }

    const flattenedItems: BtlCampaignApiRecord["items"] = [];

    rawItems
      .filter(
        (item): item is Record<string, unknown> =>
          typeof item === "object" && item !== null,
      )
      .forEach((item) => {
        if (
          "categoryId" in item ||
          "productId" in item ||
          "variantId" in item
        ) {
          flattenedItems.push({
            categoryId: String(item["categoryId"] ?? ""),
            categoryName: String(item["categoryName"] ?? ""),
            productId: String(item["productId"] ?? ""),
            productName: String(item["productName"] ?? ""),
            variantId: String(item["variantId"] ?? ""),
            variantName: String(item["variantName"] ?? ""),
            productKind: String(item["productKind"] ?? ""),
            unit: String(item["unit"] ?? ""),
            quantity: Number(item["quantity"] ?? 0),
          });
          return;
        }

        const category =
          (item["category"] as Record<string, unknown> | undefined) ?? {};
        const products = Array.isArray(item["products"])
          ? item["products"]
          : [];

        products
          .filter(
            (product): product is Record<string, unknown> =>
              typeof product === "object" && product !== null,
          )
          .forEach((product) => {
            const productRecord =
              (product["product"] as Record<string, unknown> | undefined) ?? {};
            const variants = Array.isArray(product["variants"])
              ? product["variants"]
              : [];

            variants
              .filter(
                (variant): variant is Record<string, unknown> =>
                  typeof variant === "object" && variant !== null,
              )
              .forEach((variant) => {
                const variantRecord =
                  (variant["variant"] as Record<string, unknown> | undefined) ??
                  {};

                flattenedItems.push({
                  categoryId:
                    this.readEntityId(category) ??
                    String(item["categoryId"] ?? ""),
                  categoryName: String(
                    category["name"] ?? item["categoryName"] ?? "",
                  ),
                  productId:
                    this.readEntityId(productRecord) ??
                    String(product["productId"] ?? ""),
                  productName: String(
                    productRecord["name"] ?? product["productName"] ?? "",
                  ),
                  variantId:
                    this.readEntityId(variantRecord) ??
                    String(variant["variantId"] ?? ""),
                  variantName: String(
                    variantRecord["name"] ?? variant["variantName"] ?? "",
                  ),
                  productKind: String(variant["productKind"] ?? ""),
                  unit: String(variant["unit"] ?? ""),
                  quantity: Number(variant["quantity"] ?? 0),
                });
              });
          });
      });

    return flattenedItems;
  }

  /**
   * BUSINESS LOGIC: toQueryString
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private toQueryString(query: object): string {
    const params = new URLSearchParams();

    for (const [key, value] of Object.entries(
      query as Record<string, unknown>,
    )) {
      if (value === undefined || value === null) {
        continue;
      }

      const normalized = String(value).trim();

      if (!normalized) {
        continue;
      }

      params.set(key, normalized);
    }

    const result = params.toString();
    return result ? `?${result}` : "";
  }

  override async createUser(
    payload: CreateUserPayload,
  ): Promise<ApiUserSummary> {
    const res = await firstValueFrom(
      this.http.post<unknown>(
        `${environment.apiBaseUrl}/auth/users`,
        cleanPayload(payload as unknown as Record<string, unknown>),
      ),
    );
    return this.extractSingleUser(res) as ApiUserSummary;
  }

  override async createAdminUser(
    payload: CreateUserPayload,
  ): Promise<ApiUserSummary> {
    const res = await firstValueFrom(
      this.http.post<unknown>(
        `${environment.apiBaseUrl}/auth/admins`,
        cleanPayload(payload as unknown as Record<string, unknown>),
      ),
    );
    return this.extractSingleUser(res) as ApiUserSummary;
  }

  override async createAgent(
    payload: CreateUserPayload,
  ): Promise<ApiUserSummary> {
    const res = await firstValueFrom(
      this.http.post<unknown>(
        `${environment.apiBaseUrl}/auth/agents`,
        cleanPayload(payload as unknown as Record<string, unknown>),
      ),
    );
    return this.extractSingleUser(res) as ApiUserSummary;
  }

  override async createBtlUser(
    payload: CreateUserPayload,
  ): Promise<ApiUserSummary> {
    const res = await firstValueFrom(
      this.http.post<unknown>(
        `${environment.apiBaseUrl}/auth/btl-users`,
        cleanPayload(payload as unknown as Record<string, unknown>),
      ),
    );
    return this.extractSingleUser(res) as ApiUserSummary;
  }

  override async createLogisticsUser(
    payload: CreateUserPayload,
  ): Promise<ApiUserSummary> {
    const res = await firstValueFrom(
      this.http.post<unknown>(
        `${environment.apiBaseUrl}/auth/logistics-users`,
        cleanPayload(payload as unknown as Record<string, unknown>),
      ),
    );
    return this.extractSingleUser(res) as ApiUserSummary;
  }

  override async updateLogisticsUser(
    id: string,
    payload: UpdateUserProfilePayload,
  ): Promise<ApiUserSummary> {
    const res = await firstValueFrom(
      this.http.patch<unknown>(
        `${environment.apiBaseUrl}/auth/logistics-users/${id}`,
        cleanPayload(payload as unknown as Record<string, unknown>),
      ),
    );
    return this.extractSingleUser(res) as ApiUserSummary;
  }

  override async updateUserProfile(
    id: string,
    payload: UpdateUserProfilePayload,
  ): Promise<ApiUserSummary> {
    const res = await firstValueFrom(
      this.http.patch<unknown>(
        `${environment.apiBaseUrl}/auth/users/${id}`,
        cleanPayload(payload as unknown as Record<string, unknown>),
      ),
    );
    return this.extractSingleUser(res) as ApiUserSummary;
  }

  override async updateAdminUser(
    id: string,
    payload: UpdateUserProfilePayload,
  ): Promise<ApiUserSummary> {
    const res = await firstValueFrom(
      this.http.patch<unknown>(
        `${environment.apiBaseUrl}/auth/admins/${id}`,
        cleanPayload(payload as unknown as Record<string, unknown>),
      ),
    );
    return this.extractSingleUser(res) as ApiUserSummary;
  }

  override async updateAgent(
    id: string,
    payload: UpdateUserProfilePayload,
  ): Promise<ApiUserSummary> {
    const res = await firstValueFrom(
      this.http.patch<unknown>(
        `${environment.apiBaseUrl}/auth/agents/${id}`,
        cleanPayload(payload as unknown as Record<string, unknown>),
      ),
    );
    return this.extractSingleUser(res) as ApiUserSummary;
  }

  override deleteAgent(ids: string[]): Promise<void> {
    return firstValueFrom(
      this.http.delete<void>(`${environment.apiBaseUrl}/auth/agents`, {
        body: { ids },
      }),
    );
  }

  override deleteAgency(id: string): Promise<void> {
    return firstValueFrom(
      this.http.delete<void>(
        `${environment.apiBaseUrl}/aggregation/agencies/${id}`,
      ),
    );
  }

  override changeAgentPassword(id: string, newPassword: string): Promise<void> {
    return firstValueFrom(
      this.http.patch<void>(
        `${environment.apiBaseUrl}/auth/agents/${id}/password`,
        { newPassword },
      ),
    );
  }

  override changeLogisticsPassword(
    id: string,
    newPassword: string,
  ): Promise<void> {
    return firstValueFrom(
      this.http.patch<void>(
        `${environment.apiBaseUrl}/auth/logistics-users/${id}/password`,
        { newPassword },
      ),
    );
  }

  override changeBtlPassword(id: string, newPassword: string): Promise<void> {
    return firstValueFrom(
      this.http.patch<void>(
        `${environment.apiBaseUrl}/auth/btl-users/${id}/password`,
        { newPassword },
      ),
    );
  }

  override changeAdminPassword(id: string, newPassword: string): Promise<void> {
    return firstValueFrom(
      this.http.patch<void>(
        `${environment.apiBaseUrl}/auth/admins/${id}/password`,
        { newPassword },
      ),
    );
  }

  override changeSupervisorPassword(
    id: string,
    newPassword: string,
  ): Promise<void> {
    return firstValueFrom(
      this.http.patch<void>(
        `${environment.apiBaseUrl}/auth/supervisors/${id}/password`,
        { newPassword },
      ),
    );
  }

  override async updateUserStatus(
    id: string,
    payload: UpdateUserStatusPayload,
  ): Promise<ApiUserSummary> {
    const res = await firstValueFrom(
      this.http.patch<unknown>(
        `${environment.apiBaseUrl}/auth/users/${id}/status`,
        cleanPayload(payload as unknown as Record<string, unknown>),
      ),
    );
    return this.extractSingleUser(res) as ApiUserSummary;
  }

  override async checkUserEmailExists(
    email: string,
  ): Promise<{ exists: boolean; message: string }> {
    const res = await firstValueFrom(
      this.http.get<unknown>(
        `${environment.apiBaseUrl}/auth/users/email-exists/${encodeURIComponent(email.trim().toLowerCase())}`,
      ),
    );
    const data = (res as { data?: unknown })?.data as
      | { exists?: unknown; message?: unknown }
      | undefined;

    return {
      exists: Boolean(data?.exists),
      message: typeof data?.message === "string" ? data.message : "",
    };
  }

  /**
   * BUSINESS LOGIC: extractUsersList
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private extractUsersList(res: unknown): UsersListResult {
    if (Array.isArray(res)) {
      return {
        items: res as ApiUserSummary[],
        total: res.length,
        page: 1,
        limit: res.length,
      };
    }

    const wrapper = res as { data?: unknown };
    const inner = wrapper?.data ?? res;

    if (Array.isArray(inner)) {
      return {
        items: inner as ApiUserSummary[],
        total: inner.length,
        page: 1,
        limit: inner.length,
      };
    }

    const paged = inner as {
      items?: ApiUserSummary[];
      data?: ApiUserSummary[];
      total?: number;
      page?: number;
      limit?: number;
    };
    const items = paged?.items ?? paged?.data ?? [];

    return {
      items,
      total: paged?.total ?? items.length,
      page: paged?.page ?? 1,
      limit: paged?.limit ?? items.length,
    };
  }

  /**
   * BUSINESS LOGIC: extractSingleUser
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private extractSingleUser(res: unknown): ApiUserSummary | null {
    if (!res) return null;
    if ((res as ApiUserSummary)?.id) return res as ApiUserSummary;
    const data = (res as { data?: ApiUserSummary })?.data;
    return data ?? null;
  }

  /**
   * BUSINESS LOGIC: extractDashboardActiveSummary
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private extractDashboardActiveSummary(res: unknown): DashboardActiveSummary {
    const entity = this.extractEntity(res as { data?: unknown }) ?? res;
    const candidates = this.collectDashboardSummaryCandidates(entity);

    return {
      activeAgents: this.readDashboardSummaryCount(candidates, [
        "activeAgents",
        "activeAgentCount",
        "agentCount",
        "agents",
      ]),
      btlUsers: this.readDashboardSummaryCount(candidates, [
        "btl",
        "btlUsers",
        "btlUserCount",
        "btlCount",
      ]),
      logisticsUsers: this.readDashboardSummaryCount(candidates, [
        "logistics",
        "logisticsUsers",
        "logisticsUserCount",
        "logisticsCount",
      ]),
      supervisors: this.readDashboardSummaryCount(candidates, [
        "supervisors",
        "supervisorCount",
      ]),
    };
  }

  /**
   * BUSINESS LOGIC: extractDashboardSummary
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private extractDashboardSummary(res: unknown): DashboardSummary {
    const entity = this.extractEntity(res as { data?: unknown }) ?? res;
    const root =
      entity && typeof entity === "object"
        ? (entity as Record<string, unknown>)
        : {};
    const centralInventory = Array.isArray(root["centralInventory"])
      ? (root["centralInventory"] as unknown[])
      : [];

    return {
      activePos: this.readDashboardValueObject(root["activePos"]),
      activeProspects: this.readDashboardValueObject(root["activeProspects"]),
      activeAgencies: this.readDashboardValueObject(root["activeAgencies"]),
      openAgentTasks: this.readDashboardValueObject(root["openAgentTasks"]),
      openBtlCampaigns: this.readDashboardValueObject(root["openBtlCampaigns"]),
      openLogisticsTasks: this.readDashboardValueObject(
        root["openLogisticsTasks"],
      ),
      totalStockCount: this.readDashboardValueObject(root["totalStockCount"]),
      centralInventory: centralInventory.map((item, index) =>
        this.normalizeDashboardCentralInventoryItem(item, index),
      ),
    };
  }

  private extractSupervisorDashboardSummary(
    res: unknown,
  ): SupervisorDashboardSummary {
    const entity = this.extractEntity(res as { data?: unknown }) ?? res;
    const root =
      entity && typeof entity === "object"
        ? (entity as Record<string, unknown>)
        : {};
    const agencyInventory = Array.isArray(root["agencyInventory"])
      ? (root["agencyInventory"] as unknown[])
      : [];
    const workforce =
      root["supervisorWorkforce"] &&
      typeof root["supervisorWorkforce"] === "object"
        ? (root["supervisorWorkforce"] as Record<string, unknown>)
        : {};
    const workforceAgency =
      workforce["agency"] && typeof workforce["agency"] === "object"
        ? (workforce["agency"] as Record<string, unknown>)
        : null;
    const pendingWork =
      root["supervisorPendingWork"] &&
      typeof root["supervisorPendingWork"] === "object"
        ? (root["supervisorPendingWork"] as Record<string, unknown>)
        : {};
    const pendingApprovals =
      root["supervisorPendingApprovals"] &&
      typeof root["supervisorPendingApprovals"] === "object"
        ? (root["supervisorPendingApprovals"] as Record<string, unknown>)
        : {};
    const agentPerformance =
      root["agentPerformance"] && typeof root["agentPerformance"] === "object"
        ? this.normalizeDashboardAgentPerformance(root["agentPerformance"])
        : undefined;

    return {
      activePos: this.readDashboardValueObject(root["activePos"]),
      activeProspects: this.readDashboardValueObject(root["activeProspects"]),
      openAgentTasks: this.readDashboardValueObject(root["openAgentTasks"]),
      openBtlCampaigns: this.readDashboardValueObject(root["openBtlCampaigns"]),
      openLogisticsTasks: this.readDashboardValueObject(
        root["openLogisticsTasks"],
      ),
      agencyInventory: agencyInventory.map((item, index) =>
        this.normalizeDashboardAgencyInventoryItem(item, index),
      ),
      supervisorWorkforce: {
        agency: workforceAgency
          ? {
              id: String(workforceAgency["id"] ?? workforceAgency["_id"] ?? ""),
              name: String(workforceAgency["name"] ?? ""),
            }
          : null,
        activeAgents: this.readDashboardValueObject(workforce["activeAgents"]),
        activeLogisticsUsers: this.readDashboardValueObject(
          workforce["activeLogisticsUsers"],
        ),
        activeBtlUsers: this.readDashboardValueObject(
          workforce["activeBtlUsers"],
        ),
      },
      supervisorPendingWork: {
        agentTasks: this.readDashboardValueObject(pendingWork["agentTasks"]),
        logisticsTasks: this.readDashboardValueObject(
          pendingWork["logisticsTasks"],
        ),
        btlActivities: this.readDashboardValueObject(
          pendingWork["btlActivities"],
        ),
      },
      supervisorPendingApprovals: {
        total: this.readDashboardValueObject(pendingApprovals["total"]),
        agentTaskApprovals: this.readDashboardValueObject(
          pendingApprovals["agentTaskApprovals"],
        ),
        logisticsTaskApprovals: this.readDashboardValueObject(
          pendingApprovals["logisticsTaskApprovals"],
        ),
        btlSubmissionApprovals: this.readDashboardValueObject(
          pendingApprovals["btlSubmissionApprovals"],
        ),
        fuelRequestApprovals: this.readDashboardValueObject(
          pendingApprovals["fuelRequestApprovals"],
        ),
        maintenanceRequestApprovals: this.readDashboardValueObject(
          pendingApprovals["maintenanceRequestApprovals"],
        ),
        routeChangeRequestApprovals: this.readDashboardValueObject(
          pendingApprovals["routeChangeRequestApprovals"],
        ),
        prospectApprovals: this.readDashboardValueObject(
          pendingApprovals["prospectApprovals"],
        ),
      },
      ...(agentPerformance ? { agentPerformance } : {}),
    };
  }

  /**
   * BUSINESS LOGIC: normalizeDashboardAgentPerformance
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private normalizeDashboardAgentPerformance(value: unknown): DashboardAgentPerformance | undefined {
    const record = value && typeof value === "object" ? (value as Record<string, unknown>) : null;
    if (!record) {
      return undefined;
    }

    const agent = record["agent"] && typeof record["agent"] === "object"
      ? (record["agent"] as Record<string, unknown>)
      : {};
    const tasks = record["tasks"] && typeof record["tasks"] === "object"
      ? (record["tasks"] as Record<string, unknown>)
      : {};
    const achievements = record["achievements"] && typeof record["achievements"] === "object"
      ? (record["achievements"] as Record<string, unknown>)
      : {};
    const recent = Array.isArray(tasks["recent"]) ? tasks["recent"] as unknown[] : [];
    const stockItems = Array.isArray(record["stockItems"]) ? record["stockItems"] as unknown[] : [];
    const targets = Array.isArray(record["targets"]) ? record["targets"] as unknown[] : [];

    return {
      agent: {
        id: String(agent["id"] ?? agent["_id"] ?? ""),
        name: this.toNullableString(agent["name"]) ?? undefined,
      },
      tasks: {
        total: { value: this.readDashboardValueObject(tasks["total"]) },
        open: { value: this.readDashboardValueObject(tasks["open"]) },
        inProgress: { value: this.readDashboardValueObject(tasks["inProgress"]) },
        completed: { value: this.readDashboardValueObject(tasks["completed"]) },
        recent: recent.map((item, index) => this.normalizeDashboardRecentAgentTask(item, index)),
      },
      stockItems: stockItems.map((item, index) => this.normalizeDashboardAgentStockItem(item, index)),
      targets: targets.map((item, index) => this.normalizeDashboardAgentTargetProgressRow(item, index)),
      achievements: {
        assignedTargetValue: { value: this.readDashboardValueObject(achievements["assignedTargetValue"]) },
        achievedValue: { value: this.readDashboardValueObject(achievements["achievedValue"]) },
        remainingValue: { value: this.readDashboardValueObject(achievements["remainingValue"]) },
        averageAchievementPercent: { value: this.readDashboardValueObject(achievements["averageAchievementPercent"]) },
      },
    };
  }

  /**
   * BUSINESS LOGIC: normalizeDashboardRecentAgentTask
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private normalizeDashboardRecentAgentTask(value: unknown, index: number): DashboardRecentAgentTask {
    const record = value && typeof value === "object" ? (value as Record<string, unknown>) : {};

    return {
      id: String(record["id"] ?? record["_id"] ?? `recent-agent-task-${index + 1}`),
      title: this.toNullableString(record["title"]) ?? "",
      status: this.toNullableString(record["status"]) ?? "",
      priority: this.toNullableString(record["priority"]) ?? "",
      dueDate: this.toNullableString(record["dueDate"]) ?? "",
      progressPercent: this.toNumber(record["progressPercent"]) ?? 0,
      latestActivityAt: this.toNullableString(record["latestActivityAt"]) ?? undefined,
      activityCount: this.toNumber(record["activityCount"]) ?? 0,
      assignedPosCount: this.toNumber(record["assignedPosCount"]) ?? 0,
    };
  }

  /**
   * BUSINESS LOGIC: normalizeDashboardAgentStockItem
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private normalizeDashboardAgentStockItem(value: unknown, index: number): DashboardAgentStockItem {
    const record = value && typeof value === "object" ? (value as Record<string, unknown>) : {};
    const category = record["category"] && typeof record["category"] === "object"
      ? (record["category"] as Record<string, unknown>)
      : {};
    const product = record["product"] && typeof record["product"] === "object"
      ? (record["product"] as Record<string, unknown>)
      : {};
    const variant = record["variant"] && typeof record["variant"] === "object"
      ? (record["variant"] as Record<string, unknown>)
      : {};

    return {
      id: String(record["id"] ?? record["_id"] ?? `agent-stock-${index + 1}`),
      quantity: this.toNumber(record["quantity"]) ?? 0,
      unit: this.toNullableString(record["unit"]) ?? "",
      category: {
        id: String(category["id"] ?? category["_id"] ?? ""),
        name: this.toNullableString(category["name"]) ?? "",
      },
      product: {
        id: String(product["id"] ?? product["_id"] ?? ""),
        name: this.toNullableString(product["name"]) ?? "",
      },
      variant: {
        id: String(variant["id"] ?? variant["_id"] ?? ""),
        name: this.toNullableString(variant["name"]) ?? "",
        unit: this.toNullableString(variant["unit"]) ?? "",
      },
      updatedAt: this.toNullableString(record["updatedAt"]) ?? "",
    };
  }

  /**
   * BUSINESS LOGIC: normalizeDashboardAgentTargetProgressRow
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private normalizeDashboardAgentTargetProgressRow(value: unknown, index: number): DashboardAgentTargetProgressRow {
    const record = value && typeof value === "object" ? (value as Record<string, unknown>) : {};

    return {
      id: String(record["id"] ?? record["_id"] ?? `agent-target-progress-${index + 1}`),
      userType: record["userType"] === "BTL" ? "BTL" : "AGENT",
      userId: String(record["userId"] ?? ""),
      userName: this.toNullableString(record["userName"]) ?? undefined,
      agencyId: String(record["agencyId"] ?? ""),
      agencyName: this.toNullableString(record["agencyName"]) ?? undefined,
      targetType: this.normalizeDashboardTargetType(record["targetType"]),
      categoryId: this.toNullableString(record["categoryId"]),
      categoryName: this.toNullableString(record["categoryName"]) ?? undefined,
      productId: this.toNullableString(record["productId"]),
      productName: this.toNullableString(record["productName"]) ?? undefined,
      dailyTarget: this.toNumber(record["dailyTarget"]) ?? 0,
      weeklyTarget: this.toNumber(record["weeklyTarget"]) ?? 0,
      monthlyTarget: this.toNumber(record["monthlyTarget"]) ?? 0,
      period: this.normalizeDashboardTargetPeriod(record["period"]),
      periodStart: this.toNullableString(record["periodStart"]) ?? "",
      periodEnd: this.toNullableString(record["periodEnd"]) ?? "",
      targetValue: this.toNumber(record["targetValue"]) ?? 0,
      achievedValue: this.toNumber(record["achievedValue"]) ?? 0,
      remainingValue: this.toNumber(record["remainingValue"]) ?? 0,
      achievementPercent: this.toNumber(record["achievementPercent"]) ?? 0,
      status: this.toNullableString(record["status"]) ?? "not-started",
      notes: this.toNullableString(record["notes"]) ?? "",
      createdByAuthUserId: String(record["createdByAuthUserId"] ?? ""),
      updatedByAuthUserId: String(record["updatedByAuthUserId"] ?? ""),
      createdAt: this.toNullableString(record["createdAt"]) ?? "",
      updatedAt: this.toNullableString(record["updatedAt"]) ?? "",
    };
  }

  /**
   * BUSINESS LOGIC: normalizeDashboardTargetType
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private normalizeDashboardTargetType(value: unknown): DashboardAgentTargetProgressRow["targetType"] {
    return value === "stock" || value === "float-sale" || value === "recharge-pos" || value === "customer-onboard"
      ? value
      : "stock";
  }

  /**
   * BUSINESS LOGIC: normalizeDashboardTargetPeriod
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private normalizeDashboardTargetPeriod(value: unknown): DashboardAgentTargetProgressRow["period"] {
    return value === "daily" || value === "weekly" || value === "monthly" ? value : "monthly";
  }

  private normalizeDashboardAgencyInventoryItem(
    item: unknown,
    index: number,
  ): {
    product: { id: string; name: string } | null;
    quantity: number;
    threshold: number;
  } {
    const record =
      item && typeof item === "object" ? (item as Record<string, unknown>) : {};
    const product =
      record["product"] && typeof record["product"] === "object"
        ? (record["product"] as Record<string, unknown>)
        : null;

    return {
      product: product
        ? {
            id: String(
              product["id"] ?? product["_id"] ?? `agency-product-${index + 1}`,
            ),
            name: String(product["name"] ?? "").trim(),
          }
        : null,
      quantity: this.readDashboardValueObject(record["quantity"]),
      threshold: this.readDashboardValueObject(record["threshold"]),
    };
  }

  private collectDashboardSummaryCandidates(
    value: unknown,
  ): Record<string, unknown>[] {
    if (!value || typeof value !== "object") {
      return [];
    }

    const root = value as Record<string, unknown>;
    const candidates: Record<string, unknown>[] = [root];

    for (const key of ["summary", "counts", "metrics", "activeSummary"]) {
      const nested = root[key];
      if (nested && typeof nested === "object" && !Array.isArray(nested)) {
        candidates.push(nested as Record<string, unknown>);
      }
    }

    return candidates;
  }

  private readDashboardSummaryCount(
    candidates: Record<string, unknown>[],
    keys: string[],
  ): number {
    for (const candidate of candidates) {
      for (const key of keys) {
        const value = this.toNumber(candidate[key]);
        if (value !== null) {
          return value;
        }
      }
    }

    return 0;
  }

  /**
   * BUSINESS LOGIC: readDashboardValueObject
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private readDashboardValueObject(value: unknown): number {
    if (typeof value === "number") {
      return Number.isFinite(value) ? value : 0;
    }

    if (!value || typeof value !== "object") {
      return 0;
    }

    const record = value as Record<string, unknown>;
    return this.toNumber(record["value"]) ?? 0;
  }

  private normalizeDashboardCentralInventoryItem(
    value: unknown,
    index: number,
  ): DashboardCentralInventoryItem {
    const record =
      value && typeof value === "object"
        ? (value as Record<string, unknown>)
        : {};
    const product =
      record["product"] && typeof record["product"] === "object"
        ? (record["product"] as Record<string, unknown>)
        : {};

    return {
      productId: String(
        product["id"] ?? product["_id"] ?? `dashboard-product-${index + 1}`,
      ),
      productName:
        this.toNullableString(product["name"]) ?? `Product ${index + 1}`,
      quantity: this.toNumber(record["quantity"]) ?? 0,
      threshold: this.toNumber(record["threshold"]) ?? 0,
    };
  }

  /**
   * BUSINESS LOGIC: toNumber
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  private toNumber(value: unknown): number | null {
    if (typeof value === "number" && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === "string") {
      const parsed = Number(value.trim());
      return Number.isFinite(parsed) ? parsed : null;
    }

    return null;
  }

  /**
   * Maps the auth/profile user payload into the frontend session model.
   * API NORMALIZATION: login and `/auth/me` may differ slightly, so this keeps session state
   * consistent for RBAC, preferences, and assigned-agency flows.
   */
  private mapApiUser(
    u: ApiUserShape | undefined,
    fallbackEmail = "",
  ): AuthUser {
    const role = typeof u?.role === "object" ? u.role : undefined;
    const roleCode =
      role?.code ?? (typeof u?.role === "string" ? u.role : "admin");
    const phone = u?.contact?.phone?.number ?? "";
    const address = u?.address?.formattedAddress ?? "";
    const statusRaw = u?.accountStatus ?? "active";
    const status: AuthUser["status"] =
      statusRaw === "active" ||
      statusRaw === "inactive" ||
      statusRaw === "pending"
        ? statusRaw
        : "active";
    const assignedAgency = this.readAssignedAgencyFromUser(u);
    const apiAssignedAgencyIds = Array.isArray(u?.assignedAgencyIds)
      ? u.assignedAgencyIds.filter(
          (id): id is string => typeof id === "string" && id.trim().length > 0,
        )
      : [];
    const assignedAgencyIds =
      apiAssignedAgencyIds.length > 0
        ? apiAssignedAgencyIds
        : assignedAgency?.id
          ? [assignedAgency.id]
          : [];
    const assignedAgencyId =
      u?.assignedAgencyId ?? assignedAgency?.id ?? assignedAgencyIds[0] ?? null;

    return {
      id: u?.id,
      email: u?.email ?? fallbackEmail,
      name: u?.fullName ?? `${u?.firstName ?? ""} ${u?.lastName ?? ""}`.trim(),
      firstName: u?.firstName,
      lastName: u?.lastName,
      language:
        (u?.preferredLanguage?.toLowerCase() as AuthUser["language"]) ?? "en",
      preferredLanguage: u?.preferredLanguage,
      preferredTheme:
        u?.preferredTheme === "dark"
          ? "dark"
          : u?.preferredTheme === "light"
            ? "light"
            : undefined,
      role: (roleCode as AuthUser["role"]) ?? "admin",
      roleCode,
      avatar:
        typeof u?.profilePhoto === "string"
          ? u.profilePhoto
          : typeof u?.avatar === "string"
            ? u.avatar
            : undefined,
      userType: role?.userType ?? role?.name ?? "Admin",
      status,
      agency: assignedAgency?.name || undefined,
      phone: phone || undefined,
      address: address || undefined,
      phoneDetails: u?.contact?.phone
        ? {
            dialCode: u.contact.phone.dialCode,
            iso2: u.contact.phone.iso2,
            country: u.contact.phone.country,
            number: u.contact.phone.number,
          }
        : undefined,
      addressDetails: u?.address
        ? {
            formattedAddress: u.address.formattedAddress,
            streetAddress: u.address.streetAddress,
            locality: u.address.locality,
            subLocality: u.address.subLocality,
            region: u.address.region,
            country: u.address.country,
            postalCode: u.address.postalCode,
            area: u.address.area,
          }
        : undefined,
      permissions: role?.permissions,
      roles: role?.name ? [role.name] : undefined,
      assignedAgency,
      assignedAgencyId,
      assignedAgencyIds,
      assignedSupervisorId: u?.assignedSupervisorId ?? null,
    };
  }
}
